import * as d3 from "d3";
import React from "react";
import { FaCircle } from "react-icons/fa6";
import {
  SEGMENT_ICON_THRESHOLDS,
  SEGMENT_ICON_SIZES,
  segmentIconSizeFor,
  classifyIconDisplay,
  resolveSegmentIconColor,
  hasOwnSegmentIcon,
} from "./segmentIconConfig";

import { createRoot } from "react-dom/client";
import { clampNonNegative } from "./dimensions";
import {
  computeLabelRadialX,
  clampAnchorTxToPane,
  clampPillBoxToPane,
  findClipBoundaryWidth,
  findClipBoundaryRect,
} from "./labelRadialX";
import { computeClampDx, parseTranslate } from "./labelPaneClamp";
import { changeIconSvgColor } from "./changeIconSvgColor";
import {
  graphemeUnits,
  disambiguateMiddleTruncation,
  truncateLabelEnd,
  wrapLabelIntoLines,
  renderLabelLines,
  pillSafeAnchorRadius,
  scheduleFontReadyRelayout,
  unionBBoxCenterRadius,
  runRingSafetyCorrectionLoop,
  runViewportSafetyCorrectionLoop,
  RELAYOUT_SETTLE_DELAYS_MS,
  isFullCircleWedge,
} from "./labelLayout";
import { layoutRimPillStack, rimPillStackRoom } from "./rimPillStack";
import { WHEEL_PILL_LABELS_ENABLED } from "../../constants/featureFlags";
import { colorsFor, PILL_INK, tooltipPillPaint } from "./pillColors";
import { placeFixedTooltip } from "./fixedTooltipPlacement";
// EW-003 dual-surface segment fills (flag REACT_APP_SEGMENT_ARC_FILLS, default
// OFF). toWedgePatternProps derives the closed-wedge <pattern> from the SAME fill
// MODEL the open frame uses, so the two surfaces cannot silently diverge.
import { SEGMENT_ARC_FILLS_ENABLED } from "./segmentArcFillsFlag";
// T22.145: autoplay / hover-only / still policy shared with renderer-v2.
import { GIF_PLAYBACK, gifPaintHref, resolveGifPlayback } from "../../utils/gifFillPlayback";
import { reportBrokenMediaOnce } from "../../utils/brokenMediaReport";
import {
  toWedgePatternProps,
  toWedgeGradientProps,
} from "../SegmentBuilder/Shared/SegmentStyling/logic/segmentFillSurfaces";
// Contrast hairline guard (flag REACT_APP_WHEEL_SEGMENT_HAIRLINE, default OFF).
// A white/near-white segment on a white/light surface reads as a BLANK slice;
// this draws a 1px boundary stroke on ONLY the failing segments. Pure logic +
// flag live in segmentContrastGuard.js so they are unit-testable in isolation.
import {
  isSegmentHairlineEnabled,
  isDarkBackdrop,
  needsHairline,
  deriveHairlineStroke,
  insetWedgeForHairline,
  resolveEffectiveBackdrop,
  HAIRLINE_STROKE_WIDTH,
  isLabelLegibilityEnabled,
  bestLabelTextColor,
  computeLabelCharLimit,
  clampCharsToAvailableWidth,
} from "./segmentContrastGuard";
// EMPTY WEDGE STYLE (flag REACT_APP_EMPTY_WEDGE_OUTLINE, default OFF). Gordon
// 2026-07-24: the #e1e1e1 empty/colorless fallback is AMBIGUOUS because a
// segment can legitimately BE light gray. An empty wedge instead renders
// transparent with a dashed outline — a treatment the palette cannot
// counterfeit. The spec is shared with the renderer-v2 engine + the embed so
// all three surfaces move together (see emptyWedgeStyle.js).
import {
  isEmptyWedgeDatum,
  isEmptyWedgeOutlineEnabled,
  EMPTY_WEDGE_FILL,
  EMPTY_WEDGE_OUTLINE_COLOR,
  EMPTY_WEDGE_OUTLINE_WIDTH,
  EMPTY_WEDGE_OUTLINE_DASH,
} from "./emptyWedgeStyle";

// ISP-4018 (DL-002, §12 WHEEL-WITHOUT-PORTRAIT law): a Wheel rendered with no
// portrait must show the colored iYuga company logo in its center — never a
// bare/hollow/broken center. This is the ONE shared render engine every
// WheelComponent-based surface (Explore/Gallery/Hall-of-Fame cards, search,
// pantry, folder lists…) draws through, so guarding the empty-url case here
// fixes them all at once instead of at each call site.
import DefaultIyugaLogo from "@assets/images/iyuga.jpg";
// ISP-9999 (Gordon 2026-07-21): the OTHER brand-logo lockups a no-portrait wheel
// center can fall back to (Profile avatar, ProfileMenu, ShareInvite, mobile gate
// all pass ASSETS.iyugaLogo = iyugalogo.svg; PrimaryWheel passes IyugaWhiteLogo).
// Imported here ONLY so the center-portrait padding fix below can recognise them
// by module URL — see isBrandLogoSrc / setProfile. Same asset modules the call
// sites import, so the URL strings match at runtime.
import BrandLogoWordmark from "@assets/images/iyugalogo.svg";
import BrandLogoSignup from "@assets/images/signup_logo.svg";
import BrandLogoWhiteBg from "@assets/images/Iyuga_logo_white_bg.svg";

// ISP-9999 (Gordon 2026-07-21): a wheel with NO user portrait shows the iYuga
// brand logo in its center (§12 law). That logo is a near-square wordmark+ring
// lockup; clipping it to the inscribed center circle with object-fit:cover cut
// the wordmark's horizontal extremes (the "i" + part of "a") so it read "Yuga".
// When the resting portrait src IS one of these known brand-logo assets we inset
// it (contain + padding) on a white field instead, so the FULL "iYuga" wordmark
// shows with a breathing-room margin ring. A real user photo (any other src)
// keeps object-fit:cover unchanged. INSET_PCT is per-side border-box padding: at
// 17% the logo's square bounding box is ~66% of the circle diameter, keeping the
// rainbow ring clear of the circle edge with a comfortable white margin.
const BRAND_LOGO_SRCS = new Set(
  [
    DefaultIyugaLogo,
    BrandLogoWordmark,
    BrandLogoSignup,
    BrandLogoWhiteBg,
  ].filter(Boolean)
);
const BRAND_LOGO_INSET_PCT = 17;
const isBrandLogoSrc = (src) =>
  typeof src === "string" && BRAND_LOGO_SRCS.has(src);

// ISP-9999 GIF portrait still-first: a .gif url (ignoring query/hash). A wheel
// CENTER portrait renders as an HTML <img>, which (unlike an SVG <image>) ANIMATES
// a gif immediately. On a gallery/explore card we instead show the still POSTER by
// default and swap to the gif on card hover ("lighter"). Mirrors fillModelFeed's
// looksLikeGif.
const looksLikeGifUrl = (url) =>
  typeof url === "string" && /\.gif(\?|#|$)/i.test(url.trim());

// The convention poster-still URL for an uploaded gif (`<url path>.poster.jpg`) —
// the BE upload path stores the poster there (ISP-9999). Used as a fallback when
// the card does not pass an explicit `userData.poster`. Query/hash are dropped
// before the suffix.
// T22.145: painted while a gif's still frame is extracting (shows the pattern's
// fallback-colour rect underneath).
const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const posterUrlForGif = (url) => {
  if (typeof url !== "string") return null;
  const base = url.trim().split("?")[0].split("#")[0];
  if (!/\.gif$/i.test(base)) return null;
  return base + ".poster.jpg";
};

// ISP-9999 null-title crash guard. A segment `title` is USER data and can
// legitimately arrive `null` (the BE column is nullable and the builder can
// clear a name) or `undefined` (a hand-built chartData feed). Every string op
// on a title MUST go through this: an unguarded `d.data.title.trim()` in
// updateLable() threw `TypeError: Cannot read properties of null (reading
// 'trim')` and unmounted the ENTIRE wheel page (measured: svgCount 0, empty
// body) — one bad segment took the whole route down.
//
// Normalizing to "" (never a placeholder) is deliberate: it makes a null title
// behave EXACTLY like the empty-string title this engine has always filtered
// out of the label layer, and it matches what renderer-v2 already does on
// purpose (renderer-v2/engine.js: `wedge.title == null ? "" : String(...)
// .trim()` then `if (!title) return; // v1 filters empty labels`), so the two
// engines stay in parity (asserted by renderer-v2/pills.crossengine.test.js).
// The positional "Segment N" placeholder is NOT this layer's job — it is
// produced upstream by the one shared, flag-gated utils/segmentLabels
// `withComputedChartLabels` helper. Emitting it here instead would draw rim
// labels on wheels that show none today, and would diverge v1 from v2.
const segmentTitleText = (title) => (title == null ? "" : String(title));

// Unicode-safe truncation. The legacy `str.slice(0, limit)` counts UTF-16 code
// units, so it splits astral emoji surrogate pairs (producing a broken "�"
// half-char) and mis-measures CJK width, which drove an oversized/duplicate
// label pill onto the adjacent segment. Truncate by GRAPHEME (Intl.Segmenter
// when available) so an emoji/flag/ZWJ sequence counts as one visible unit;
// fall back to code-points (Array.from) where Segmenter is unavailable.
// (graphemeUnits itself now lives in labelLayout.js — imported above — so the
// v2 engine's disambiguation pass measures units identically to this one.)

// GVW-P02: middle-ellipsis, grapheme-safe. Same total visible-unit budget a
// single-line all-head truncation would use (limit + "..."), spent on a head
// AND a tail instead, so same-prefix segment names stay distinguishable at a
// small phone-width limit instead of both collapsing to e.g. "Segment...".
const truncateUnicodeSafeMiddle = (str, limit) => {
  const s = str == null ? "" : String(str);
  if (limit == null || limit < 0) return s;
  const units = graphemeUnits(s);
  if (units.length <= limit) return s;
  const head = Math.ceil(limit / 2);
  const tail = Math.floor(limit / 2);
  return units.slice(0, head).join("") + "..." + units.slice(units.length - tail).join("");
};

// gv-0914c (Gordon video #3, "always full titles"): wrapLabelIntoLines /
// renderLabelLines used to be private copies here. Moved to labelLayout.js
// (fix-vy0918d-rimpill-ellipsis-truncates-view-page) so the v2 engine
// (renderer-v2/engine.js) shares the IDENTICAL wrap-instead-of-ellipsis
// behaviour instead of still hard-truncating the public View page's labels
// — see labelLayout.js's own header comment for the full rationale.

// ISP-9999 rs-0914-dev-segment-labels-truncate-10ch-390: GVW-P02's middle-
// ellipsis (truncateUnicodeSafeMiddle above) fixed the SAME-PREFIX collision
// ("Segment Alpha"/"Segment Beta" no longer both read "Segment...") but two
// DIFFERENT titles that share both a common prefix AND a common suffix
// within the head+tail budget still collapse to the IDENTICAL string —
// MEASURED live (web-dev.iyuga.net, wheel 6232, phone limit 7): "Segment 1"
// and "Segment 1Segment 1" both render "Segm...t 1". disambiguateMiddleTruncation
// now lives in labelLayout.js (imported above) — SINGLE SOURCE OF TRUTH so the
// v2 engine (renderer-v2/engine.js) shares the identical retry policy instead
// of missing it entirely (fix-fe-5497-rimpill-collision-viewwheel-regression-0917).

// Walk the just-rendered rim-pill labels in DOM order (their angular order);
// when a later label's rendered text collides with an earlier sibling's
// despite a DIFFERENT original title, re-render it with an alternate split
// (see disambiguateMiddleTruncation) so a phone viewer can tell the two
// segments apart. Two segments that share the SAME original title are left
// colliding on purpose — that's not a bug, it's the same label twice.
// Scoped to the "middle" truncate style: that's the only style this
// collision was MEASURED on (the phone ViewWheel path, wheelLabelConfig.js);
// the legacy all-head style predates this fix and is out of scope.
// gv-0914c-rebase: "middle" style always renders exactly one line (see the
// call site below), so re-rendering via renderLabelLines(node, [resolved])
// instead of a raw textContent write keeps the tspan/dy structure every
// other label already has intact.
function resolveLabelTextCollisions(labelGroup, self, widthClampedLimitFor) {
  // review-1008-Frontend-7048: the phone's one-line "end" style (T22.188) is
  // single-line too and collides the same way, so it gets the same re-split.
  if (
    self.segmentLabelTruncateStyle !== "middle" &&
    self.segmentLabelTruncateStyle !== "end"
  ) {
    return;
  }
  const taken = new Map(); // rendered text -> title of its first owner
  labelGroup.select("text.rect-text").each(function (d) {
    const node = this;
    const title = segmentTitleText(d.data.title);
    const current = node.textContent;
    const owner = taken.get(current);
    if (owner === undefined) {
      taken.set(current, title);
      return;
    }
    if (owner === title) return; // same title colliding with itself
    const limit = widthClampedLimitFor(node, d);
    const resolved = disambiguateMiddleTruncation(title, limit, taken);
    renderLabelLines(node, [resolved]);
    taken.set(resolved, title);
  });
}

// Per-chart-instance namespace for fill-model <pattern> ids: several wheels can
// render on one page (gallery grid) with colliding segment ids, and SVG pattern
// ids are document-global. Legacy `d.data.pattern` defs keep the historical
// un-namespaced id — only the flag-gated fill-model path namespaces.
let wedgeFillNsCounter = 0;

// Rim-pill label geometry (flag WHEEL_PILL_LABELS_ENABLED, default OFF). When ON,
// each Segment-title label drops its leader leg and hugs the wedge rim this many
// SVG user units outside the (possibly enlarged) outer radius. Matches the deck's
// ewflow-onbrand.mjs RIM_GAP. Flag OFF renders the legacy spider-leg label unchanged.
const WHEEL_PILL_RIM_GAP = 8;

// COMPB-02: the label's own font-size (px), used to convert the pixel-width
// cap (clampCharsToAvailableWidth) into a char budget so an unusually long
// label's pill truncates with an ellipsis instead of bleeding past the
// Builder canvas pane's edge and getting clipped.
const LABEL_FONT_SIZE_PX = 12;

// COMPB-02 (anchor-aware, redo): floor for the pane-aware char budget below —
// an ordinary short title is never truncated harder than this by the
// anchor-aware pass, no matter how close its anchor sits to the pane edge.
const MIN_GUARANTEED_LABEL_CHARS = 15;

// Label legibility (flag ON): size the char budget to the slice's actual
// arc length so a wide slice shows more text; never below the legacy cap,
// so this can only REVEAL more, never truncate harder. Flag OFF keeps the
// flat cap byte-identical. QA-136's mobile View-Wheel limit raise
// (segmentLableLimit: 10 -> MOBILE_VIEW_SEGMENT_LABEL_LIMIT) feeds this
// same `self.segmentLableLimit` base, so both fixes compose.
//
// COMPB-02 (anchor-aware, redo): also caps the budget to the pixel room left
// between the label's own RIM-PILL anchor (`node.parentNode.dataset.pillTx`/
// `pillRawTx`) and the Builder canvas pane's edge (`self.parent.clientWidth`),
// floored at MIN_GUARANTEED_LABEL_CHARS so an ordinary (<15-char) title is
// never touched by the anchor-aware tightening.
//
// stgqa-0921-builder-label-measure (audit1005-fix-editwheel-batch-open-p1p2p3):
// extracted out of `updateLable()`'s local closure (where it originated,
// behaviour unchanged there) into this module-level function, `self`/`node`/
// `d` passed explicitly, so `syncLabelSelectionTransforms()` — the
// selection-only fast path a real segment CLICK takes — can call the exact
// SAME pane-aware budget. Before this, a segment click that narrowed the
// Builder's own pane (BUILDER_SEGMENT_THIRDS, opening the Segment Settings
// pane) repositioned every pill's ANCHOR for the new, narrower geometry but
// left every pill's TEXT/BOX at whatever width the last full `updateLable()`
// draw measured under the OLD, WIDE pane — anchors pulled closer together,
// boxes stayed the same size, so two pills that never collided at the wide
// spacing could collide at the narrow one. MEASURED LIVE (web-dev.iyuga.net,
// main.a0b1030e.js, builder/7500, 10 segments): opening a segment narrowed
// the canvas from 835px to 463px and left "Step 1  👉 Oil Blend (4 Ways)"
// genuinely overlapping "Step 2 👉 MetaPWR ASSIST" (DOM rect intersection,
// not just visually adjacent).
function widthClampedLimitForNode(node, d, self) {
  const limit = isLabelLegibilityEnabled({
    segmentLabelLegibility: self.segmentLabelLegibility,
  })
    ? computeLabelCharLimit(
        d.endAngle - d.startAngle,
        self.pieOutterRadius * 1.2,
        12,
        self.segmentLableLimit
      )
    : self.segmentLableLimit;
  // COMPB-02: cap the budget above at a pixel width tied to the wheel's
  // own drawn radius (position-independent — the label's anchor already
  // legitimately floats past the raw SVG viewBox edge by design, per the
  // RIM-PILL CLIP FIX above, so bounding against viewBox/anchor geometry
  // would over-truncate every ordinary label). Only a label long enough
  // to render WIDER than the wheel itself is what actually produced the
  // measured 233px pill that clipped past the Builder canvas pane's
  // overflow:hidden edge (COMPB-02) — this never tightens the existing
  // char-count caps for a normal-length title.
  const flatBudget = clampCharsToAvailableWidth(
    limit,
    self.pieOutterRadius,
    LABEL_FONT_SIZE_PX
  );
  // gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-never-allowed:
  // prefer the RAW (pre-pane-clamp) ring-safe anchor -- see its own comment
  // where it's stashed -- falling back to pillTx for any node that never
  // set it (flag OFF, or the spider-label branch, both unaffected by this
  // fix).
  const anchorTxAbs = Math.abs(
    parseFloat(
      (node.parentNode && node.parentNode.dataset.pillRawTx) != null
        ? node.parentNode.dataset.pillRawTx
        : node.parentNode && node.parentNode.dataset.pillTx
    ) || 0
  );
  const paneWidth = self.parent && self.parent.clientWidth;
  // clampCharsToAvailableWidth treats a non-positive width as
  // "unmeasurable, don't clamp" (returns the budget untouched) — right
  // for its original flat-radius caller, where the input is always a
  // real positive radius. Here a non-positive result is a MEANINGFUL
  // signal (the anchor already sits at or past the pane's half-width),
  // not a bad measurement — floor at 1px so the clamp still engages
  // and MIN_GUARANTEED_LABEL_CHARS below is what actually governs the
  // result, instead of silently falling through to "no clamp at all".
  const paneAwareBudget = clampCharsToAvailableWidth(
    limit,
    typeof paneWidth === "number"
      ? Math.max(1, paneWidth / 2 - anchorTxAbs)
      : NaN,
    LABEL_FONT_SIZE_PX
  );
  return Math.min(
    flatBudget,
    Math.max(paneAwareBudget, MIN_GUARANTEED_LABEL_CHARS)
  );
}

// ISP-9999 (grow-motion): segment-pill grow-on-hover/selected (View Wheel +
// Edit Wheel). The wedge FILL itself can never take an affine CSS
// `transform: scale` — LAW 1 REVISED above explains why (a uniform scale would
// drag the inner radius off-centre, reopening bug #910's mid-transition seam
// gap). The segment's own rim-pill LABEL (the small rounded-rect + text this
// file calls a "pill" throughout, e.g. WHEEL_PILL_RIM_GAP / rect-label /
// colorsFor()) carries no such risk: it is a floating badge positioned by a
// single translate, not part of the annulus. Scaling IT is the safe, additive
// way to give View Wheel / Edit Wheel segments the same "grow" affordance the
// gallery/discover wedge-breath gives tile surfaces, without touching wedge
// geometry or segment layout/positions. Matches the wedge-hover breath's own
// easing family (d3.easeCubicOut ~= cubic-bezier(0.33,1,0.68,1)); duration
// sits between the 260ms wedge breath and the 160ms selected pop.
const SEGMENT_PILL_GROW_SCALE = 1.08;
const SEGMENT_PILL_GROW_DURATION_MS = 220;
const SEGMENT_PILL_GROW_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";

// ISP-9999 (rework-fe-pr-4566-devpr, label-blink-on-select; extended
// gordon-0914-builder-4908-segment-click-images-vanish-rearrange to also gate
// updateImage()): true when two chartData arrays differ only in which
// segment(s) are `selected` — every other rendering input (id/title/value/
// color/position/pattern/fill) is identical. Used so a segment click can
// reposition/re-grow the EXISTING rim-pill nodes AND reuse the existing
// <defs>/<pattern>/<image> nodes instead of updateArcs() tearing down and
// rebuilding every label <g> / every segment's image (which blinked all
// pills AND all segment images off for a frame on every click, not just the
// clicked segment's).
function chartDataUnchangedExceptSelection(prev, next) {
  if (
    !Array.isArray(prev) ||
    !Array.isArray(next) ||
    prev.length !== next.length ||
    prev.length === 0
  ) {
    return false;
  }
  return prev.every((a, i) => {
    const b = next[i];
    return (
      a &&
      b &&
      a.id === b.id &&
      a.title === b.title &&
      a.value === b.value &&
      a.color === b.color &&
      a.position === b.position &&
      a.pattern === b.pattern &&
      a.fill === b.fill
    );
  });
}

// T22.90: renderIconElementToHtml waits for React's commit (see there).
export const ICON_RENDER_RETRY_MS = 16;
export const ICON_RENDER_MAX_RETRIES = 30;

// Fallback wedge fill for a segment whose `color` is null/undefined/empty. A d3
// `.style("fill", undefined)` is a no-op, so a colorless segment would fall
// through to the SVG default (opaque BLACK) — visible on card-grid / gallery
// thumbnails (e.g. /profile) whose data can carry colorless segments. Degrade to
// the SAME neutral the canonical View-Wheel engine uses (renderer-v2
// DEFAULTS.wedgeColor) and this file's own emptyChartData color, so a thumbnail
// matches the full wheel instead of painting black.
const DEFAULT_WEDGE_COLOR = "#e1e1e1";

// Outer label-pill collision tuning (LABEL_PILL_GAP_PX / LABEL_DOT_RADIUS_PX)
// now lives in labelLayout.js as the single source of truth shared with the v2
// engine (renderer-v2/engine.js) — imported above. Values are unchanged.

class CreatePieChart {
  svg;
  parent;
  svgG;
  visibleArcG;
  hiddenArcG;
  lineG;
  chartData;
  userData;
  hiddenArcData;
  updateDataCB;
  width;
  height;
  margin = 50;
  padding = { left: 3, right: 2 };
  pieInnerRadius;
  pieOutterRadius;
  // fix-gordon-0928-P0-kelly-wheel-294918-zero-value-segments (all-zero case)
  // + fix-kelly-...-web-staging 2026-09-30 (MIXED case, this wheel's CURRENT
  // shape): the original fix only floored a non-positive value when EVERY
  // segment was non-positive, so a partially-generated AI wheel — some
  // segments already weighted, others still stub segments awaiting their
  // weight — kept a literal 0-degree (invisible) wedge for every stub, even
  // though it carries a real title. Re-measured live 2026-09-30 on wheel
  // 294918 (GET /api/v2/wheel/294918/): 4 segments, 2 titled at 85.71%/14.29%
  // and 2 MORE titled segments at value 0.0 each — "three titles with only
  // two segments" per the report. A segment's `is_visible` flag (BE
  // `yuga_wheel/models.py` Activity model) is the one sanctioned way to hide
  // it; `value` is a proportion, never a visibility switch, so ANY
  // non-positive value reaching this accessor for a segment meant to be seen
  // is a data defect, not intent — floor it unconditionally rather than only
  // when every sibling is also non-positive.
  pie = d3
    .pie()
    .value((d) => {
      const v = +d?.value;
      return Number.isFinite(v) && v > 0 ? v : 1;
    })
    .sort(null);
  arcGenerator;
  currentSegment;
  hiddenArcSvg;
  isViewMode;
  getSelectedArcs;
  selectedArcs = [];
  maxSelectedArcs = 1;
  maxArcPerSide = 1;
  arcHoverEvents;
  isDragging = false;
  arcThickness = 0;
  handleProfileClick;
  polyLinesG;
  titleG;
  isShowLabelOn = false;
  isSmallView = false;
  profileMouseHoverIn;
  profileMouseHoverOut;
  profileOpacityOnMouseHover = 0.5;
  Tooltip;
  isNotDraggable = false;
  prevSwappedArcId = "";
  arcPaddingRadius = 2;
  segmentHighlighting = false;
  segmentBorder = "#000051";
  segmentOpacity = 0.2;
  segmentLableOpacity = 0.2;
  isDoubleCol = null;
  svgHeightWidth;
  // vy-0915-dev-segment-select-white-ghost-disc: last "image" portrait signature
  // painted by setProfile(), so a selection-only update (same portrait content,
  // only geometry changed) can resize the existing foreignObject in place
  // instead of tearing down and recreating the <img> -- see setProfile() below.
  _lastProfileSignature = null;
  showTooltips = true;
  centerGap = 0;

  // Contrast hairline guard state (flag-gated; see segmentContrastGuard.js).
  // segmentBackdrop: explicit effective backdrop colour for floating contexts
  // (e.g. the app-download popup wheel sitting on a white puck). segmentHairline:
  // a per-instance enable override used ONLY by fixtures/tests — production leaves
  // it undefined so the build-time env flag governs (default OFF ⇒ byte-identical).
  segmentBackdrop;
  segmentHairline;
  // Label legibility sub-flag override (test seam; production leaves undefined).
  segmentLabelLegibility;
  // Empty-wedge outline override (test/fixture seam; production leaves undefined
  // so the build-time REACT_APP_EMPTY_WEDGE_OUTLINE flag governs — default OFF).
  emptyWedgeOutline;

  // ── Keyboard navigation (a11y composite widget) ──────────────────────────
  // Opt-in via config.keyboardNav (default OFF ⇒ every existing WheelComponent
  // usage stays byte-identical). When ON, the SVG becomes a single-tab-stop
  // role=listbox whose visible arc paths are role=option; a roving focusedIndex
  // driven by arrow/Home/End moves an on-brand focus ring, and Enter/Space call
  // the SAME arcSelected() handler a mouse click uses. The transparent hidden-arc
  // drag/click layer is left untouched so pointer + drag behaviour is unchanged.
  keyboardNav = false;
  wheelName = "";
  focusedIndex = null;
  hasWidgetFocus = false;
  focusRingG;
  _kbdCleanup = null;
  _reduceMotion = false;

  // ISP-9999 — Segment-wedge hover motion (browse tiles). When true, applyWedgeHoverMotion()
  // tags the container with `wedge-hover-motion` and stamps each wedge's radial-outward
  // translate onto `--wtx`/`--wty`; index.scss eases them out on container :hover. Opt-in
  // per surface (gifHoverPreview) + flag-gated in WheelComponent; default false = untouched.
  segmentHoverMotion = false;

  emptyChartData = [
    {
      title: "blank",
      value: "1",
      color: "#e1e1e1",
      isBlank: true,
      id: 0,
      position: 10,
    },
  ];

  // Adaptive segment-icon config. Sizes + thresholds are sourced from the
  // shared engine module (segmentIconConfig.js) so the tunables live in one
  // place; per-call overrides still flow through updateConfig().
  // minSizeToShowIcons / minSizeToShowDotIcons are ANGULAR thresholds in
  // radians (arc.endAngle - arc.startAngle): >= full-icon, >= dot, else hidden.
  segmentIconConfig = {
    // Gordon 2026-07-12: was "both" (icons+numbers). The per-segment "icon" is a
    // hard-coded FaBookOpen DEMO placeholder (updateIcons: `const Icon = FaBookOpen`,
    // real `d.data.icon` still commented out) — it painted a book-open glyph on
    // every segment. Show numbers only until real per-segment icons are wired.
    // Reversible one-value flip; the icon code path stays intact, just unused.
    showOnSegment: "numbers", //icons,numbers,blank,both
    iconSize: SEGMENT_ICON_SIZES.iconSize,
    dotIconSize: SEGMENT_ICON_SIZES.dotIconSize,
    numberLableSize: SEGMENT_ICON_SIZES.numberLableSize,
    minSizeToShowIcons: SEGMENT_ICON_THRESHOLDS.MIN_ANGLE_FOR_FULL_ICON,
    minSizeToShowDotIcons: SEGMENT_ICON_THRESHOLDS.MIN_ANGLE_FOR_DOT,
    // When true, the icon color defaults to the dev's auto-contrast color
    // instead of the theme-responsive default. Per-segment `iconColor` always
    // wins over both. Default false = theme-responsive.
    preferAutoContrast: false,
  };

  constructor(
    element,
    data,
    userData,
    updateDataCB,
    isViewMode,
    getSelectedArcs,
    maxSelectedArcs,
    arcHover,
    arcThickness,
    handleProfileClick,
    config,
    margin
  ) {
    this.margin = margin ?? 50;
    // feat-0923-broken-media-alerts: optional, additive -- only used to tag
    // this instance's client broken-media reports (see reportBrokenMediaOnce
    // call sites below). undefined when the caller doesn't pass it (every
    // existing call site/test), matching the report contract's optional
    // wheel_id.
    this.wheelId = config?.wheelId;
    // T22.145: opt-in GIF autoplay policy (the Edit Wheel passes it). Cards and
    // every other pie leave it unset -> still-at-rest + card hover, unchanged.
    this.gifPlayback = !!config?.gifPlayback;
    this.gifPlaybackOverride = config?.gifPlaybackOverride;
    this.updateDataCB = updateDataCB;
    this.parent = element;
    // stgfix-1007-P0-editwheel-video-hover-icons-missing-repeat: this used to be
    // `.selectAll("svg").remove()` — a blanket sweep of EVERY <svg> descendant of
    // `element`, not just a stale prior wheel svg. `element` is the SAME
    // container WheelComponent also mounts PortraitVideoOverlay into (a sibling
    // React subtree, not a D3-owned one) whenever a video/gif center_media is
    // present. React commits that subtree's JSX (including its own nested
    // <svg> glyphs — progress ring, play/pause, speaker icon) BEFORE any
    // useEffect runs, so by the time this constructor's effect fires, those
    // glyph <svg> elements already exist as children of `element`. The old
    // blanket selector matched and removed them too — React's fiber still
    // believed they were mounted (this DOM mutation bypassed reconciliation
    // entirely), so they never came back: the play/pause and mute buttons
    // rendered with zero children forever, live-measured on web-stg
    // (web-stg.iyuga.net/builder/507106, bundle main.e615452d.js — both
    // buttons present, fully empty, `childNodes.length === 0`). Scoping the
    // removal to the wheel's own `svg#chart` (the id this constructor assigns
    // two lines below) still clears a genuinely stale prior wheel svg on
    // reconstruction, without touching any unrelated svg a sibling React tree
    // owns.
    d3.select(this.parent).select("svg#chart").remove();
    this.svg = d3.select(element).append("svg").attr("id", "chart");
    // RIM-PILL CLIP FIX (Gordon 2026-07-24, builder 827f7818): the svg viewport is
    // the wheel square (width = svgHeightWidth + margin) with only margin/2 (~25px)
    // of room beyond the outer rim, but a rim-hugging PILL label at 3 & 9 o'clock
    // extends ~outerRadius*0.05 + WHEEL_PILL_RIM_GAP + its own text width (~90px+)
    // past the rim — far more than 25px — so the svg's UA-default overflow:hidden
    // clipped the pill's outer edge horizontally. overflow:visible lets the pill
    // paint into the (overflow:visible) parent canvas whitespace WITHOUT changing
    // the wheel's radius or centering, and is a no-op for spider labels (they sit
    // inside outerRadius) and for the wedges themselves.
    this.svg.style("overflow", "visible");
    this.chartData = data;
    this.userData = userData;
    // this.userData = {
    //   id: "1",
    //   url: 'http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
    //   type: "video", // updated
    // };
    this.isViewMode = isViewMode;
    this.getSelectedArcs = getSelectedArcs ? getSelectedArcs : () => {};
    this.maxSelectedArcs = maxSelectedArcs;
    this.updateConfig(config);
    this.arcHoverEvents = !!arcHover ? arcHover : () => {};
    if (arcThickness) {
      this.arcThickness = arcThickness;
    }
    this.handleProfileClick = !!handleProfileClick
      ? handleProfileClick
      : () => {};
    this.showTooltips =
      config?.showTooltips !== undefined ? config.showTooltips : true;
    // gordon-0920-labels-firefox: loadRectSize() (below) sizes each pill's
    // push-out radius from a getBBox() taken synchronously at draw time, which
    // races the DM Sans web font load exactly like renderer-v2/engine.js's
    // renderPill (see scheduleFontReadyRelayout's header in labelLayout.js) —
    // same shared function, same race, so this legacy engine (Edit
    // Wheel/Builder, and any surface with RENDERER_V2_VIEW_ENABLED off) gets
    // the identical re-place-once-fonts-are-real fix.
    this._cancelFontRelayout = scheduleFontReadyRelayout(
      element && element.ownerDocument,
      () => {
        this.relayoutLabelPills();
        this.scheduleSettleRelayout();
      }
    );
  }

  // gordon-0920-labels-firefox: re-run loadRectSize() against the CURRENT,
  // already-bound label groups (no re-`.data()`/`.enter()` — this must stay a
  // pure re-measure, never a second copy of the labels) once the real DM Sans
  // metrics are in. No-op before the first draw (empty selection) and a
  // harmless repeat if the font was already warm.
  relayoutLabelPills() {
    if (!WHEEL_PILL_LABELS_ENABLED || !this.titleG) return;
    const labelGroup = this.titleG.selectAll("g.label-group");
    if (labelGroup.empty()) return;
    this.loadRectSize(labelGroup, this);
    // stgfix-10101843-T419: loadRectSize() re-derives every pill's translate
    // from scratch (natural ring-safe radius), which silently wiped the Pill
    // Rule A stack the full draw applied — MEASURED live on web-stg.iyuga.net
    // (main.a1a75c38.js, /builder/507662): 300ms/1200ms after load (the
    // settle passes) the 12/6 o'clock pills were back to overlapping
    // (Segment 16 x Segment 17 = 546px^2). Re-apply the stack every time.
    this.reapplyTightPillStack(labelGroup);
  }

  // Pill Rule A stack over the CURRENT label groups, without resolveLabelOverlaps'
  // DOM paint-order reshuffle (a relayout can land while a pill is hovered;
  // re-appending the hovered node would drop its mouse state).
  reapplyTightPillStack(labelGroup) {
    if (!WHEEL_PILL_LABELS_ENABLED || !labelGroup || labelGroup.empty()) return;
    const self = this;
    const entries = [];
    labelGroup.each(function (d) {
      if (!d || !d.data) return;
      if (!self.titleG.select(`#b${d.data.id}`).node()) return;
      entries.push({ id: d.data.id, d });
    });
    if (entries.length) self.applyTightPillStack(entries, self);
  }

  // gordon-0928-P0-rim-labels-wrong-on-first-load-until-hover: schedule a
  // couple of bounded, later re-verification passes after any event that can
  // legitimately change label geometry (a resize settle, a font-ready
  // signal). relayoutLabelPills() is idempotent and self-correcting (its
  // ring-safety loop re-measures the REAL current DOM box every call), so an
  // extra call is a no-op once things are actually settled — this exists only
  // to catch the one bad pass MEASURED live (see RELAYOUT_SETTLE_DELAYS_MS's
  // header in labelLayout.js), never depending on a hover to trigger it.
  // Debounced (clears any still-pending timers) so a burst of resize ticks
  // schedules one settle check, not a pile of them.
  scheduleSettleRelayout() {
    if (this._settleRelayoutTimers) {
      this._settleRelayoutTimers.forEach((t) => clearTimeout(t));
    }
    this._settleRelayoutTimers = RELAYOUT_SETTLE_DELAYS_MS.map((ms) =>
      setTimeout(() => this.relayoutLabelPills(), ms)
    );
  }
  updateConfig(config) {
    this.maxArcPerSide = config?.maxArcPerSide;
    this.profileMouseHoverIn = config?.profileMouseHoverIn
      ? config?.profileMouseHoverIn
      : () => {};
    this.profileMouseHoverOut = config?.profileMouseHoverOut
      ? config?.profileMouseHoverOut
      : () => {};
    this.profileOpacityOnMouseHover = config?.profileOpacityOnMouseHover;
    this.isSmallView = config?.isSmallView;
    // search-comp-vY-02: the 60px search-result wheel row renders a
    // brand-logo-fallback portrait at ~26px (BRAND_LOGO_INSET_PCT shrinks it)
    // next to a real-cover portrait at ~39px (no inset) -- same outer ring,
    // inconsistent inner image size. Per-instance override so that ONE surface
    // (WheelItem, the search row) can render both cases at the SAME size;
    // every other isBrandLogo surface keeps the default 17% breathing-room
    // inset untouched.
    this.brandLogoInsetPct =
      config?.brandLogoInsetPct !== undefined
        ? config.brandLogoInsetPct
        : BRAND_LOGO_INSET_PCT;
    this.isNotDraggable = config?.isNotDraggable;
    if (config?.keyboardNav !== undefined) this.keyboardNav = config.keyboardNav;
    if (config?.wheelName !== undefined) this.wheelName = config.wheelName;
    this.segmentHighlighting =
      config?.segmentHighlighting === undefined
        ? this.segmentHighlighting
        : config.segmentHighlighting;
    this.segmentOpacity = config?.segmentOpacity || this.segmentOpacity;
    this.segmentBorder = config?.segmentBorder || this.segmentBorder;
    this.centerGap = config?.centerGap ?? 0;
    // Contrast hairline guard config (flag-gated). segmentBackdrop is a plain
    // config value; segmentHairline is preserved across calls that omit it so a
    // later config update can't silently disable a fixture override.
    this.segmentBackdrop = config?.segmentBackdrop;
    if (config?.segmentHairline !== undefined) {
      this.segmentHairline = config.segmentHairline;
    }
    if (config?.segmentLabelLegibility !== undefined) {
      this.segmentLabelLegibility = config.segmentLabelLegibility;
    }
    if (config?.segmentLabelTruncateStyle !== undefined) {
      this.segmentLabelTruncateStyle = config.segmentLabelTruncateStyle;
    }
    if (config?.emptyWedgeOutline !== undefined) {
      this.emptyWedgeOutline = config.emptyWedgeOutline;
    }
    this.setSvg();

    this.segmentLableLimit = config?.segmentLableLimit || 25;
    this.inactiveOpacity = config?.inactiveOpacity || 0;

    this.arcPaddingRadius = config?.arcPaddingRadius || this.arcPaddingRadius;
    this.segmentLableOpacity =
      config?.segmentLableOpacity || this.segmentLableOpacity;
    if (config?.segmentIconConfig) {
      Object.keys(this.segmentIconConfig).forEach((key) => {
        this.segmentIconConfig[key] =
          config.segmentIconConfig[key] || this.segmentIconConfig[key];
      });
    }
    this.showTooltips =
      config?.showTooltips !== undefined
        ? config.showTooltips
        : this.showTooltips;
    // ISP-9999 segment-wedge hover motion opt-in (preserved across config updates
    // that omit it, so a later partial update can't silently disable it).
    if (config?.segmentHoverMotion !== undefined) {
      this.segmentHoverMotion = config.segmentHoverMotion;
    }
    this.setSvg();
  }

  // Extra px added between pieInnerRadius and the wedge inner rim. Same value
  // resize() feeds into arcGenerator.innerRadius — kept as one helper so the
  // centre portrait (below) can size to the SAME hole and not leave a white
  // annulus between the photo and the coloured segments.
  arcHolePadding() {
    return this.isSmallView || this.parent.clientHeight - this.margin < 250
      ? this.padding.right
      : this.padding.left + this.padding.right;
  }

  // Radius of the donut HOLE the wedges leave (arc inner rim). The centre
  // portrait must fill this exactly — ISP-4018 removed the old white
  // `.center-image-border` ring that used to paint this band, but the portrait
  // kept sizing to bare `pieInnerRadius`, so the band showed through as a
  // white crescent (most obvious on collection covers / small tiles).
  //
  // gv-0914c-dev-white-ghost-disc-third-report-narrow-window: used to read the
  // CACHED `this.pieInnerRadius` field, only refreshed by resize() (called
  // from setSvg(), bundled into WheelComponent's 50ms-settle-debounced
  // updateSize()). A sustained relayout (a react-spring pane-width animation,
  // or a fast window-narrow drag) keeps that debounce from ever settling
  // mid-animation, so `this.pieInnerRadius` — and every consumer of
  // portraitRadius(), including WheelComponent's backing-disc sizing — stayed
  // pinned to its PRE-relayout value for the whole animation. arcHolePadding()
  // above already re-measures `this.parent` LIVE on every call for the same
  // reason; mirror that here instead of trusting the cache, so this is correct
  // no matter how often (or how late) it's called relative to the last resize().
  portraitRadius() {
    const svgHeightWidth = clampNonNegative(
      Math.min(
        this.parent.clientWidth - this.margin,
        this.parent.clientHeight - this.margin
      )
    );
    const pieInnerRadius = clampNonNegative(
      svgHeightWidth / this.padding.left
    );
    return clampNonNegative(
      pieInnerRadius + this.arcHolePadding() + (this.centerGap || 0)
    );
  }

  resize() {
    this.width = this.parent.clientWidth - this.margin;
    this.height = this.parent.clientHeight - this.margin;
    this.svgHeightWidth = Math.min(this.width, this.height);
    this.svg
      .attr("width", this.svgHeightWidth + this.margin)
      .attr("height", this.svgHeightWidth + this.margin);
    // Floor the pie radii to >= 0. When the parent measures smaller than `margin`
    // (transient layout frames) svgHeightWidth goes negative, and every
    // <image>/<foreignObject>/<circle> dimension derived from these radii would
    // otherwise be negative — which SVG rejects, spamming the console.
    this.pieInnerRadius = clampNonNegative(this.svgHeightWidth / this.padding.left);
    this.pieOutterRadius = clampNonNegative(this.svgHeightWidth / this.padding.right);
    const padding = this.arcHolePadding();
    this.pieOutterRadius +=
      this.pieOutterRadius * (this.arcThickness ? this.arcThickness / 100 : 0);
    this.arcGenerator = d3
      .arc()
      .innerRadius(this.pieInnerRadius + padding + this.centerGap)
      .outerRadius((d, i) => {
        return (
          this.pieOutterRadius +
          // LAW 2 / PRODUCTION PARITY (Gordon 2026-09-15): the ONLY selected-wedge
          // treatment is this outer-radius grow — no ring, no glow, no translate
          // (the former selectionTint/segmentPopGlow suppression of this grow is
          // gone along with those two features; see removed applySelectionTint /
          // applySegmentPopGlow above).
          (d?.data?.selected && !this.segmentHighlighting
            ? this.selectedBulgeDelta()
            : 0)
        );
      });
    this.Tooltip?.style("opacity", 0)?.style("stroke", "none");
  }

  // gordon-0915-selected-segment-effect-prod-parity ("double bounce" / snap +
  // freeze + staircase, Kelly's video 2026-09-15; regression-anchored by
  // createPie.resizeThrash.test.js, diagnosed Gordon 2026-08-03, prior
  // unmerged attempt PR#2624): setSvg() is a FULL structural teardown —
  // `this.svg.selectAll("g").remove()` wipes every top-level group (arcs,
  // icons, labels, portrait, hidden drag layer, tooltip, focus ring) and
  // rebuilds them from scratch. WheelComponent's ResizeObserver-driven
  // updateSize() used to call setSvg() on EVERY resize tick — so a sustained
  // container relayout (e.g. the Builder's Segment Settings side panel
  // opening) tore down and rebuilt the entire wheel ~15-20x, measured live as
  // one large snap followed by a 300ms-800ms freeze then a staircase of small
  // steps (~/segment-select-0915-amend.txt section 2), not a smooth per-frame
  // resize.
  //
  // Fix: a resize NEVER needs a fresh SVG skeleton — only the two things that
  // are actually a function of container size: (1) the radii/arcGenerator/svg
  // width+height, already handled by resize() above, and (2) the two
  // transform-bearing wrapper groups whose translate offset is the wheel's
  // center point (svgG — arcs/icons/labels/portrait; hiddenArcSvg — the
  // invisible drag/click hit-layer). Everything else already updates itself
  // IN PLACE on the next updateArcs()/updateIcons()/setProfile() call via
  // D3 data-joins (.merge()) or signature-checked fast paths — see
  // updateArcs()'s `selectionOnly`/`skipImageRebuild` guards and
  // setProfile()'s `_lastProfileSignature` guard — none of that requires the
  // wrapping groups to be destroyed and recreated first. Mount-time setup
  // (Tooltip div, focus ring, keyboard nav wiring, DOM group creation itself)
  // stays exclusively in setSvg(), called once at construction; a bare
  // resize tick now only ever calls this cheap sibling.
  resizeSvg() {
    this.resize();
    if (this.svgG || this.hiddenArcSvg) {
      const translate = `translate(${
        (this.svgHeightWidth + this.margin) / this.padding.right
      },${(this.svgHeightWidth + this.margin) / this.padding.right})`;
      this.svgG?.attr("transform", translate);
      this.hiddenArcSvg?.attr("transform", translate);
    }
  }

  setMode(isViewMode) {
    this.isViewMode = isViewMode;
  }

  setSvg() {
    this.svg.selectAll("g").remove();
    this.resize();
    this.svgG = this.svg
      .append("g")
      .attr("class", "visible-g")
      .attr(
        "transform",
        `translate(${
          (this.svgHeightWidth + this.margin) / this.padding.right
        },${(this.svgHeightWidth + this.margin) / this.padding.right})`
      );
    this.arcG = this.svgG.append("g").attr("class", "arcs-g");

    this.iconsG = this.svgG.append("g").attr("class", "icon-g");

    this.numberLableG = this.svgG.append("g").attr("class", "number-lable-g");

    this.titleG = this.svgG.append("g").attr("class", "title-text");

    this.polyLinesG = this.svgG.append("g").attr("class", "poly-lines");

    this.lineG = this.svgG.append("g").attr("class", "line-g");

    this.hiddenArcSvg = this.svg
      .append("g")
      .attr("class", "hidden-g")
      .attr(
        "transform",
        `translate(${
          (this.svgHeightWidth + this.margin) / this.padding.right
        },${(this.svgHeightWidth + this.margin) / this.padding.right})`
      );

    this.hiddenArcs = this.hiddenArcSvg.append("g").attr("class", "hidden-arc");

    // The transparent hidden-arc drag/click layer is a pointer-only interaction
    // proxy — keep it out of the a11y tree so screen readers don't announce two
    // sets of arcs. Only when the keyboard widget is active (so other usages are
    // byte-identical).
    if (this.keyboardNav) {
      this.hiddenArcSvg.attr("aria-hidden", "true");
    }

    this.Tooltip = d3
      .select(this.parent)
      .append("div")
      .attr("class", "tooltip")
      .style("position", "fixed") // Add fixed positioning
      .style("z-index", "9999"); // Add high z-index

    this.setProfile(this.userData);

    // Focus-ring layer sits above the arcs/profile and never intercepts pointer
    // events. Recreated on every setSvg() (which wipes svgG); the ring itself is
    // (re)drawn by renderFocusRing() only while the widget holds focus.
    this.focusRingG = this.svgG
      .append("g")
      .attr("class", "focus-ring-g")
      .style("pointer-events", "none");

    // ISP-9999 (F2, a11y-i18n-mobileweb) — computed UNCONDITIONALLY (every
    // setSvg(), i.e. every mount + resize/reconfig) so entrance/update wedge
    // transitions can honour prefers-reduced-motion even when config.keyboardNav
    // is off (the default for every existing WheelComponent caller). Previously
    // this was only computed inside setupKeyboardNav(), so reduced-motion users
    // on a non-keyboard-nav wheel still got a 200ms wedge sweep on every render.
    this._reduceMotion =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;

    if (this.keyboardNav) {
      this.setupKeyboardNav();
      if (this.hasWidgetFocus) this.renderFocusRing();
    }
  }

  // ── Keyboard-nav wiring ────────────────────────────────────────────────────
  // Idempotent: safe to call on every setSvg()/update. Attaches role/tabindex +
  // key/focus/blur listeners to the SVG node and tears down any prior binding
  // first so re-renders never stack duplicate listeners.
  setupKeyboardNav() {
    const node = this.svg.node();
    if (!node) return;
    if (this._kbdCleanup) this._kbdCleanup();

    node.setAttribute("role", "listbox");
    node.setAttribute("tabindex", "0");
    node.setAttribute("aria-orientation", "horizontal");
    node.setAttribute("aria-roledescription", "wheel");
    // We paint our own on-brand ring — suppress the browser's default outline,
    // which renders inconsistently (and often not at all) on an <svg>/path.
    node.style.outline = "none";
    this.refreshWidgetLabel();

    this._reduceMotion =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;

    const onKeyDown = (e) => this.handleWidgetKeydown(e);
    const onFocus = () => this.onWidgetFocus();
    const onBlur = () => this.onWidgetBlur();
    node.addEventListener("keydown", onKeyDown);
    node.addEventListener("focus", onFocus);
    node.addEventListener("blur", onBlur);
    this._kbdCleanup = () => {
      node.removeEventListener("keydown", onKeyDown);
      node.removeEventListener("focus", onFocus);
      node.removeEventListener("blur", onBlur);
    };
  }

  // Segments the keyboard can land on (blank placeholder excluded), in the
  // wheel's own display order.
  navigableSegments() {
    return (this.chartData || []).filter((d) => !d.isBlank);
  }

  segmentA11yLabel(seg) {
    const title = (seg && segmentTitleText(seg.title).trim()) || "Untitled segment";
    return seg && seg.selected ? `${title}, open` : title;
  }

  refreshWidgetLabel() {
    const node = this.svg && this.svg.node();
    if (!node || !this.keyboardNav) return;
    const n = this.navigableSegments().length;
    const name = this.wheelName ? `${this.wheelName} wheel` : "Wheel";
    node.setAttribute(
      "aria-label",
      `${name}, ${n} segment${n === 1 ? "" : "s"}. Use the arrow keys to move between segments, Enter to open.`
    );
  }

  // Decorate the visible arc paths as listbox options (id/role/label/selected)
  // so the accessibility tree exposes each segment by name + state. Called from
  // updateArcs() after the arcs are (re)bound.
  decorateArcA11y() {
    if (!this.keyboardNav || !this.arcG) return;
    const self = this;
    this.arcG
      .selectAll(".arc")
      .attr("id", (d) => (d.data.isBlank ? null : `seg-${d.data.id}`))
      .attr("role", (d) => (d.data.isBlank ? null : "option"))
      .attr("aria-label", (d) =>
        d.data.isBlank ? null : self.segmentA11yLabel(d.data)
      )
      .attr("aria-selected", (d) =>
        d.data.isBlank ? null : d.data.selected ? "true" : "false"
      )
      .attr("aria-hidden", (d) => (d.data.isBlank ? "true" : null));
  }

  clampFocusedIndex() {
    const n = this.navigableSegments().length;
    if (n === 0) {
      this.focusedIndex = null;
      return;
    }
    if (this.focusedIndex == null) return;
    if (this.focusedIndex < 0) this.focusedIndex = 0;
    if (this.focusedIndex > n - 1) this.focusedIndex = n - 1;
  }

  onWidgetFocus() {
    this.hasWidgetFocus = true;
    const segs = this.navigableSegments();
    if (!segs.length) return;
    if (this.focusedIndex == null) {
      // Land on the currently-open segment if one exists, else the first.
      const openIdx = segs.findIndex((s) => s.selected);
      this.focusedIndex = openIdx >= 0 ? openIdx : 0;
    }
    this.clampFocusedIndex();
    this.renderFocusRing();
    this.updateActiveDescendant();
  }

  onWidgetBlur() {
    this.hasWidgetFocus = false;
    this.clearFocusRing();
    const node = this.svg && this.svg.node();
    if (node) node.removeAttribute("aria-activedescendant");
  }

  handleWidgetKeydown(e) {
    const n = this.navigableSegments().length;
    if (!n) return;
    let handled = true;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        this.moveFocus(1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        this.moveFocus(-1);
        break;
      case "Home":
        this.setFocusIndex(0);
        break;
      case "End":
        this.setFocusIndex(n - 1);
        break;
      case "Enter":
      case " ":
      case "Spacebar":
        this.activateFocused(e);
        break;
      case "Escape":
        this.onEscape(e);
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  moveFocus(delta) {
    const n = this.navigableSegments().length;
    if (!n) return;
    const cur =
      this.focusedIndex == null ? (delta > 0 ? -1 : 0) : this.focusedIndex;
    this.focusedIndex = (cur + delta + n) % n;
    this.renderFocusRing();
    this.updateActiveDescendant();
  }

  setFocusIndex(i) {
    const n = this.navigableSegments().length;
    if (!n) return;
    this.focusedIndex = Math.max(0, Math.min(n - 1, i));
    this.renderFocusRing();
    this.updateActiveDescendant();
  }

  activateFocused(e) {
    const segs = this.navigableSegments();
    const seg = segs[this.focusedIndex];
    if (!seg) return;
    const datum = this.pie(this.chartData).find(
      (pd) => pd.data.id === seg.id
    );
    // SAME handler a mouse click uses — never a parallel activation path.
    if (datum) this.arcSelected(e, datum);
  }

  onEscape(e) {
    // Close any open segment (mirrors clicking its arc/close again), then keep
    // focus on the wheel container so the user stays in the widget.
    const openSeg = (this.chartData || []).find(
      (d) => d.selected && !d.isBlank
    );
    if (openSeg) {
      const datum = this.pie(this.chartData).find(
        (pd) => pd.data.id === openSeg.id
      );
      if (datum) this.arcSelected(e, datum);
    }
    const node = this.svg && this.svg.node();
    if (node && typeof node.focus === "function") node.focus();
  }

  updateActiveDescendant() {
    const node = this.svg && this.svg.node();
    if (!node) return;
    const seg = this.navigableSegments()[this.focusedIndex];
    if (seg) node.setAttribute("aria-activedescendant", `seg-${seg.id}`);
    else node.removeAttribute("aria-activedescendant");
  }

  clearFocusRing() {
    if (this.focusRingG) this.focusRingG.selectAll("*").remove();
  }

  // Draw the on-brand dual-tone focus ring on the focused segment. A two-tone
  // light+dark ring is the only construction that clears the WCAG non-text 3:1
  // threshold against an ARBITRARY user-chosen fill: the white halo carries
  // contrast on dark fills, the brand-ink navy #123051 core carries it on light
  // AND saturated mid-tone fills (a single purple/mid ring fails on greens/ambers,
  // measured). Their luminance thresholds overlap with no gap, so at least one
  // always exceeds 3:1. Fade-in is opacity-only (GPU-friendly) and disabled under
  // prefers-reduced-motion.
  renderFocusRing() {
    if (!this.focusRingG || !this.keyboardNav) return;
    this.clampFocusedIndex();
    this.focusRingG.selectAll("*").remove();
    if (!this.hasWidgetFocus) return;
    const seg = this.navigableSegments()[this.focusedIndex];
    if (!seg) return;
    const datum = this.pie(this.chartData).find((pd) => pd.data.id === seg.id);
    if (!datum) return;
    const dAttr = this.arcGenerator(datum);
    if (!dAttr) return;

    const reduce = this._reduceMotion;
    const mkPath = (stroke, width, cls) =>
      this.focusRingG
        .append("path")
        .attr("d", dAttr)
        .attr("class", cls)
        .style("fill", "none")
        .style("stroke", stroke)
        .style("stroke-width", width)
        .style("stroke-linejoin", "round")
        .style("stroke-linecap", "round");

    mkPath("#FFFFFF", 6, "wheel-focus-halo");
    mkPath("#123051", 3, "wheel-focus-ring");

    const paths = this.focusRingG.selectAll("path");
    if (reduce) {
      paths.style("transition", "none").style("opacity", 1);
    } else {
      paths
        .style("transition", "opacity 120ms linear")
        .style("opacity", 0);
      const show = () => this.focusRingG.selectAll("path").style("opacity", 1);
      if (typeof requestAnimationFrame === "function")
        requestAnimationFrame(show);
      else show();
    }
  }

  destroy() {
    if (this._kbdCleanup) this._kbdCleanup();
    this._kbdCleanup = null;
    if (this._cancelFontRelayout) this._cancelFontRelayout();
    this._cancelFontRelayout = null;
    if (this._settleRelayoutTimers) {
      this._settleRelayoutTimers.forEach((t) => clearTimeout(t));
    }
    this._settleRelayoutTimers = null;
  }

  setProfile(userData) {
    // vy-0915-dev-segment-select-white-ghost-disc-still-present: setProfile()
    // is called on EVERY WheelComponent D3-update effect run (index.jsx),
    // including a pure segment-SELECTION change that never touches the
    // portrait itself (updateArcs() already guards this exact class of
    // no-op rebuild for the segment wedge images -- see its
    // `contentUnchanged`/`skipImageRebuild`, "gordon-0914-builder-4908-
    // segment-click-images-vanish-rearrange" -- setProfile() had no
    // equivalent guard). The unconditional `.profile` remove()+rebuild
    // below tears down and recreates the center portrait's <img>, forcing
    // the browser to redecode/repaint it even though its src never
    // changed -- MEASURED live on web-dev.iyuga.net builder/4908 (CDP
    // screencast): the portrait's own foreignObject already resizes
    // correctly within ~150ms of a segment click, but the freshly-recreated
    // <img> stays blank for several hundred ms more, exposing the
    // always-mounted white PORTRAIT_BG_STYLE backing div (WheelComponent/
    // index.jsx) behind it as a plain white disc. Scoped to the "image"
    // branch only (the common case, and what MEASURED reproduced) -- the
    // "video" branch below is untouched and always fully rebuilds, same as
    // before. On a genuine content change (or first paint, or after a
    // setSvg() teardown) the signature/DOM-presence check below fails and
    // the full rebuild runs exactly as it did previously.
    const isImageProfile = !userData?.type || userData.type === "image";
    if (isImageProfile) {
      const signature = JSON.stringify({
        type: userData?.type || "image",
        url: userData?.url,
        name: userData?.name,
        gifHoverPreview: userData?.gifHoverPreview,
        poster: userData?.poster,
      });
      const existingForeignObject = this.svgG.select(".profile foreignObject");
      if (
        this._lastProfileSignature === signature &&
        !existingForeignObject.empty()
      ) {
        const portraitR = this.portraitRadius();
        existingForeignObject
          .attr("x", -portraitR)
          .attr("y", -portraitR)
          .attr("width", portraitR * 2)
          .attr("height", portraitR * 2);
        this.userData = userData;
        return;
      }
      this._lastProfileSignature = signature;
    } else {
      this._lastProfileSignature = null;
    }
    this.userData = userData;
    this.svgG.selectAll(".profile").remove();
    this.svgG.selectAll("defs.patterns").remove();
    // Drop any stale gif-portrait swap ref (the rebuilt portrait re-sets it in the
    // image branch below; video / no-portrait leave it null).
    this._gifPortrait = null;
    const center = this.svgG.append("g").attr("class", "profile");

    if (!this.userData.type || this.userData.type === "image") {
      const onMouseHover = () => {
        centerSvg.style("opacity", this.profileOpacityOnMouseHover);
        this.profileMouseHoverIn();
      };
      const onMouseHoverOut = () => {
        centerSvg.style("opacity", 1);
        this.profileMouseHoverOut();
      };

      const handleClick = () => {
        this.handleProfileClick();
      };

      // BUG-605 (Gordon 2026-08-19, wheel/4783): clicking the centre Portrait
      // drew the browser's default UA focus rectangle (a "square/selection
      // box") around the wheel. Root cause: this foreignObject sits inside
      // `<svg id="chart">`, which setupKeyboardNav makes a Tab stop
      // (tabindex=0) whenever keyboardNav is on (the public View Wheel opts
      // in). Clicking any non-focusable descendant of a focusable element
      // (like this foreignObject, which owns no tabindex/focus of its own)
      // runs the browser's default mousedown action of focusing the nearest
      // focusable ancestor — #chart — which then paints its on-brand
      // `:focus-visible` box-shadow ring (index.scss) even though the
      // interaction was a mouse click, not keyboard nav. Wedge clicks hit the
      // same mechanism, but the resulting Segment panel draws attention away
      // from it; the Portrait click is a no-op on the public View Wheel
      // (handleProfileClick defaults to a no-op), so the stray square is the
      // ONLY visible effect. preventDefault() on mousedown is the standard
      // way to stop a click from moving DOM focus at all (Tab-key navigation
      // fires no mousedown, so keyboard focus/the on-brand ring are
      // untouched) — same fix shape as engine.js's BUG-605 (outline:none with
      // no ring reintroduced), just applied at the event-default layer since
      // this element has no tabindex of its own to suppress an outline on.
      const handleMouseDown = (event) => {
        event.preventDefault();
      };

      const centerSvg = center
        .append("g")
        .on("mouseover", onMouseHover)
        .on("mouseout", onMouseHoverOut);

      // Render the portrait as an HTML <img> inside a foreignObject (same
      // approach as the video branch below) so animated GIFs keep playing. An
      // SVG <image> only paints a single static frame for GIFs (animation is
      // undefined per spec), which froze animated portraits.
      //
      // No crossorigin attribute: Safari strictly enforces "access control
      // checks" and refuses to render an HTML <img> requested with
      // crossorigin="anonymous" when the host returns no
      // Access-Control-Allow-Origin header (e.g. the prod S3 bucket or arbitrary
      // user-pasted hosts), leaving the gallery portrait blank. The old SVG
      // <image> tolerated this; the new <img> does not. crossorigin is not
      // needed for exports either — html-to-image re-fetches each URL via
      // fetch() and inlines it as a data URL, ignoring this attribute entirely.
      // Fill the wedge hole exactly (see portraitRadius) — bare pieInnerRadius
      // left a 2–5px white ring between the photo and the segments.
      const portraitR = this.portraitRadius();
      const foreignObject = centerSvg
        .append("foreignObject")
        .attr("x", -portraitR)
        .attr("y", -portraitR)
        .attr("width", portraitR * 2)
        .attr("height", portraitR * 2)
        .style("cursor", "pointer")
        .on("mousedown", handleMouseDown)
        .on("click", handleClick);

      // ISP-9999 GIF portrait still-first (gallery/explore CARD only): when this is
      // a gif portrait AND the surface opted into gif-hover (userData.gifHoverPreview,
      // set by WheelComponent behind the TILE_GIF_HOVER flag), render the POSTER
      // still by default and swap to the gif on card hover (animateGifPortrait /
      // freezeGifPortrait). Reduced-motion users keep the still (the swap is a no-op).
      // Every other surface (full wheel VIEW, flag off, non-gif) is byte-unchanged.
      const portraitUrl = this.userData.url;
      const wantsGifStill =
        looksLikeGifUrl(portraitUrl) && !!this.userData.gifHoverPreview;
      const portraitPoster = wantsGifStill
        ? this.userData.poster || posterUrlForGif(portraitUrl)
        : null;
      // §12 fallback: no portrait url → colored iYuga company logo (never a
      // bare/broken center). Portrait-present wheels are byte-unchanged.
      const restingSrc =
        (wantsGifStill && portraitPoster ? portraitPoster : portraitUrl) ||
        DefaultIyugaLogo;

      // ISP-9999 (Gordon 2026-07-21): brand-logo fallback centers get breathing
      // room (contain + white field + inset) so the full "iYuga" wordmark shows;
      // a real user portrait is unchanged (cover, no bg, no padding).
      const isBrandLogo = isBrandLogoSrc(restingSrc);
      const portraitWrap = foreignObject
        .append("xhtml:div")
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "50%")
        .style("overflow", "hidden");
      if (isBrandLogo) {
        portraitWrap
          .style("background", "#ffffff")
          .style("box-sizing", "border-box")
          .style("padding", `${this.brandLogoInsetPct}%`);
      }
      const portraitImg = portraitWrap
        .append("img")
        .attr("src", restingSrc)
        // A11y (ISP-4018): name the portrait for screen readers when the
        // wheel's name is known; otherwise alt="" marks it decorative so SRs
        // skip it instead of announcing the raw src URL/filename.
        //
        // ...but ONLY when the rendered image really IS this wheel's portrait.
        // Measured live on web-stg /explore 2026-07-28: 12/12 cards render the
        // generic iYuga brand-logo FALLBACK (no portrait stored) yet still
        // carried alt="<wheel name>", so a screen reader announced a shared
        // placeholder as that wheel's own picture — and announced the name a
        // second time, since the card's own heading already carries it. A
        // placeholder is decorative: alt="" (WCAG 1.1.1).
        .attr("alt", isBrandLogo ? "" : this.userData.name || "")
        // BUG-gallery-bc4968 (Gordon, live find 2026-08-13): the default
        // <img> display is "inline", so this element sits inside an
        // anonymous inline-formatting-context line box governed by the
        // AMBIENT (inherited, unrelated) line-height, and gets positioned by
        // `vertical-align` relative to that line's baseline rather than the
        // true center of its parent. Invisible whenever the img fills its
        // wrap edge-to-edge (no padding: real portraits, cover) — but the
        // isBrandLogo wrap adds 17% padding (BRAND_LOGO_INSET_PCT), and once
        // the shrunk img is shorter than the ambient line-height the
        // mismatch becomes a real, visible vertical offset. Measured live on
        // web-stg /gallery/21 (account-menu avatar, 30x30 wrap): gapTop
        // 8.16px vs gapBottom 2.03px (should be ~5.09px each) — the "UR
        // avatar logo off-center" half of the bug report. display:block
        // takes the <img> out of inline flow entirely so it fills its
        // parent's content box via plain block layout, with no
        // line-height dependency — true geometric centering regardless of
        // wrap size.
        .style("display", "block")
        .style("width", "100%")
        .style("height", "100%")
        .style("object-fit", isBrandLogo ? "contain" : "cover");

      // T13.7 (2026-10-09): the wedges already paint instantly (solid CSS
      // colour), but this <img> paints nothing until its own network
      // fetch+decode finishes — with the wrap transparent by design
      // (ISP-4018 below), that left a bare white disc at the wheel's centre
      // for the whole gap (measured live web-stg.iyuga.net /wheel/507106,
      // ~700-900ms). Same "paint a fallback colour first" doctrine
      // updateImage() already uses for wedge image fills (a few hundred
      // lines below): a neutral tint while loading, cleared the instant the
      // photo paints so RP-A1.2's no-persistent-wash guarantee is untouched
      // once loaded. The brand-logo fallback already gets its own
      // deliberate white field above, so this only applies to a real photo.
      const clearLoadingTint = () =>
        portraitWrap.style("background-color", null);
      if (!isBrandLogo) {
        portraitWrap.style("background-color", DEFAULT_WEDGE_COLOR);
        portraitImg
          .node()
          ?.addEventListener("load", clearLoadingTint, { once: true });
      }

      // §12 never-hollow (RP-A2.4 / RP-A1.4): swap a broken portrait for the
      // iYuga logo. The `|| DefaultIyugaLogo` above only covers a FALSY url; a
      // present-but-broken url (e.g. an expired presigned S3 link returning 403)
      // had NO handler in this branch, so the browser painted a broken-image
      // glyph / hollow white center (measured on live Explore mini-wheels). This
      // restyles the wrap/img to the brand-logo layout (contain + white field +
      // inset) so the wordmark reads correctly, matching the falsy-url fallback.
      const applyLogoFallback = () => {
        // review-1008-Frontend-6730: the swapped-in logo's own `load` must not
        // fire the T13.7 tint-clear above — it would strip the white field set
        // below (logo then shows as a hard-edged white square on the card).
        portraitImg.node()?.removeEventListener("load", clearLoadingTint);
        reportBrokenMediaOnce({
          wheelId: this.wheelId,
          kind: "wheel_profile",
          url: portraitUrl,
        });
        portraitImg
          .attr("src", DefaultIyugaLogo)
          // The swapped-in image is the shared placeholder, not this wheel's
          // portrait — drop the wheel-name alt with it (same law as the
          // falsy-url branch above), so a broken portrait does not leave a
          // screen reader believing the logo IS the wheel's picture.
          .attr("alt", "")
          .style("object-fit", "contain");
        portraitWrap
          .style("background", "#ffffff")
          .style("box-sizing", "border-box")
          .style("padding", `${this.brandLogoInsetPct}%`);
      };

      if (wantsGifStill && portraitPoster) {
        // Keep refs so a card hover can swap poster<->gif. onError: if the poster
        // ever fails to load (e.g. an external gif with no generated poster), fall
        // back to the gif once so the center is never broken.
        this._gifPortrait = { img: portraitImg, poster: portraitPoster, gif: portraitUrl };
        portraitImg.node()?.addEventListener(
          "error",
          () => {
            if (this._gifPortrait) this._gifPortrait.poster = portraitUrl;
            // Second-level fallback: if the gif ALSO fails, land on the logo so
            // the center is never hollow. Register before swapping the src.
            portraitImg.node()?.addEventListener("error", applyLogoFallback, {
              once: true,
            });
            portraitImg.attr("src", portraitUrl);
          },
          { once: true }
        );
      } else {
        this._gifPortrait = null;
        // A real (non-logo) portrait that fails to load falls back to the logo.
        if (restingSrc !== DefaultIyugaLogo) {
          portraitImg
            .node()
            ?.addEventListener("error", applyLogoFallback, { once: true });
        }
      }

      // ISP-4018: no portrait border ring. The former semi-transparent white
      // ".center-image-border" circle (r = pieInnerRadius + 2, white @ 0.5
      // opacity) painted a translucent white wash over the inner edge of the
      // wedges, making the wheel read as bi-colored / off-brand. Removed so the
      // portrait sits directly ringed by the wedges as one continuous wheel.
    } else if (this.userData.type === "video") {
      const centerSvg = center.append("g");
      const portraitR = this.portraitRadius();

      // Add clip path
      centerSvg
        .append("defs")
        .append("clipPath")
        .attr("id", "circle-clip")
        .append("circle")
        .attr("r", portraitR);

      // Create foreignObject for video and thumbnail
      const foreignObject = centerSvg
        .append("foreignObject")
        .attr("x", -portraitR)
        .attr("y", -portraitR)
        .attr("width", portraitR * 2)
        .attr("height", portraitR * 2);

      const container = foreignObject
        .append("xhtml:div")
        .style("position", "relative")
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "100%");

      // Add thumbnail image
      const thumbnail = container
        .append("img")
        .attr("src", this.userData.thumbnail) // Replace with your thumbnail URL
        // A11y (ISP-4018): same alt rule as the image-branch portrait above.
        .attr("alt", this.userData.name || "")
        .style("position", "absolute")
        .style("top", 0)
        .style("left", 0)
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "100%")
        .style("object-fit", "cover");

      // Add video element
      const videoElement = container
        .append("video")
        .attr("id", "profileVideo")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("muted", false)
        .style("position", "absolute")
        .style("top", 0)
        .style("left", 0)
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "100%")
        .style("display", "none");

      videoElement
        .append("source")
        .attr("src", this.userData.url) // sampleVideo
        .attr("type", "video/mp4");

      let playValue = "pause";

      // Create play button
      const playButton = centerSvg
        .append("circle")
        .attr("r", this.pieInnerRadius * 0.2)
        .style("fill", "white")
        .style("opacity", 0)
        .on("mouseover", () => {
          playButton.style("opacity", 1);
          playButtonIcon.style("opacity", 1);
        })
        .on("click", playButtonClick);

      // Create play button icon
      const playButtonIcon = centerSvg
        .append("foreignObject")
        .attr("x", -this.pieInnerRadius * 0.1)
        .attr("y", -this.pieInnerRadius * 0.1)
        .attr("width", this.pieInnerRadius * 0.2)
        .attr("height", this.pieInnerRadius * 0.2)
        .style("opacity", 1)
        .html(
          '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="currentColor"/></svg>'
        ) // FaPlay icon
        .on("click", playButtonClick);

      // Change icon on mouseover
      foreignObject
        .on("mouseover", () => {
          playButton.style("opacity", 1);
          playButtonIcon.style("opacity", 1);
          setTimeout(() => {
            const iconHtml =
              playValue === "pause"
                ? '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="currentColor"/></svg>' // FaPlay icon
                : '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 19h4.5V5H6v14zm7.5-14v14H18V5h-4.5z" fill="currentColor"/></svg>'; // FaPause icon
            playButtonIcon.html(iconHtml);
          });
        })
        .on("mouseout", () => {
          playButton.style("opacity", 0);
          playButtonIcon.style("opacity", 0);
        });

      function playButtonClick() {
        const video = videoElement.node();
        if (playValue === "pause") {
          playValue = "play";
          thumbnail.style("display", "none"); // Hide thumbnail
          videoElement.style("display", "block"); // Show video
          video.play();
          setTimeout(() => {
            playButtonIcon.html(
              '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 19h4.5V5H6v14zm7.5-14v14H18V5h-4.5z" fill="currentColor"/></svg>'
            ); // FaPause icon
          });
        } else {
          playValue = "pause";
          video.pause();
          setTimeout(() => {
            playButtonIcon.html(
              '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="currentColor"/></svg>'
            ); // FaPlay icon
          });
        }
      }
    }
  }

  // `animate` (default true): a genuine chartData/selection change tweens the
  // entrance transition below. A pure container-RESIZE redraw (WheelComponent's
  // ResizeObserver debounce) passes animate=false — QA-61 already seeds resize
  // re-entrants at their FINAL angle so the tween is a visual no-op, but the
  // transition still runs for its full 200ms duration regardless. If the
  // container is STILL settling (e.g. a side-pane opening) and a second resize
  // tick lands mid-transition, `.transition()` on the same selection INTERRUPTS
  // the in-flight one and restarts a fresh 200ms run from wherever it was
  // caught — repeated resize ticks close enough together therefore chain into
  // a visibly stepped/frozen redraw even though no single tween ever moves the
  // geometry (gordon-0915-selected-segment-effect-prod-parity, Kelly's
  // "double-bounce"). animate=false skips `.transition()` entirely for a
  // resize-triggered call and sets `d` directly, so a resize redraw is always
  // ONE settled frame with nothing left running to interrupt.
  updateArcs(data, { animate = true } = {}) {
    const self = this;
    const prevChartData = this.chartData;
    this.chartData = data;
    if (!this.chartData?.length) {
      this.chartData = this.emptyChartData;
    }
    // ISP-9999 (label-blink-on-select): only reuse the existing label DOM when
    // the pills are already drawn AND this update changes nothing but which
    // segment(s) are selected — a real content edit (title/value/color/
    // position/count) always falls through to the normal rebuild below.
    const labelsAlreadyDrawn =
      this.isShowLabelOn &&
      this.titleG &&
      !this.titleG.selectAll("g.label-group").empty();
    const contentUnchanged = chartDataUnchangedExceptSelection(prevChartData, this.chartData);
    const selectionOnly = labelsAlreadyDrawn && contentUnchanged;
    // kelly-1002-editwheel-suspicion (staggered segment movement on open):
    // a successful autosave dispatches a BRAND-NEW chartData array/object
    // identity even when nothing render-relevant actually changed (see
    // SegmentBuilder/index.jsx's post-save `setWheelSegments(updatedSegments)`,
    // which always remaps every segment to a fresh object for id-reconciliation
    // purposes). That reference change alone re-runs WheelComponent's update
    // effect, which calls updateArcs() with its default animate=true — replaying
    // the full 200ms wedge-geometry tween a second time, ~0.5-1s after the
    // user's own click, for a redraw that is pixel-identical to what is already
    // on screen. MEASURED live on web-stg.iyuga.net/builder/507177: clicking a
    // segment produced two distinct geometry transitions (one at the click,
    // t+0..~200ms; a second, unprompted one at t+500..~700ms, aligned with the
    // autosave PUT's response) — the "weird staggered movement" report.
    // `contentUnchanged` already ignores `selected` (by design, for the label/
    // image fast path above); also comparing `selected` here catches the ONE
    // case that matters for geometry — truly nothing changed, not even which
    // wedge is open — so a genuine selection click (where `selected` DOES
    // differ) still falls through to the real tween below.
    //
    // `this._entrancePlayed` guard (found via createPie.qa61EntranceOnce.test.js
    // + createPie.reducedMotionEntrance.test.js going red): the FIRST-ever
    // updateArcs() call after construction is seeded with `prevChartData`
    // identical to the just-passed-in initial data (both unselected), so
    // without this guard `nothingToAnimate` was also true on MOUNT — skipping
    // the real entrance transition's 200ms duration and its collapsed-start
    // seed, making every wheel render fully "popped in" instead of sweeping
    // open. Only a call AFTER the entrance has already played once can
    // legitimately be a no-op save-echo.
    const nothingToAnimate =
      this._entrancePlayed &&
      contentUnchanged &&
      prevChartData.every((a, i) => !!a.selected === !!this.chartData[i].selected);
    // ISP-9999 (gordon-0914-builder-4908-segment-click-images-vanish-rearrange):
    // updateImage() used to run on EVERY updateArcs() call, including a pure
    // selection-only click — it unconditionally removes every <defs> under
    // arcG then rebuilds every segment's <pattern>/<image> from scratch, so
    // ALL segment images (not just the clicked one) blanked out and
    // re-decoded on every single Segment click. When nothing but `selected`
    // changed, the existing defs are already correct — skip the rebuild.
    const defsAlreadyDrawn = !this.arcG.selectAll("defs").empty();
    const skipImageRebuild = contentUnchanged && defsAlreadyDrawn;
    this.selectedArcs = [];
    this.pie(this.chartData).forEach((dd) => {
      if (dd.data.selected) this.selectedArcs.push(dd.data.id);
    });
    // stgfix-10101713-T42-auto (T4.2): resizeSvg(), not a bare resize(). resize()
    // re-sizes the svg + radii to the CURRENT container but leaves the visible/
    // hidden groups translated to the OLD center, so a redraw that lands after
    // the container grew (Edit Wheel first open: pie built at the legacy 500px,
    // container then fit to 672px) painted a full-size ring centered at 250,250
    // -- ~86px up-left of its own white backing disc -- until the debounced
    // ResizeObserver redraw re-centered it (MEASURED on web-stg wheel 507652).
    this.resizeSvg();
    const arcs = this.arcG
      .selectAll(".arc")
      .data(this.pie(this.chartData), (d) => d.data.id);

    arcs.exit().remove();

    if (!skipImageRebuild) {
      this.updateImage();
    }

    const merged = arcs
      .enter()
      .append("path")
      .attr("class", "arc")
      .attr("d", this.arcGenerator)
      .each(function (d) {
        // Seed the arc-tween start so the first transition frame doesn't jump.
        //
        // ISP-9999 / QA-61: the zero-width→full entrance sweep is a MOUNT-ONLY
        // flourish. setSvg() wipes arcG and re-appends it (svg.selectAll("g")
        // .remove()), so on ANY resize every arc RE-ENTERS through this seed —
        // and the ResizeObserver behind updateSize() fires setSvg() on spurious
        // container reflows too (typing a Segment title, dropping a Capture
        // block). Without the guard those unrelated edits replay the entrance and
        // the whole wheel visibly rotates though no wedge geometry changed
        // (Kelly, 2026-08-03). After the first draw, seed re-entered arcs at their
        // FINAL angle (endAngle === d.endAngle) so a rebuild is visually static:
        // prev === target ⇒ the attrTween below has a zero-length interpolation.
        this._current = {
          startAngle: d.startAngle,
          endAngle: self._entrancePlayed ? d.endAngle : d.startAngle,
          padAngle: d.padAngle,
        };
      })
      .style("fill", (d) => this.wedgeArcFill(d))
      .style("stroke-width", this.arcPaddingRadius)
      .merge(arcs)
      .style("stroke", (d) => {
        if (this.segmentHighlighting && this.selectedArcs.length) {
          if (this.selectedArcs.find((item) => item === d.data.id)) {
            return this.segmentBorder;
          }
        }
      })
      .style("opacity", (d) => {
        if (this.segmentHighlighting && this.selectedArcs.length) {
          if (!this.selectedArcs.find((item) => item === d.data.id)) {
            return this.segmentOpacity;
          }
        }
      });
    if (!animate || nothingToAnimate) {
      // Resize-triggered redraw (or a redraw whose chartData is byte-identical
      // to what's already on screen, incl. `selected` — see nothingToAnimate
      // above): settle every arc's `d` (and its tween seed, so a LATER genuine
      // selection/data change still tweens from the right place) in one
      // synchronous frame — no `.transition()` to interrupt or restart if
      // another resize tick lands 50ms later.
      merged.each(function (d) {
        const current = { startAngle: d.startAngle, endAngle: d.endAngle, padAngle: d.padAngle };
        this._current = current;
        this.setAttribute("d", self.arcGenerator({ ...current, data: d.data }));
      });
      merged.style("fill", (d) => this.wedgeArcFill(d));
    } else {
      merged
        .transition()
        .duration(this._reduceMotion ? 0 : this.chartData.length > 2 ? 200 : 0)
        .ease(d3.easeLinear)
        // Tween the datum angles (not the raw path string) so every frame
        // regenerates a VALID arc — keeps the large-arc-flag a clean 0/1 instead
        // of interpolateString passing through fractional flag values.
        .attrTween("d", function (d) {
          const prev = this._current || {
            startAngle: d.startAngle,
            endAngle: d.startAngle,
            padAngle: d.padAngle,
          };
          const target = {
            startAngle: d.startAngle,
            endAngle: d.endAngle,
            padAngle: d.padAngle,
          };
          const i = d3.interpolate(prev, target);
          this._current = i(1);
          // Re-attach the real datum's `.data` (selection state, colour, etc) on
          // every tween frame. `prev`/`target` above carry ONLY the three angle
          // fields — d3.interpolate's result therefore has no `.data` — but the
          // live arcGenerator's outerRadius accessor reads `d?.data?.selected` to
          // apply the LAW 2 bulge. Without this, EVERY frame (including the final
          // one that becomes the settled `d`) resolves `.data` as undefined, so a
          // wedge selected/deselected via an UPDATE (not a fresh mount) loses its
          // bulge the instant this transition runs — the wedge briefly renders
          // taller on the synchronous entrance draw, then snaps back down/inward
          // once the tween's last frame overwrites `d` (the reported jump/shift).
          // `.data` itself never changes mid-tween (only start/endAngle do), so
          // reattaching the SAME reference on every frame is correct, not stale.
          return (t) => self.arcGenerator({ ...i(t), data: d.data });
        })
        .style("fill", (d) => this.wedgeArcFill(d));
    }
    // ISP-9999 / QA-61: the one-time mount entrance has now been seeded. Every
    // later updateArcs (and every setSvg rebuild it triggers) seeds re-entered
    // arcs at their final geometry via the guard in the .each above, so no edit
    // re-spins a wheel that is already on screen. A brand-new instance (View
    // Wheel / Gallery card / fresh Builder load) starts with the flag unset, so
    // the intended first-load entrance is preserved everywhere.
    self._entrancePlayed = true;
    // Contrast hairline guard: draw a boundary stroke on only the low-contrast
    // (near-invisible) segments. No-op unless the flag/override is on; adequate
    // segments are left byte-identical. Runs on the freshly-bound .arc selection.
    this.applyContrastHairline();
    // EMPTY WEDGE OUTLINE: the dashed boundary that makes a transparent
    // (colorless / blank) wedge unambiguous next to a pale-but-valid one.
    // No-op (and zero extra DOM) flag OFF or when no wedge is empty.
    this.applyEmptyWedgeOutline();
    // ISP-9999: refresh the per-wedge radial-outward transform vars (+ container
    // class) so the CSS hover "breath" targets the CURRENT geometry after any
    // reorder/resize. No-op (byte-identical) unless segmentHoverMotion is opted in.
    this.applyWedgeHoverMotion();
    // Adaptive segment icons: per-segment icons that adapt to the segment's
    // size (full icon -> dot -> nothing) and color. updateNumberLable() stays
    // disabled here — number labels are a separate, unrequested surface kept
    // off to avoid changing current rendering.
    this.updateIcons();
    this.mapDndChartData();
    if (this.isShowLabelOn) {
      if (selectionOnly) {
        // T4.11 (stgfix-1008-N08-r2-segment-pills-jump-34-42-px-for-300-ms-when):
        // `selectionOnly` only requires title/value/color/position/pattern/fill
        // to be unchanged — it says nothing about whether `selected` itself
        // changed, so a pure save-echo (e.g. a video block's upload-start/
        // upload-end autosave, which never touches any of those 7 fields)
        // still lands here on every redundant chartData dispatch. Unlike the
        // arc tween above (already guarded by `nothingToAnimate`,
        // kelly-1002), this call was unconditional: it re-measures
        // `self.parent.clientWidth` LIVE and re-applies the pane-boundary
        // clamp every single time, so a save-echo whose dispatch happens to
        // land mid an unrelated transient reflow (exactly what a video
        // block's own UI swap causes) re-clamps every pill to a position
        // based on that transient width — painted immediately, and only
        // self-corrected ~300ms later by scheduleSettleRelayout()'s first
        // timer.
        //
        // This must NOT also swallow a genuine resize tick:
        // WheelComponent's ResizeObserver path (updateSize(), index.jsx)
        // calls updateArcs(data, { animate: false }) with that SAME
        // content-and-selection-unchanged `data` on every real container
        // resize — `nothingToAnimate` is true there too, but the pills
        // genuinely need to re-anchor to the new geometry (createPie.
        // settleRelayoutAfterResize.test.js / reducedMotionMidSession.test.js
        // depend on exactly this). The resize pipeline always passes an
        // explicit `animate: false`; every data/redux-reactive call
        // (including the save-echo) uses the default `animate: true`
        // (WheelComponent/index.jsx's two plain `updateArcs(hydratedChartData)`
        // calls) — so `animate` itself is the reliable signal for "this is
        // the resize pipeline, not a content dispatch", and only a true
        // no-op on the NON-resize path can safely skip the reposition — see
        // createPie.saveEchoNoLabelReclamp.test.js.
        if (!(animate && nothingToAnimate)) {
          this.syncLabelSelectionTransforms();
        }
      } else {
        this.titleG.selectAll("text").remove();
        this.titleG.selectAll("g").remove();
        this.polyLinesG.selectAll("polyline").remove();
        this.lineG.selectAll("line").remove();
        // this.updatedTitle();
        this.updateLable();
      }
    }
    // Keyboard-nav a11y: expose each arc as a named listbox option, refresh the
    // widget label + roving focus after the data/geometry changed.
    if (this.keyboardNav) {
      this.decorateArcA11y();
      this.refreshWidgetLabel();
      this.clampFocusedIndex();
      this.renderFocusRing();
      this.updateActiveDescendant();
    }
  }

  // Resolve the paint value for one wedge arc. Precedence: a REAL gradient def
  // (url) wins, then an image/gif/procedural-pattern def (url), then a solid —
  // the gradient's first stop (conic fallback) if any, else the segment color,
  // else the default. ONE source of truth so the initial-draw and tween-end fill
  // accessors can never diverge. Flag-OFF → wedgeGradientProps/wedgeFillProps are
  // null → byte-identical to the pre-EW-003 `color || DEFAULT_WEDGE_COLOR` path.
  wedgeArcFill(d) {
    const grad = this.wedgeGradientProps(d);
    if (grad && grad.kind !== "solid") return `url(#${this.wedgeGradientId(d)})`;
    if (d.data.pattern || this.wedgeFillProps(d)) return `url(#${this.wedgePatternId(d)})`;
    if (grad && grad.solid) return grad.solid;
    // EMPTY WEDGE (flag REACT_APP_EMPTY_WEDGE_OUTLINE). Reached ONLY when the
    // wedge has no gradient / pattern / image fill, i.e. exactly the case
    // DEFAULT_WEDGE_COLOR was invented for. `transparent` (never `none`) so the
    // wedge still hit-tests — the builder's click/hover/drag depend on it.
    // Flag OFF ⇒ the original `color || DEFAULT_WEDGE_COLOR` line, unchanged.
    if (this.emptyWedgeOutlineOn() && isEmptyWedgeDatum(d.data)) {
      return EMPTY_WEDGE_FILL;
    }
    return d.data.color || DEFAULT_WEDGE_COLOR;
  }

  // Enable decision for the empty-wedge treatment. Production leaves the
  // per-instance override undefined ⇒ collapses to the build-time flag.
  emptyWedgeOutlineOn() {
    return isEmptyWedgeOutlineEnabled(this.emptyWedgeOutline);
  }

  // ── Empty-wedge outline ───────────────────────────────────────────────────
  // The companion to the transparent fill above: a DASHED boundary drawn as a
  // SEPARATE inset path, never as a stroke on the wedge itself. Two reasons,
  // both learned the hard way on the hairline work:
  //   · the wedge path keeps its white divider stroke, so an empty wedge stays
  //     the exact same visible size as a filled one (Gordon 2026-07-21), and the
  //     white divider doubles as a halo that keeps the gray dashes legible on a
  //     photo / pattern wallpaper;
  //   · the outline layer is pointer-events:none, so it can never steal the
  //     clicks the transparent fill is there to keep receiving.
  // Geometry reuses insetWedgeForHairline (half the divider width) — the SAME
  // inset the contrast guard uses, so the two never trace different boundaries.
  // No empty wedge on the wheel ⇒ zero extra DOM (byte-identical).
  emptyWedgeOutlinePath(d, i) {
    const baseInner = this.arcGenerator.innerRadius();
    const baseOuter = this.arcGenerator.outerRadius();
    const geo = insetWedgeForHairline(
      {
        innerRadius: baseInner(d, i),
        outerRadius: baseOuter(d, i),
        startAngle: d.startAngle,
        endAngle: d.endAngle,
      },
      this.arcPaddingRadius
    );
    return d3
      .arc()
      .innerRadius(geo.innerRadius)
      .outerRadius(geo.outerRadius)
      .startAngle(geo.startAngle)
      .endAngle(geo.endAngle)();
  }

  applyEmptyWedgeOutline() {
    const existingG = this.arcG.select("g.arc-empty-outline-g");
    const clear = () => {
      if (!existingG.empty()) existingG.remove();
    };
    if (!this.emptyWedgeOutlineOn()) return clear();

    // Same source of truth as the paint: a wedge is empty iff its resolved fill
    // IS the empty paint, so the outline and the transparency can never disagree.
    const data = this.arcG
      .selectAll(".arc")
      .data()
      .filter((d) => this.wedgeArcFill(d) === EMPTY_WEDGE_FILL);
    if (!data.length) return clear();

    let eg = existingG;
    if (eg.empty()) {
      eg = this.arcG
        .append("g")
        .attr("class", "arc-empty-outline-g")
        .attr("pointer-events", "none");
    }
    eg.raise();
    const sel = eg
      .selectAll("path.arc-empty-outline")
      .data(data, (d) => (d.data && d.data.id != null ? d.data.id : d.index));
    sel.exit().remove();
    sel
      .enter()
      .append("path")
      .attr("class", "arc-empty-outline")
      .attr("fill", "none")
      .style("stroke-linejoin", "round")
      .merge(sel)
      .attr("d", (d, i) => this.emptyWedgeOutlinePath(d, i))
      .style("stroke", EMPTY_WEDGE_OUTLINE_COLOR)
      .style("stroke-width", EMPTY_WEDGE_OUTLINE_WIDTH)
      .style("stroke-dasharray", EMPTY_WEDGE_OUTLINE_DASH);
  }

  // ── Contrast hairline guard ────────────────────────────────────────────────
  // Flag REACT_APP_WHEEL_SEGMENT_HAIRLINE (default OFF; per-instance override via
  // config.segmentHairline for fixtures/tests). When enabled, draw a 1px boundary
  // stroke on ONLY the segments whose solid fill is indistinguishable from the
  // effective backdrop (the near-white "blank slice"). The stroke is the segment's
  // OWN fill shaded a fixed delta (darkened on a light backdrop, lightened on a
  // dark one) — never a generic gray.
  //
  // GEOMETRY (Gordon 2026-07-21): the base render leaves EVERY wedge its white 2px
  // divider stroke (`.arc { stroke: white }` × arcPaddingRadius) — we never touch
  // it — so a near-white segment stays the exact same visible size as a dark one
  // (the earlier build replaced that wide white stroke with a thin one, which made
  // light segments look larger and pushed the line to the outer rim). The hairline
  // is instead a SEPARATE path, inset by half the divider width via
  // insetWedgeForHairline, so it traces exactly where the colour meets the white
  // rim / inter-wedge border, never the wheel's outer rim.
  //
  // A gradient/pattern (url) fill is skipped (needsHairline rejects a non-colour).
  // Adequate-contrast segments and a fully-coloured wheel add ZERO extra DOM
  // (byte-identical, VRT-0). Highlighted segments are excluded so the hairline
  // never fights a selection border.
  resolveBackdrop() {
    if (this.segmentBackdrop) return this.segmentBackdrop;
    return resolveEffectiveBackdrop(this.parent);
  }

  // Build the inset SVG arc path (fill-none outline) that traces a wedge's
  // coloured region, inside its white divider. Uses the SAME base radii as the
  // live arcGenerator (incl. the selected-wedge outer bump) so it stays locked to
  // the rendered geometry.
  hairlineArcPath(d, i) {
    return this.hairlineArcPathAtDelta(d, i, 0);
  }

  // bug #910 (part 2): the SAME hairline geometry as hairlineArcPath, but with the
  // wedge-hover-motion delta folded into the outer radius BEFORE insetting — the
  // hairline's own LAW 1 REVISED companion. applyContrastHairline() runs once per
  // updateArcs(), not per hover frame, so without this the low-contrast overlay
  // stays pinned to the settled (delta=0) boundary while arcAtDelta grows the
  // wedge's actual outer edge past it: a bare, hairline-less sliver of near-white
  // fill opens up mid-hover — the white-segment-hairline ruling's gap, this time
  // between the fill and its own boundary marker rather than at the inner rim.
  // setWedgeHoverExpansion calls this every tick (same live delta as arcAtDelta)
  // so the two stay locked together at every frame, not just at rest.
  hairlineArcPathAtDelta(d, i, delta) {
    const baseInner = this.arcGenerator.innerRadius();
    const baseOuter = this.arcGenerator.outerRadius();
    const geo = insetWedgeForHairline(
      {
        innerRadius: baseInner(d, i),
        outerRadius: baseOuter(d, i) + delta,
        startAngle: d.startAngle,
        endAngle: d.endAngle,
      },
      this.arcPaddingRadius
    );
    return d3
      .arc()
      .innerRadius(geo.innerRadius)
      .outerRadius(geo.outerRadius)
      .startAngle(geo.startAngle)
      .endAngle(geo.endAngle)();
  }

  applyContrastHairline() {
    const enabled = isSegmentHairlineEnabled({
      segmentHairline: this.segmentHairline,
    });
    const existingG = this.arcG.select("g.arc-hairline-g");
    const clear = () => {
      if (!existingG.empty()) existingG.remove();
    };
    if (!enabled) return clear();

    const backdrop = this.resolveBackdrop();
    const backdropDark = isDarkBackdrop(backdrop);
    const isHighlighted = (d) =>
      !!(
        this.segmentHighlighting &&
        this.selectedArcs.length &&
        this.selectedArcs.find((item) => item === d.data.id)
      );
    // Only the genuinely-invisible (near-backdrop) solid segments get a hairline.
    const data = this.arcG
      .selectAll(".arc")
      .data()
      .filter(
        (d) => !isHighlighted(d) && needsHairline(this.wedgeArcFill(d), backdrop)
      );
    // No qualifying segment ⇒ no extra DOM at all (fully-coloured wheel stays
    // byte-identical flag-ON vs flag-OFF).
    if (!data.length) return clear();

    let hg = existingG;
    if (hg.empty()) {
      hg = this.arcG
        .append("g")
        .attr("class", "arc-hairline-g")
        .attr("pointer-events", "none");
    }
    hg.raise(); // keep the hairlines above the freshly-bound arcs
    const sel = hg
      .selectAll("path.arc-hairline")
      .data(data, (d) => (d.data && d.data.id != null ? d.data.id : d.index));
    sel.exit().remove();
    sel
      .enter()
      .append("path")
      .attr("class", "arc-hairline")
      .attr("fill", "none")
      .style("stroke-linejoin", "round")
      .merge(sel)
      .attr("d", (d, i) => this.hairlineArcPath(d, i))
      .style("stroke", (d) =>
        deriveHairlineStroke(this.wedgeArcFill(d), backdropDark)
      )
      .style("stroke-width", HAIRLINE_STROKE_WIDTH);
  }

  // LAW 2 (Gordon 2026-08-09): the selected-segment bulge grows the OUTER radius
  // ONLY — a taller/thicker wedge that eases OUTWARD — by a SLIGHT, radius-
  // PROPORTIONAL amount. The inner radius and the angular span are untouched
  // (padAngle-independent), so the wedge never widens and the white divider strokes
  // on both of its edges are preserved. Replaces the former fixed `padding * 2`
  // absolute bump, which read as a large both-directions balloon on smaller wheels
  // (the absolute px dominated a small radius). Clamped so it stays perceptible on a
  // thumbnail yet slight on a large builder wheel.
  selectedBulgeDelta() {
    const r = this.pieOutterRadius || 0;
    return Math.min(10, Math.max(2.5, r * 0.045));
  }

  // ── Segment-wedge hover motion (ISP-9999; LAW 1 REVISED, bug #910) ────────────
  // On a browse tile, hovering the wheel eases every wedge gently OUTWARD in a
  // subtle radial "breath". The original LAW 1 (Gordon 2026-08-09) grew BOTH the
  // inner AND outer radius by the same delta, reasoning that a per-wedge CSS
  // `translate` along the centroid ray (the even-older --wtx/--wty build) shifted a
  // WIDE arc's inner edge off-centre, so one angular end sat closer to the portrait
  // than the other (up to ~7px gap spread). Growing both radii fixed that UNEVENNESS
  // but kept the underlying problem: receding the inner edge AT ALL — even evenly —
  // opens a growing white gap between the wedges and the fixed-size centre portrait
  // during the hover motion itself (bug #910; invisible in a settled screenshot,
  // visible only mid-transition where 0 < delta < target). LAW 1 REVISED: grow the
  // OUTER radius only; the inner radius — and therefore the seam against the
  // portrait and against every neighbouring wedge — never moves, so the wheel stays
  // exactly as seamless mid-hover as it is at rest. (Concentricity is now moot: an
  // unchanged inner radius is trivially "equal gap at every angle" — it's the SAME
  // gap as the static state.) Mirrors selectedBulgeDelta's LAW 2, which grows the
  // outer radius only for the same reason. Runtime: JS mouseenter/mouseleave
  // transition each wedge's `d`; only the hovered tile animates (no global
  // re-render). Under prefers-reduced-motion the wheel stays static (delta held
  // at 0).
  applyWedgeHoverMotion() {
    const enabled = !!this.segmentHoverMotion;
    if (this.parent) {
      d3.select(this.parent).classed("wedge-hover-motion", enabled);
    }
    if (!this.arcG) return;
    // Always clear any legacy translate vars a prior build/render may have left, so
    // no stale CSS `translate(var(--wtx),var(--wty))` fights the new geometry motion.
    this.arcG.selectAll(".arc").each(function () {
      this.style.removeProperty("--wtx");
      this.style.removeProperty("--wty");
    });
    const parentSel = this.parent ? d3.select(this.parent) : null;
    if (!enabled) {
      if (parentSel) {
        parentSel
          .on("mouseenter.wedgeHover", null)
          .on("mouseleave.wedgeHover", null);
      }
      // Settle any residual expansion so an opted-out surface is byte-identical.
      this.setWedgeHoverExpansion(false);
      return;
    }
    if (!parentSel) return;
    const self = this;
    parentSel
      .on("mouseenter.wedgeHover", () => self.setWedgeHoverExpansion(true))
      .on("mouseleave.wedgeHover", () => self.setWedgeHoverExpansion(false));
  }

  // The concentric radial bloom distance: a slight, radius-proportional separation,
  // clamped so it reads on a tiny thumbnail yet never gets gaudy on a large tile.
  wedgeHoverDelta() {
    return Math.min(8, Math.max(2.5, (this.pieOutterRadius || 0) * 0.05));
  }

  // Pure geometry (LAW 1 REVISED, bug #910): the `d` for `datum` with ONLY the OUTER
  // radius grown by `delta`, keeping the base generator's padAngle/cornerRadius AND
  // inner radius UNTOUCHED. The original LAW 1 (2026-08-09) grew BOTH radii to keep
  // the expansion concentric — that fixed the old per-centroid CSS translate's UNEVEN
  // gap, but growing the inner radius at all recedes every wedge from the fixed-size
  // centre portrait (and its neighbours' shared inner edge), revealing a growing white
  // ring at the inner rim during the hover animation itself (bug #910 — the settled
  // start/end frames look fine; only the in-between motion frames show it, since delta
  // is 0 at rest and > 0 only while hovered/animating). Mirrors selectedBulgeDelta's
  // already-established rule (LAW 2): grow the outer radius only, so the wedge "breathes"
  // outward while its inner edge — and thus the seam against the portrait and against
  // every adjacent wedge — never moves. This is the single source of the hover geometry
  // (driven each frame by setWedgeHoverExpansion) and the unit-testable contract below.
  arcAtDelta(datum, delta) {
    const innerAcc = this.arcGenerator.innerRadius();
    const outerAcc = this.arcGenerator.outerRadius();
    const gen = d3
      .arc()
      .innerRadius(innerAcc)
      .outerRadius((d, i) => outerAcc(d, i) + delta)
      .padAngle(this.arcGenerator.padAngle())
      .cornerRadius(this.arcGenerator.cornerRadius());
    return gen(datum);
  }

  // Grow (expanded=true) or settle (false) every wedge concentrically by animating its
  // `d`. Honours reduced-motion by holding the wheel static (target 0). The per-node
  // `__wedgeHoverDelta` carries the current delta so an interrupted hover tweens from
  // where it was, never snapping.
  setWedgeHoverExpansion(expanded) {
    if (!this.arcG) return 0;
    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const target = expanded && !reduce ? this.wedgeHoverDelta() : 0;
    const self = this;
    // bug #910 (part 2): index the low-contrast hairline overlay (if any) by the
    // SAME join key applyContrastHairline() used, so each wedge's attrTween tick
    // below can drive its hairline's `d` in lockstep with arcAtDelta — no separate
    // transition to keep in sync, no stale mid-hover geometry.
    const hairlineNodeByKey = new Map();
    this.arcG
      .select("g.arc-hairline-g")
      .selectAll("path.arc-hairline")
      .each(function (d) {
        hairlineNodeByKey.set(d.data && d.data.id != null ? d.data.id : d.index, this);
      });
    this.arcG
      .selectAll(".arc")
      .interrupt("wedgeHover")
      .transition("wedgeHover")
      .duration(reduce ? 0 : 260)
      .ease(d3.easeCubicOut)
      .attrTween("d", function (d, i) {
        if (!d || (d.data && d.data.isBlank)) return null;
        const node = this;
        const from = node.__wedgeHoverDelta || 0;
        const interp = d3.interpolateNumber(from, target);
        const hairlineNode = hairlineNodeByKey.get(
          d.data && d.data.id != null ? d.data.id : d.index
        );
        return (t) => {
          const delta = interp(t);
          node.__wedgeHoverDelta = delta;
          if (hairlineNode) {
            hairlineNode.setAttribute("d", self.hairlineArcPathAtDelta(d, i, delta));
          }
          return self.arcAtDelta(d, delta);
        };
      });
    return target;
  }

  // EW-003 gradient wedge: the structured SVG-gradient props derived from a
  // segment's fill MODEL (a gradient fill, OR a legacy per-segment gradient
  // stored as a css `pattern` — currentBg=3/segmentBgColor). Returns null when
  // the flag is OFF, there is no fill model, or the fill is NOT a gradient
  // (image/gif/color/procedural-pattern) — so those wedges are byte-identical to
  // before. Cached per fill object identity (result may be null).
  wedgeGradientProps(d) {
    if (!SEGMENT_ARC_FILLS_ENABLED) return null;
    const fill = d && d.data && d.data.fill;
    if (!fill || typeof fill !== "object") return null;
    if (!this._wedgeGradCache) this._wedgeGradCache = new WeakMap();
    if (this._wedgeGradCache.has(fill)) return this._wedgeGradCache.get(fill);
    const props = toWedgeGradientProps(fill);
    this._wedgeGradCache.set(fill, props);
    return props;
  }

  // The gradient def id a wedge paints from — namespaced per chart instance (the
  // SAME `w<ns>` suffix wedgePatternId uses) so multi-wheel pages can't
  // cross-resolve def ids.
  wedgeGradientId(d) {
    if (!this._fillIdNs) this._fillIdNs = ++wedgeFillNsCounter;
    return `g${d.data.id}w${this._fillIdNs}`;
  }

  // EW-003: the wedge <pattern> props derived from a segment's fill MODEL
  // (`d.data.fill`) — the SAME model the open frame paints from. Returns null
  // (→ legacy `d.data.pattern` path) when the flag is OFF or the segment carries
  // no fill model, so flag-OFF rendering is byte-identical to today.
  wedgeFillProps(d) {
    if (!SEGMENT_ARC_FILLS_ENABLED) return null;
    const fill = d && d.data && d.data.fill;
    if (!fill) return null;
    // Pattern-type fills rebuild an SVG data-URI in toWedgePatternProps, and this
    // getter runs in the d3 fill accessors AND updateImage on the hottest render
    // path — cache per fill object (identity) + radius so each fill is derived
    // once, not 2-3x per segment per render.
    if (typeof fill !== "object") {
      return toWedgePatternProps(fill, { outerRadius: this.pieOutterRadius });
    }
    if (!this._wedgeFillCache) this._wedgeFillCache = new WeakMap();
    const hit = this._wedgeFillCache.get(fill);
    if (hit && hit.r === this.pieOutterRadius) return this.applyGifPlayback(d, hit.props);
    const props = toWedgePatternProps(fill, { outerRadius: this.pieOutterRadius });
    this._wedgeFillCache.set(fill, { r: this.pieOutterRadius, props });
    return this.applyGifPlayback(d, props);
  }

  // ---- T22.145 GIF autoplay (Edit Wheel opt-in) ------------------------------
  // A gif painted through the wedge <pattern><image> ANIMATES in Chromium +
  // WebKit (measured live on web-stg), so the rest state is the href we paint:
  // "autoplay" -> the real gif; "hover" (many gifs / low deviceMemory) -> a
  // still, swapped to the gif while the wedge is hovered; "still"
  // (prefers-reduced-motion) -> a still, always. See utils/gifFillPlayback.js.
  gifPlaybackMode() {
    if (!this.gifPlayback || !SEGMENT_ARC_FILLS_ENABLED) return null;
    const data = this.chartData || [];
    const count = data.filter((d) => d && d.fill && d.fill.type === "gif").length;
    return count ? resolveGifPlayback(count, this.gifPlaybackOverride) : null;
  }

  applyGifPlayback(d, wp) {
    const fill = d && d.data && d.data.fill;
    if (!wp || !fill || fill.type !== "gif") return wp;
    const mode = this.gifPlaybackMode();
    if (!mode) return wp;
    const gif = wp.animateHref || wp.href;
    const poster = wp.href !== gif ? wp.href : undefined;
    const hovered =
      mode === GIF_PLAYBACK.HOVER &&
      this._gifHoverId != null &&
      String(this._gifHoverId) === String(d.data.id);
    if (!this._onGifStillReady) this._onGifStillReady = () => this.refreshGifWedgeHrefs();
    const href = hovered ? gif : gifPaintHref({ gif, poster }, mode, this._onGifStillReady);
    // Still frame still extracting: a transparent pixel lets the pattern's
    // fallback-colour <rect> show (never motion, never a broken-media report).
    return { ...wp, href: href === undefined ? TRANSPARENT_PIXEL : href };
  }

  refreshGifWedgeHrefs() {
    if (!this.parent || !this.chartData) return;
    const self = this;
    const root = d3.select(this.parent);
    this.pie(this.chartData).forEach((d) => {
      if (!d.data || !d.data.fill || d.data.fill.type !== "gif") return;
      const wp = self.wedgeFillProps(d);
      if (!wp) return;
      root
        .selectAll("image[data-gif-wedge]")
        .filter(function () {
          return this.getAttribute("data-gif-wedge") === String(d.data.id);
        })
        .attr("xlink:href", wp.href);
    });
  }

  // Hover-only mode: play the hovered wedge's gif, rest on the still otherwise.
  setGifHoverWedge(id) {
    const next = id == null ? null : id;
    if (next === this._gifHoverId) return;
    this._gifHoverId = next;
    if (this.gifPlaybackMode() === GIF_PLAYBACK.HOVER) this.refreshGifWedgeHrefs();
  }

  // The <pattern> id a wedge's fill paints from. Legacy path (d.data.pattern)
  // keeps the historical document-global `p<id>`; fill-model defs are namespaced
  // per chart instance so multi-wheel pages can't cross-resolve.
  wedgePatternId(d) {
    if (!d.data.pattern && SEGMENT_ARC_FILLS_ENABLED && d.data.fill) {
      if (!this._fillIdNs) this._fillIdNs = ++wedgeFillNsCounter;
      return `p${d.data.id}w${this._fillIdNs}`;
    }
    return `p${d.data.id}`;
  }

  updateImage() {
    this.arcG.selectAll("defs").remove();
    const self = this;
    if (this.gifPlayback && this.svg) {
      this.svg.attr("data-gif-playback", this.gifPlaybackMode());
    }
    this.arcG
      .selectAll("images")
      .data(this.pie(this.chartData), (d) => d.data.id)
      .enter()
      .append("defs")
      .each(function (d) {
        const defsSel = d3.select(this);

        // EW-003 gradient wedge: a per-segment LINEAR/RADIAL gradient paints a
        // REAL SVG <linear/radialGradient> def (parity with renderer-v2
        // fills.js), instead of degrading to the createPie color fallback
        // (#e1e1e1). objectBoundingBox coords (0..1) fill the arc's bbox. Conic
        // resolves to kind:'solid' upstream and skips this def (painted as a
        // solid first stop by wedgeArcFill).
        const grad = self.wedgeGradientProps(d);
        if (grad && grad.kind !== "solid") {
          const gid = self.wedgeGradientId(d);
          let gEl;
          if (grad.kind === "radial") {
            gEl = defsSel.append("radialGradient").attr("id", gid);
            if (grad.center) {
              gEl
                .attr("cx", grad.center.cx)
                .attr("cy", grad.center.cy)
                .attr("fx", grad.center.cx)
                .attr("fy", grad.center.cy);
            }
          } else {
            gEl = defsSel
              .append("linearGradient")
              .attr("id", gid)
              .attr("x1", grad.x1)
              .attr("y1", grad.y1)
              .attr("x2", grad.x2)
              .attr("y2", grad.y2);
          }
          grad.stops.forEach((s) => {
            gEl
              .append("stop")
              .attr("offset", `${Math.round(s.offset * 100)}%`)
              .attr("stop-color", s.color);
          });
          return;
        }

        const pat = defsSel.append("pattern").attr("id", self.wedgePatternId(d));
        // §12 never-hollow: SVG <image> paints NOTHING on a 404/dead URL (no
        // broken-image glyph like an HTML <img>), so a stale media reference
        // used to render the whole wedge blank/hollow. A same-color <rect>
        // painted FIRST (SVG draws in document order) always shows through a
        // failed/transparent image — matches the portrait fallback's
        // never-hollow doctrine a few hundred lines above.
        const fallbackColor = d.data.color || DEFAULT_WEDGE_COLOR;
        pat.append("rect").attr("x", "0%").attr("y", "0%").attr("width", "100%").attr("height", "100%").attr("fill", fallbackColor);
        const wp = self.wedgeFillProps(d);
        if (wp) {
          // Fill-model wedge: deck userSpaceOnUse crop math (anchored at the
          // wheel origin, focal + zoom applied to the inner <image>).
          pat
            .attr("patternUnits", wp.patternUnits)
            .attr("x", wp.x)
            .attr("y", wp.y)
            .attr("width", wp.width)
            .attr("height", wp.height);
          const img = pat
            .append("image")
            .attr("xlink:href", wp.href)
            .attr("data-gif-wedge", d.data.fill && d.data.fill.type === "gif" ? String(d.data.id) : null)
            .attr("preserveAspectRatio", wp.preserveAspectRatio)
            .on("error", function () {
              d3.select(this).attr("data-fill-error", "true");
              reportBrokenMediaOnce({
                wheelId: self.wheelId,
                kind: "segment_fill",
                segmentId: d.data.id,
                url: wp.href,
              });
            });
          if (wp.tile) {
            img
              .attr("x", 0)
              .attr("y", 0)
              .attr("width", wp.width)
              .attr("height", wp.height);
          } else {
            img
              .attr("x", wp.image.x)
              .attr("y", wp.image.y)
              .attr("width", wp.image.width)
              .attr("height", wp.image.height);
          }
        } else {
          // Legacy wedge pattern (byte-identical to the pre-EW-003 renderer,
          // plus the fallback rect + error tag above).
          pat
            .attr("x", "0%")
            .attr("y", "0%")
            .attr("height", "100%")
            .attr("width", "100%")
            .append("image")
            .attr("xlink:href", d.data.pattern)
            .attr("x", "0%")
            .attr("y", "0%")
            .attr("height", self.pieOutterRadius * 2.5)
            .attr("width", self.pieOutterRadius * 2.5)
            .attr("preserveAspectRatio", "xMidYMid slice")
            .on("error", function () {
              d3.select(this).attr("data-fill-error", "true");
              reportBrokenMediaOnce({
                wheelId: self.wheelId,
                kind: "segment_fill",
                segmentId: d.data.id,
                url: d.data.pattern,
              });
            });
        }
      });
  }

  // ---- EW-003 GIF-hover (tile preview) -------------------------------------
  // A gif wedge freezes to its poster inside the SVG <pattern><image> (EW-009),
  // so a resting tile shows a still frame. mountGifHoverWedges() overlays, per
  // gif wedge, a <foreignObject> holding an animating HTML <img> of the SAME gif,
  // CLIPPED to the wedge arc and placed with the SAME crop math the frozen
  // <pattern> uses (via wedgeFillProps), so the poster and the playing frame are
  // pixel-aligned. unmountGifHoverWedges() removes it -> back to the frozen
  // poster. GPU-light (a plain <img>, no JS loop) and reduced-motion-safe (mount
  // is a no-op under prefers-reduced-motion). Additive: the base wedge <pattern>
  // rendering is untouched, and nothing is wired unless a caller opts in.
  prefersReducedMotion() {
    try {
      return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    } catch (e) {
      return false;
    }
  }

  // True when the current chart has at least one gif wedge that could animate
  // (flag on, a resolved gif fill). Lets a caller skip wiring hover listeners on
  // tiles that have no gif to play.
  hasGifHoverWedges() {
    if (!SEGMENT_ARC_FILLS_ENABLED) return false;
    const data = this.chartData || [];
    return data.some((d) => d && d.fill && d.fill.type === "gif");
  }

  mountGifHoverWedges() {
    if (this._gifHoverLayer) return; // already mounted
    if (!SEGMENT_ARC_FILLS_ENABLED) return;
    if (this.prefersReducedMotion()) return; // reduced motion: stay frozen
    if (!this.arcG || !this.chartData) return;
    const self = this;
    const layer = this.arcG
      .append("g")
      .attr("class", "gif-hover-layer")
      .attr("pointer-events", "none");
    let mounted = 0;
    this.pie(this.chartData).forEach((d) => {
      const fill = d && d.data && d.data.fill;
      if (!fill || fill.type !== "gif") return;
      const wp = self.wedgeFillProps(d);
      // Need an image-crop pattern (not a solid/tile) with a source to animate.
      if (!wp || wp.tile || !wp.image || !wp.href) return;
      const uid = `${d.data.id}-${self._fillIdNs || 0}`;
      const clipId = `gifhoverclip-${uid}`;
      // Clip the overlay to the wedge arc (same geometry as the painted arc).
      // A BARE <clipPath> (NOT wrapped in <defs>): updateImage() does
      // arcG.selectAll("defs").remove() on every redraw, so a defs-wrapped clip
      // here would be silently torn out; a bare clipPath is never matched.
      layer
        .append("clipPath")
        .attr("id", clipId)
        .attr("clipPathUnits", "userSpaceOnUse")
        .append("path")
        .attr("d", self.arcGenerator(d));
      const fo = layer
        .append("foreignObject")
        .attr("x", wp.x)
        .attr("y", wp.y)
        .attr("width", wp.width)
        .attr("height", wp.height)
        .attr("clip-path", `url(#${clipId})`);
      // FO local origin (0,0) == the pattern tile origin (wp.x,wp.y), and the
      // pattern's inner <image> coords are relative to that tile origin, so the
      // animating <img> at (wp.image.x, wp.image.y) lands pixel-identical to the
      // frozen poster. object-fit:cover mirrors the pattern's slice fit.
      fo.append("xhtml:div")
        .style("position", "relative")
        .style("width", "100%")
        .style("height", "100%")
        .style("overflow", "hidden")
        .append("img")
        // No crossorigin: matches the center-portrait <img> rationale (Safari
        // refuses cross-origin <img> without ACAO — would blank the wedge).
        // Animate the ACTUAL gif (wp.animateHref); wp.href may be the frozen POSTER
        // still (ISP-9999 gif.poster), which would not animate.
        .attr("src", wp.animateHref || wp.href)
        .attr("alt", "")
        .attr("aria-hidden", "true")
        .attr("draggable", "false")
        .style("position", "absolute")
        .style("left", `${wp.image.x}px`)
        .style("top", `${wp.image.y}px`)
        .style("width", `${wp.image.width}px`)
        .style("height", `${wp.image.height}px`)
        .style("object-fit", "cover")
        .style("pointer-events", "none");
      mounted += 1;
    });
    if (mounted === 0) {
      layer.remove();
      return;
    }
    this._gifHoverLayer = layer;
  }

  unmountGifHoverWedges() {
    if (this._gifHoverLayer) {
      this._gifHoverLayer.remove();
      this._gifHoverLayer = null;
    } else if (this.arcG) {
      this.arcG.selectAll("g.gif-hover-layer").remove();
    }
  }

  // ISP-9999 GIF portrait hover-swap. On card hover, swap the still POSTER for the
  // animating gif; off-hover, revert. Reduced-motion holds the still (animate is a
  // no-op). A no-op when the portrait is not a still-first gif (this._gifPortrait
  // null), so non-gif / full-view / flag-off portraits are byte-unchanged.
  animateGifPortrait() {
    if (!this._gifPortrait) return;
    if (this.prefersReducedMotion()) return; // reduced motion: keep the still
    this._gifPortrait.img.attr("src", this._gifPortrait.gif);
  }

  freezeGifPortrait() {
    if (!this._gifPortrait) return;
    this._gifPortrait.img.attr("src", this._gifPortrait.poster);
  }

  // Classify a segment into "full" | "dot" | "none" by its available size.
  // Delegates to the shared, unit-testable classifier so thresholds live in
  // one place. arcAngle is the segment's angular span in radians.
  iconDisplayFor(d) {
    const arcAngle = d.endAngle - d.startAngle;
    const thresholds = {
      MIN_ANGLE_FOR_FULL_ICON: this.segmentIconConfig.minSizeToShowIcons,
      MIN_ANGLE_FOR_DOT: this.segmentIconConfig.minSizeToShowDotIcons,
      RADIAL_PIXEL_FLOOR: SEGMENT_ICON_THRESHOLDS.RADIAL_PIXEL_FLOOR,
      MIN_WHEEL_DIAMETER_FOR_ICONS:
        SEGMENT_ICON_THRESHOLDS.MIN_WHEEL_DIAMETER_FOR_ICONS,
    };
    const ringThicknessPx =
      typeof this.pieOutterRadius === "number" &&
      typeof this.pieInnerRadius === "number"
        ? this.pieOutterRadius - this.pieInnerRadius
        : null;
    // Rendered wheel diameter (px). svgHeightWidth = min(width,height) after the
    // margin subtraction — the wheel's drawn pixel size — and is the deck's
    // "wheel diameter". Fall back to 2*pieOutterRadius when it is not set yet.
    const wheelDiameterPx =
      typeof this.svgHeightWidth === "number" && this.svgHeightWidth > 0
        ? this.svgHeightWidth
        : typeof this.pieOutterRadius === "number" &&
          this.pieOutterRadius > 0
        ? this.pieOutterRadius * 2
        : null;
    return classifyIconDisplay(
      arcAngle,
      thresholds,
      ringThicknessPx,
      wheelDiameterPx
    );
  }

  // T22.184: the wedge's radial depth in px -- outer rim minus the inner rim
  // resize() hands arcGenerator (pieInnerRadius + hole padding + centerGap).
  // null until resize() has measured real radii.
  iconRingThicknessPx() {
    if (
      !(typeof this.pieOutterRadius === "number") ||
      !(typeof this.pieInnerRadius === "number")
    ) {
      return null;
    }
    const innerRim =
      this.pieInnerRadius + this.arcHolePadding() + (this.centerGap || 0);
    const ring = this.pieOutterRadius - innerRim;
    return ring > 0 ? ring : null;
  }

  // Render a React element (the per-segment icon) to an HTML string. Returns
  // "" if no element. Synchronous-enough for our use because react-icons
  // render to static SVG on first commit; we read innerHTML on a microtask.
  renderIconElementToHtml(element) {
    return new Promise((resolve) => {
      try {
        const container = document.createElement("div");
        const root = createRoot(container);
        root.render(element);
        // Let React flush the SVG, then read + tear down.
        // T22.90 (MEASURED on the PR build 2026-10-10, box dev server against
        // app-stg, wheel 507720): React 18's createRoot commits on its own
        // scheduler, which under load can land AFTER a single setTimeout(0).
        // The read then got "" -- a FIRST pick on a segment stayed blank for 6+
        // seconds (its foreignObject existed, the datum had the icon, nothing
        // was written) until some unrelated edit repainted. Wait for the commit:
        // re-check every frame-ish tick, up to ~0.5 s, before giving up.
        const read = (triesLeft) => {
          const html = container.innerHTML;
          if (!html && triesLeft > 0) {
            setTimeout(() => read(triesLeft - 1), ICON_RENDER_RETRY_MS);
            return;
          }
          try {
            root.unmount();
          } catch (e) {
            /* noop */
          }
          resolve(html);
        };
        setTimeout(() => read(ICON_RENDER_MAX_RETRIES));
      } catch (e) {
        resolve("");
      }
    });
  }

  // Icons on segment — adaptive (full -> dot -> none) + theme/override color.
  // Reads the per-segment icon from d.data.icon (a React element saved by the
  // SegmentIcon picker, spread onto chartData from segment.settings). Falls
  // back to a default icon when a segment has none. Color comes from
  // resolveSegmentIconColor: per-segment iconColor override > theme-responsive
  // default > auto-contrast.
  updateIcons() {
    this.iconsG.selectAll("foreignObject").remove();
    // Per-update token: bumped on every updateIcons() call. The async icon
    // renders below capture this token and only write if it's still current,
    // so a later updateIcons() (which .remove()s the prior foreignObjects)
    // invalidates any in-flight write to a now-detached/repurposed node.
    this.iconUpdateToken = (this.iconUpdateToken || 0) + 1;
    // Resolved-icon HTML cache (fixes the mobile flash-then-clear). Rendering a
    // react-icon to an SVG string is async (createRoot + a microtask), so on the
    // first paint the base SVG is produced ONCE per icon and cached here; every
    // later updateIcons() then writes it SYNCHRONOUSLY (below). That is what lets
    // the icons SURVIVE a re-render / ResizeObserver churn: on the mobile-
    // portrait path a second render used to .remove() the foreignObjects and re-
    // append empties while the prior async writes were invalidated by the token
    // race, leaving permanent blanks a phone user sees. With a warm cache there
    // is no async window to lose, and the remove + repaint happen in one
    // synchronous turn (no empty intermediate frame) -> desktop keeps its stable,
    // flicker-free paint too. Color is applied per-paint AFTER, so it is NOT part
    // of the key (a wedge/theme color change repaints from the same base SVG).
    if (!this.iconHtmlCache) this.iconHtmlCache = new Map();
    if (
      this.segmentIconConfig.showOnSegment === "icons" ||
      this.segmentIconConfig.showOnSegment === "both"
    ) {
      const self = this;
      const updateToken = this.iconUpdateToken;
      // Stale-write guard for the async cache-MISS path only: an in-flight icon
      // render captured `node` for a given segment; before writing innerHTML,
      // verify (a) this update is still the current one, (b) the node is still
      // attached to the DOM and lives under this.iconsG, and (c) the node's bound
      // datum still matches the intended segment id. Any mismatch => the node was
      // removed or repurposed by a newer updateIcons(); skip the write (the newer
      // update — cache now warm — paints synchronously instead).
      const isWriteStillValid = (node, segmentId) =>
        updateToken === self.iconUpdateToken &&
        !!node &&
        node.isConnected &&
        self.iconsG.node() != null &&
        self.iconsG.node().contains(node) &&
        node.__data__ &&
        node.__data__.data &&
        node.__data__.data.id === segmentId;
      const iconsG = this.iconsG
        .selectAll(".icons-g")
        .data(this.pie(this.chartData), (d) => d.data.id);

      // T22.184: size each icon to the wedge it sits in, not a fixed px, so it
      // stays proportional when the wheel grows/shrinks (builder pane switch).
      const ringThicknessPx = self.iconRingThicknessPx();
      const sizeFor = (d) =>
        segmentIconSizeFor(self.iconDisplayFor(d), ringThicknessPx, {
          iconSize: self.segmentIconConfig.iconSize,
          dotIconSize: self.segmentIconConfig.dotIconSize,
        });

      // Default dot element (used when a segment is too small for a full icon).
      const dotElement = <FaCircle />;

      // Stable cache key for an icon's base (uncolored) SVG. The "dot" rung
      // always uses the shared dot glyph; the "full" rung keys on the icon
      // component identity (react-icons are named + prop-free here, so the type
      // name uniquely determines the SVG). Color is deliberately excluded.
      const iconCacheKey = (display, iconElement) => {
        if (display === "dot") return "dot";
        const t = iconElement && iconElement.type;
        const name = (t && (t.displayName || t.name)) || "default-full";
        return "full:" + name;
      };

      iconsG
        .enter()
        .append("foreignObject")
        .attr("class", "icons")
        .attr("transform", (d) => {
          const pos = self.arcGenerator.centroid(d);
          const iconSize = sizeFor(d);
          return `translate(${pos[0] - iconSize / 2},${pos[1] - iconSize / 2})`;
        })
        .attr("width", (d) => sizeFor(d))
        .attr("height", (d) => sizeFor(d))
        .each(function (d) {
          const node = this;
          const segmentId = d.data.id;
          const display = self.iconDisplayFor(d);

          // Too small for even a dot -> render nothing.
          if (display === "none") {
            node.innerHTML = "";
            return;
          }

          // T22.90 (Kelly 2026-10-10 video, web-stg): a segment with NO icon of
          // its own (never picked, cleared with the "No icon" tile, or switched
          // off with its "Show icon on segment" toggle) draws NOTHING. It used
          // to fall back to a FaBookOpen placeholder, so picking one icon made
          // every other segment sprout a book, and turning a segment's icon off
          // or removing it looked like it did nothing (the book took its place).
          if (!hasOwnSegmentIcon(d.data)) {
            node.innerHTML = "";
            return;
          }

          // Per-WEDGE adaptive contrast (deck `adaptive-icons` white-default):
          // wire adaptiveIconColor via resolveSegmentIconColor's adaptiveContrast
          // option so each icon is readable on its OWN wedge, instead of a fixed
          // theme-default ink that goes invisible on dark wedges. Per-segment
          // iconColor override still wins.
          const color = resolveSegmentIconColor(
            d.data,
            self.getColorContrast(d.data.color),
            {
              preferAutoContrast: self.segmentIconConfig.preferAutoContrast,
              adaptiveContrast: true,
            }
          );

          const iconElement = display === "dot" ? dotElement : d.data.icon;
          const key = iconCacheKey(display, iconElement);
          const cachedHtml = self.iconHtmlCache.get(key);

          // Cache HIT -> paint SYNCHRONOUSLY. No async window, so a subsequent
          // re-render can never remove-and-leave-empty; the icon survives.
          if (cachedHtml !== undefined) {
            node.innerHTML = cachedHtml
              ? self.changeIconSvgColor(cachedHtml, color)
              : "";
            return;
          }

          // Cache MISS -> render the base SVG async exactly once. Cache it on
          // SUCCESS only (never cache an empty render: a transient createRoot/
          // setTimeout empty must self-heal on the next call, not poison the
          // cache), then write to the node if this update is still current.
          self.renderIconElementToHtml(iconElement).then((html) => {
            if (html) self.iconHtmlCache.set(key, html);
            if (!isWriteStillValid(node, segmentId)) return;
            node.innerHTML = html ? self.changeIconSvgColor(html, color) : "";
          });
        });
    }
  }

  // Number lable on segment
  updateNumberLable() {
    this.numberLableG.selectAll("text").remove();
    if (
      this.segmentIconConfig.showOnSegment === "numbers" ||
      this.segmentIconConfig.showOnSegment === "both"
    ) {
      const numberLableG = this.numberLableG
        .selectAll(".numberLable-g")
        .data(this.pie(this.chartData), (d) => d.data.id);
      numberLableG
        .join("text")
        .style("fill", (d) => this.getColorContrast(d.data.color))
        .style("font-size", this.segmentIconConfig.numberLableSize * 0.75)
        .attr("dy", "0.35em")
        .attr("transform", (d) => {
          if (this.segmentIconConfig.showOnSegment === "both") {
            const rotation =
              d.endAngle < Math.PI
                ? (d.endAngle * 180) / Math.PI
                : ((d.endAngle + Math.PI) * 180) / Math.PI;
            const angle = d.endAngle - 0.05 - Math.PI / 2;
            const padding =
              this.isSmallView || this.parent.clientHeight - this.margin < 250
                ? this.padding.right
                : this.padding.left + this.padding.right;

            const radius =
              this.pieOutterRadius +
              (d?.data?.selected && !this.segmentHighlighting
                ? padding * 2
                : 0);

            const x = Math.cos(angle) * (radius - 2);
            const y = Math.sin(angle) * (radius - 2);
            return `translate(${x}, ${y}) rotate(-90) rotate(${rotation})`;
          } else {
            const rotation =
              d.endAngle <= Math.PI
                ? ((d.startAngle / 2 + d.endAngle / 2) * 180) / Math.PI
                : ((d.startAngle / 2 + d.endAngle / 2 + Math.PI) * 180) /
                  Math.PI;
            return `translate(${this.arcGenerator.centroid(d)}) , rotate(${
              rotation - 90
            })`;
          }
        })
        .text((d) => {
          if (
            this.segmentIconConfig.minSizeToShowDotIcons <
            d.endAngle - d.startAngle
          ) {
            return this.formatNumberLable(d.data.value);
          }
        })
        .attr("text-anchor", (d) => {
          if (this.segmentIconConfig.showOnSegment === "both") {
            return d.endAngle >= Math.PI ? "start" : "end";
          } else {
            return "middle";
          }
        });
    }
  }

  formatNumberLable(num) {
    if (num >= 1000000) {
      let millions = num / 1000000;
      return millions % 1 === 0 ? millions + "M" : millions.toFixed(1) + "M";
    } else if (num >= 1000) {
      let thousands = num / 1000;
      return thousands % 1 === 0 ? thousands + "K" : thousands.toFixed(1) + "K";
    } else {
      return num.toString();
    }
  }

  // Fetch the svg icon data and change the color. Delegates to the standalone,
  // unit-tested `changeIconSvgColor` module (EXPLORE-02: guards against empty/
  // malformed SVG so a <parsererror> node is never serialized into the DOM).
  changeIconSvgColor(svgString, color) {
    return changeIconSvgColor(svgString, color);
  }

  // Get the color contrast
  getColorContrast(hex) {
    if (!hex || hex == null || hex == undefined) return "";
    let threshold = 200;
    let hRed = hexToR(hex);
    let hGreen = hexToG(hex);
    let hBlue = hexToB(hex);

    function hexToR(h) {
      return parseInt(cutHex(h)?.substring(0, 2), 16);
    }
    function hexToG(h) {
      return parseInt(cutHex(h)?.substring(2, 4), 16);
    }
    function hexToB(h) {
      return parseInt(cutHex(h)?.substring(4, 6), 16);
    }
    function cutHex(h) {
      return h.charAt(0) == "#" ? h?.substring(1, 7) : h;
    }

    let cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    if (cBrightness > threshold) {
      return "#00008B";
    } else {
      return "#ffffff";
    }
  }

  manageLabels(event) {
    // ISP-9999 (label-blink-on-select): WheelComponent calls updateArcs(data)
    // then manageLabels(showLabels) on every chartData change. When labels are
    // already on, updateArcs() above already refreshed them (via the
    // selectionOnly fast path or a real rebuild) — a second unconditional
    // updateLable() here re-tore-down and rebuilt every pill a SECOND time per
    // click, which is the other half of the blink.
    const show = !!event;
    if (show && this.isShowLabelOn) return;
    this.isShowLabelOn = event;
    if (this.isShowLabelOn) {
      // this.updatedTitle();
      this.updateLable();
    } else {
      this.titleG.selectAll("text").remove();
      this.titleG.selectAll("g").remove();
      this.polyLinesG.selectAll("polyline").remove();
      this.lineG.selectAll("line").remove();
    }
  }

  // The rendered outer radius a segment's pill anchors against — grown for a
  // popped-out/selected wedge (v1, unlike v2, DOES grow the selected wedge's
  // outer edge; see the `pad*2` bump below), otherwise `pieOutterRadius`
  // as-is. SINGLE SOURCE OF TRUTH for this formula: it used to be duplicated
  // (byte-identical) in updateLable()'s transform callback AND
  // syncLabelSelectionTransforms() — extracted so the gordon-0920 radial
  // never-overlap-the-wheel push-out (loadRectSize below) always measures
  // against the SAME grown/ungrown radius those two already position the
  // pill's bare anchor against, instead of risking a third, driftable copy.
  pillOuterRadiusFor(d) {
    const pad =
      this.isSmallView || this.parent.clientHeight - this.margin < 250
        ? this.padding.right
        : this.padding.left + this.padding.right;
    return (
      this.pieOutterRadius +
      (d && d.data && d.data.selected && !this.segmentHighlighting
        ? pad * 2
        : 0)
    );
  }

  updateLable() {
    if (this.chartData.length && this.chartData[0].isBlank) return;
    const self = this;
    const arc = d3
      .arc()
      .innerRadius(this.pieOutterRadius)
      .outerRadius(this.pieOutterRadius);

    self.titleG.selectAll("g").remove();
    self.lineG.selectAll("line").remove();
    // KILL THE SPIDER (flag ON): the rim-pill design has no leader legs — pills
    // ride on placement + colour alone (deck: ewflow-onbrand.mjs step 3, client
    // 2026-07-06). Flag OFF draws the legacy leader legs exactly as before.
    if (!WHEEL_PILL_LABELS_ENABLED) {
      this.lineG
        .selectAll("line")
        .data(
          this.pie(this.chartData).filter(
            (d) => segmentTitleText(d.data.title).trim() !== ""
          ),
          (d) => d.data.id
        ) // Filter out empty labels
        .join("line")
        .attr("id", (d) => `l${d.data.id}`)
        .style("stroke", (d) => d.data.color)
        .style("stroke-width", 2)
        .style("opacity", 1) // Fully opaque by default
        .attr("x1", (d) => arc.centroid(d)[0])
        .attr("y1", (d) => arc.centroid(d)[1])
        .attr("x2", (d) => arc.centroid(d)[0] * 1.15)
        .attr("y2", (d) => arc.centroid(d)[1] * 1.15);
    }

    const labelGroup = this.titleG
      .selectAll("labels")
      .data(
        this.pie(this.chartData).filter(
          (d) => segmentTitleText(d.data.title).trim() !== ""
        ),
        (d) => d.data.id
      ) // Filter out empty labels
      .enter()
      .append("g")
      .attr("id", (d, i) => `b${d.data.id}`)
      .style("opacity", 1) // Fully opaque by default
      .attr("transform", function (d) {
        // RIM-PILL (flag ON): with no leader leg, pull the label in from the
        // floated 1.2R spider position so the pill sits just outside the wedge
        // rim (WHEEL_PILL_RIM_GAP px). Recompute against the ENLARGED outer
        // radius when this is the selected/enlarged wedge, so its pill hugs the
        // grown rim instead of burying in the fill (deck §13 + ewflow-onbrand.mjs
        // rim-hug). Scale-independent: derived from pieOutterRadius, no hardcoded
        // absolute radius.
        if (WHEEL_PILL_LABELS_ENABLED) {
          var rOut = self.pillOuterRadiusFor(d);
          var pillR = rOut * 1.05 + WHEEL_PILL_RIM_GAP;
          // fix-daniyal-should-come-center-when-segment-only-one-web (round 2,
          // Daniyal Slack 2026-10-02 on PR #6371): a wedge spanning the full
          // circle has no real "direction". isFullCircleWedge keys on THIS
          // wedge's OWN sweep (endAngle - startAngle), not chartData.length —
          // so a 0-value leftover row sitting alongside a 100%-value row
          // still correctly flags the 100%-value wedge here (its sweep is
          // the full 2π regardless of a sibling 0-sweep row; verified via
          // layoutWedges([{value:0},{value:100}])). Round 1 anchored this
          // wedge at the wheel's own CENTER (translate(0,0)), which parks the
          // pill ON the logo — not what "center" means here. "Center" =
          // centered UNDER THE RIM at 6 o'clock, same pillR every other rim
          // pill uses, still outside the circle. See renderer-v2/geometry.js's
          // pillLabelAnchor for the v2 engine's identical fix.
          if (isFullCircleWedge(d.startAngle, d.endAngle)) {
            return `translate(0,${pillR})`;
          }
          var cen = arc.centroid(d);
          var f = self.pieOutterRadius ? pillR / self.pieOutterRadius : 1.05;
          // hw-0914-dev-label-pill-clipped-offscreen-1280: bound the anchor
          // itself to the pane's current half-width, the same boundary
          // COMPB-02 already uses for the label's TEXT width just below --
          // see labelRadialX.js's clampAnchorTxToPane header for the full
          // MEASURED root cause.
          var rawTx = cen[0] * f;
          var clampedTx = clampAnchorTxToPane(
            rawTx,
            self.parent && self.parent.clientWidth
          );
          // ISP-9999 (grow-motion): stash the SAME translate this attr just
          // computed so the hover/selected grow scale (applied via CSS style,
          // set further below) can recompose it without re-deriving the
          // geometry -- a CSS `transform` style, if set at all, replaces this
          // attribute entirely rather than composing with it.
          this.dataset.pillTx = clampedTx;
          this.dataset.pillTy = cen[1] * f;
          // gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-never-
          // allowed: widthClampedLimitFor() below reads this to decide how
          // hard to truncate this label's TEXT for a narrow pane. Reading the
          // PANE-CLAMPED anchor there under-estimates how far out this pill
          // must actually sit to clear the ring (the ring-safe distance is
          // `rawTx`, computed above, BEFORE the pane clamp shrinks it) -- so a
          // long title was not truncated enough, and the box the ring-safety
          // pass measures later ends up too wide to satisfy both the pane and
          // the ring gap, forcing the trade this fix removes. Stashing the
          // raw, unclamped value lets the text shrink FIRST, so the ring-safe
          // box is more likely to already fit the pane -- with the final
          // re-clamp's own new ring-safety floor (see its comment) as the
          // backstop for whatever conflict remains.
          this.dataset.pillRawTx = rawTx;
          return `translate(${clampedTx},${cen[1] * f})`;
        }
        // Legacy spider-leg labels (flag OFF, unreachable in any deployed
        // environment — every CI workflow sets WHEEL_PILL_LABELS_ENABLED
        // true): out of scope for this fix, left byte-identical.
        if (isFullCircleWedge(d.startAngle, d.endAngle)) {
          return "translate(0,0)";
        }
        // NOTE: this callback is intentionally a plain function (not an
        // arrow function) because D3 binds `this` to the DOM node being
        // operated on for non-arrow `.attr()` callbacks -- reading
        // `this.pieOutterRadius` here (as the code previously did) silently
        // resolved to `undefined` on that DOM node instead of the
        // CreatePieChart instance, collapsing `pos[0]` to NaN with no error.
        // Use the `self` reference captured at the top of updateLable()
        // instead, matching this file's existing convention for plain D3
        // callbacks that need the class instance (see e.g. `self.highlighLabels`
        // a few lines below).
        var pos = arc.centroid(d);
        var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = computeLabelRadialX(self.pieOutterRadius, midangle);
        // ISP-9999 (grow-motion): see the WHEEL_PILL_LABELS_ENABLED branch above.
        this.dataset.pillTx = arc.centroid(d)[0] * 1.2;
        this.dataset.pillTy = arc.centroid(d)[1] * 1.2;
        return `translate(${
          arc.centroid(d)[0] * 1.2
        },${arc.centroid(d)[1] * 1.2})`;
      })
      .attr("class", "label-group")
      // ISP-9999 (grow-motion): a GPU-only CSS scale on the pill itself (never
      // the wedge -- see the constant's header comment). fill-box/center makes
      // the scale grow from the pill's own visual centre instead of this <g>'s
      // local (0,0), which sits at the text's start/middle/end anchor edge, not
      // its middle. Base state renders every pill at scale(1) (selected pills
      // pre-grown), byte-identical in size/position to before this change;
      // hover growth is layered on top by highlighLabels() below.
      .style("transform-box", "fill-box")
      .style("transform-origin", "center")
      .style("transition", () =>
        self._reduceMotion
          ? null
          : `transform ${SEGMENT_PILL_GROW_DURATION_MS}ms ${SEGMENT_PILL_GROW_EASING}`
      )
      .style("transform", function (d) {
        const tx = this.dataset.pillTx;
        const ty = this.dataset.pillTy;
        const grow = !!(d && d.data && d.data.selected);
        return grow
          ? `translate(${tx}px, ${ty}px) scale(${SEGMENT_PILL_GROW_SCALE})`
          : `translate(${tx}px, ${ty}px)`;
      });

    // stg-clobber 2026-10-07: updateLable() keeps its ORIGINAL local copy of this budget (unchanged from
    // DevOps-Staging-prod) so a human's recent lines are not removed; the module-level
    // widthClampedLimitForNode() above is the same calculation for syncLabelSelectionTransforms().
    // Fold this copy into widthClampedLimitForNode() once those lines age past the 14-day window.
    // Label legibility (flag ON): size the char budget to the slice's actual
    // arc length so a wide slice shows more text; never below the legacy cap,
    // so this can only REVEAL more, never truncate harder. Flag OFF keeps the
    // flat cap byte-identical. QA-136's mobile View-Wheel limit raise
    // (segmentLableLimit: 10 -> MOBILE_VIEW_SEGMENT_LABEL_LIMIT) feeds this
    // same `self.segmentLableLimit` base, so both fixes compose.
    //
    // COMPB-02 (anchor-aware, redo): also caps the budget to the pixel room
    // left between the label's own RIM-PILL anchor (`node.parentNode.dataset.
    // pillTx`, set by the `.attr("transform", ...)` callback above) and the
    // Builder canvas pane's edge (`self.parent.clientWidth`), floored at
    // MIN_GUARANTEED_LABEL_CHARS so an ordinary (<15-char) title is never
    // touched by the anchor-aware tightening. Extracted to a named function
    // (behaviour-unchanged) so resolveLabelTextCollisions (ISP-9999 rs-0914)
    // can recompute the IDENTICAL per-node budget when it needs to re-split a
    // colliding label, instead of drifting out of sync with a second copy.
    const widthClampedLimitFor = (node, d) => {
      const limit = isLabelLegibilityEnabled({
        segmentLabelLegibility: self.segmentLabelLegibility,
      })
        ? computeLabelCharLimit(
            d.endAngle - d.startAngle,
            self.pieOutterRadius * 1.2,
            12,
            self.segmentLableLimit
          )
        : self.segmentLableLimit;
      // COMPB-02: cap the budget above at a pixel width tied to the wheel's
      // own drawn radius (position-independent — the label's anchor already
      // legitimately floats past the raw SVG viewBox edge by design, per the
      // RIM-PILL CLIP FIX above, so bounding against viewBox/anchor geometry
      // would over-truncate every ordinary label). Only a label long enough
      // to render WIDER than the wheel itself is what actually produced the
      // measured 233px pill that clipped past the Builder canvas pane's
      // overflow:hidden edge (COMPB-02) — this never tightens the existing
      // char-count caps for a normal-length title.
      const flatBudget = clampCharsToAvailableWidth(
        limit,
        self.pieOutterRadius,
        LABEL_FONT_SIZE_PX
      );
      // gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-never-allowed:
      // prefer the RAW (pre-pane-clamp) ring-safe anchor -- see its own
      // comment where it's stashed, a few lines up in this same function --
      // falling back to pillTx for any node that never set it (flag OFF, or
      // the spider-label branch, both unaffected by this fix).
      const anchorTxAbs = Math.abs(
        parseFloat(
          (node.parentNode && node.parentNode.dataset.pillRawTx) != null
            ? node.parentNode.dataset.pillRawTx
            : node.parentNode && node.parentNode.dataset.pillTx
        ) || 0
      );
      const paneWidth = self.parent && self.parent.clientWidth;
      // clampCharsToAvailableWidth treats a non-positive width as
      // "unmeasurable, don't clamp" (returns the budget untouched) — right
      // for its original flat-radius caller, where the input is always a
      // real positive radius. Here a non-positive result is a MEANINGFUL
      // signal (the anchor already sits at or past the pane's half-width),
      // not a bad measurement — floor at 1px so the clamp still engages
      // and MIN_GUARANTEED_LABEL_CHARS below is what actually governs the
      // result, instead of silently falling through to "no clamp at all".
      const paneAwareBudget = clampCharsToAvailableWidth(
        limit,
        typeof paneWidth === "number"
          ? Math.max(1, paneWidth / 2 - anchorTxAbs)
          : NaN,
        LABEL_FONT_SIZE_PX
      );
      return Math.min(
        flatBudget,
        Math.max(paneAwareBudget, MIN_GUARANTEED_LABEL_CHARS)
      );
    };

    labelGroup
      .append("text")
      .attr("x", 0)
      .attr("y", 0)
      .each(function (d) {
        const widthClampedLimit = widthClampedLimitFor(this, d);
        const text = segmentTitleText(d.data.title);
        // T22.188: "end" = one line cut off with "..." (phone View Wheel).
        const lines =
          self.segmentLabelTruncateStyle === "middle"
            ? [truncateUnicodeSafeMiddle(text, widthClampedLimit)]
            : self.segmentLabelTruncateStyle === "end"
              ? [truncateLabelEnd(text, widthClampedLimit)]
              : wrapLabelIntoLines(text, widthClampedLimit);
        renderLabelLines(this, lines);
      })
      .style("text-anchor", (d) => {
        // fix-daniyal-should-come-center-when-segment-only-one-web (round 2):
        // force "middle" for a full-circle wedge via the same robust
        // isFullCircleWedge check used for its anchor above — NOT
        // `midAngle === Math.PI`, which fails on float drift in d3's
        // cumulative angle sum (MEASURED on PR #6371: a full-circle wedge
        // that doesn't start at exactly 0 computed text-anchor "end", pill
        // rect x≈-70, off to the left instead of centered).
        if (isFullCircleWedge(d.startAngle, d.endAngle)) return "middle";
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        if (midAngle === Math.PI) return "middle";
        else return midAngle < Math.PI ? "start" : "end";
      })
      .style("font-size", "12px")
      .attr("class", "rect-text")
      // RIM-PILL (flag ON): canonical pill ink on the faint tint + DM Sans.
      // White auto-contrast would be invisible on the tint. Flag OFF keeps the
      // legacy auto-contrast fill on the solid-colour pill, no font-family set —
      // UNLESS the label-legibility guard is on, which upgrades that legacy fill
      // to the higher-contrast of {white, ink} but ONLY when the legacy choice
      // fails WCAG AA vs the pill's own segment-colour background (so a passing
      // label stays byte-identical). The pill bg in the legacy path is
      // d.data.color (see loadRectSize), which is exactly what we measure against.
      .style("fill", (d) => {
        if (WHEEL_PILL_LABELS_ENABLED) return PILL_INK;
        // T22.128: measure against the same DEFAULT_WEDGE_COLOR fallback the
        // legacy pill rect paints (loadRectSize) when the segment has no colour.
        const pillHue = d.data.color || DEFAULT_WEDGE_COLOR;
        const legacy = this.getColorContrast(pillHue);
        return isLabelLegibilityEnabled({
          segmentLabelLegibility: this.segmentLabelLegibility,
        })
          ? bestLabelTextColor(pillHue, legacy)
          : legacy;
      })
      .style(
        "font-family",
        WHEEL_PILL_LABELS_ENABLED ? "'DM Sans', sans-serif" : null
      )
      .style("cursor", "pointer")
      // ISP-9999 (#601a): same iOS Safari double-tap-to-zoom fix as the wedge
      // hit-area above — the rim-pill label is an equally valid "open segment"
      // tap target (MobileView defaults showLabels on).
      .each(function () {
        this.style.touchAction = "manipulation";
      })
      .on("mouseover", function (event, d, i) {
        self.highlighLabels(d.data, labelGroup, self, true);
      })
      .on("mouseleave", function (event, d) {
        self.highlighLabels(d.data, labelGroup, self, false);
      })
      .on("click", function (event, d) {
        // Simulate a segment click when the label is clicked
        self.arcSelected(event, d); // Call the arcSelected method
      });
    // ISP-9999 rs-0914-dev-segment-labels-truncate-10ch-390: two DIFFERENT
    // titles can still render an IDENTICAL truncated string (see
    // disambiguateMiddleTruncation above) — resolve those before sizing the
    // pill background so loadRectSize measures the corrected text.
    resolveLabelTextCollisions(labelGroup, self, widthClampedLimitFor);
    self.loadRectSize(labelGroup, self);
    // gordon-0915-law: a pill is never degraded or hidden to avoid overlap.
    // stgfix-1009-T418 (Pill Rule A) narrows the "never moved" half of that
    // law: paint (z-)order is resolved below, AND a pill may now be shifted
    // VERTICALLY (never off its segment's angle) to turn an overlap into a
    // tight, 0px-gap touch — see resolveLabelOverlaps/applyTightPillStack.
    self.resolveLabelOverlaps(labelGroup, self);

    // gordon-0920-builder-labels-overlap-left: the constructor's ONE-SHOT
    // scheduleFontReadyRelayout (gordon-0920-labels-firefox) is registered
    // before this method ever runs — for the Builder's `resize=true`
    // WheelComponent wrapper, the FIRST label draw is itself deferred behind
    // a debounced ResizeObserver tick, so that constructor-time hook can fire
    // (fonts.ready resolves, or an unrelated font's `loadingdone` fires) and
    // find `this.titleG` still empty, no-op via relayoutLabelPills()'s guard,
    // and never fire again for THIS font — permanently stranding whichever
    // labels happened to be measured against a not-yet-loaded DM Sans (MEASURED
    // live on web-dev.iyuga.net, wheel builder/6378: two labels with byte-
    // identical text, one from a cold first draw at -21 to -30px INSIDE the
    // rim gap, one drawn later on a warm font at +20px outside it — same
    // formula, same box math, different font-readiness at measurement time).
    // Re-registering AFTER every full label rebuild closes that gap: this
    // attachment always finds `labelGroup` already populated (we just built
    // it above), so relayoutLabelPills() can never no-op here, and the
    // shared scheduleFontReadyRelayout util (used byte-identically by
    // renderer-v2/engine.js) needs no fork.
    if (this._cancelFontRelayout) this._cancelFontRelayout();
    this._cancelFontRelayout = scheduleFontReadyRelayout(
      self.parent && self.parent.ownerDocument,
      () => {
        self.relayoutLabelPills();
        self.scheduleSettleRelayout();
      }
    );
    // gordon-0928-P0-rim-labels-wrong-on-first-load-until-hover: a full draw
    // (first paint, or a real content change) is itself a trigger that can
    // leave a pill's placement wrong until a LATER pass re-verifies it — see
    // scheduleSettleRelayout's header.
    self.scheduleSettleRelayout();
  }

  // gordon-0915-law (Gordon 2026-09-15, video Recording 2026-09-14 233202.mp4,
  // web-dev /builder/4908 narrowed to ~1280px): segment title pills were
  // collapsing to small DOTS. THIRD reversal of this exact code path: 08-12
  // (bounded 45% overlap, anti-drift) -> 08-28 (zero-tolerance, degrade to
  // dot) -> 08-31 (never hide, dot may overlap, clockwise z-order) -> 09-15
  // (this change: the full -> dot ladder is REMOVED entirely). DESIGN LAW: a
  // segment title is NEVER reduced to a dot at any width or wheel size —
  // always the full title pill (ellipsis-truncated only when the pill itself
  // would exceed the viewport, unrelated to this resolver). Pills MAY overlap
  // each other; they are NEVER displaced/rearranged/pushed to avoid overlap —
  // that displacement/degradation logic was itself the source of real UX bugs
  // (jumps, labels vanishing on hover, shifts under panels). Every pill keeps
  // the SAME fixed anchor computed once in updateLable() (its segment's arc
  // midpoint); this function only resolves PAINT ORDER (reorderLabelPaintOrder
  // below) — never geometry, never display state.
  // Compute a label pill's collision rect algebraically from its already-known
  // anchor translate (dataset.pillTx/pillTy, set by the transform callbacks in
  // updateLable) and its <rect class="rect-label">'s local-space x/y/width/
  // height (set by loadRectSize from text.getBBox(), which reports the SVG
  // element's OWN user-space box — unaffected by the ancestor <g>'s CSS
  // transform, unlike getBoundingClientRect). ISP-9999 (Gordon 2026-09-14,
  // vy-0914-xe-webkit-builder-left-segment-pill-missing): this used to call
  // node.getBoundingClientRect() on the label <g> instead, which depends on
  // the engine having already flushed the `.style("transform", ...)` CSS
  // change (set synchronously a few lines above, same tick) into layout
  // before the rect is read. Chromium/Firefox flush that synchronously on any
  // getBoundingClientRect() call; WebKit measured live on web-dev
  // (main.56132db7.js, /builder/<id>) did not — every label but the first in
  // DOM order came back with a STALE (pre-transform) box, so almost every
  // pill was wrongly judged to collide with the first and got demoted to a
  // dot, even though the wheel had plenty of room. Deriving the rect from
  // already-computed local values needs no layout flush at all, so it is
  // correct in every engine — and finally exercisable in jsdom, which has no
  // real layout engine either (see createPie.brandLogoCenter.test.js).
  labelPillRect(node) {
    if (!node) return null;
    const tx = parseFloat(node.dataset.pillTx);
    const ty = parseFloat(node.dataset.pillTy);
    if (!Number.isFinite(tx) || !Number.isFinite(ty)) return null;
    const rectNode = node.querySelector("rect.rect-label");
    if (!rectNode) return null;
    const rx = parseFloat(rectNode.getAttribute("x"));
    const ry = parseFloat(rectNode.getAttribute("y"));
    const rw = parseFloat(rectNode.getAttribute("width"));
    const rh = parseFloat(rectNode.getAttribute("height"));
    if (![rx, ry, rw, rh].every(Number.isFinite)) return null;
    const left = tx + rx;
    const top = ty + ry;
    return {
      left,
      top,
      right: left + rw,
      bottom: top + rh,
      width: rw,
      height: rh,
    };
  }

  resolveLabelOverlaps(labelGroup, self) {
    if (!labelGroup || labelGroup.empty()) return;

    // Collect each label group, in DOM order (which follows the segments'
    // angular/clockwise order). Every pill always stays "full" — no
    // measurement-driven collision decision, no dot, no hide, no drift.
    const entries = [];
    labelGroup.each(function (d) {
      const node = self.titleG.select(`#b${d.data.id}`).node();
      if (!node) return;
      d.data._labelDisplay = "full";
      entries.push({ id: d.data.id, d });
    });
    if (!entries.length) return;
    self.reorderLabelPaintOrder(entries, self);

    // stgfix-1009-T418 (Pill Rule A, WINS over gordon-0915-law for this one
    // decision — see labelLayout.js's resolveTightPillStack header): resolve
    // any REMAINING pill-pill overlap with a tight, orderly vertical stack
    // instead of silently tolerating it.
    if (WHEEL_PILL_LABELS_ENABLED) {
      self.applyTightPillStack(entries, self);
    }
  }

  // Measures every pill's REAL, already ring-/pane-/viewport-corrected box
  // (getBoundingClientRect — page-pixel space, same API the owner's
  // acceptance ruler uses) and asks labelLayout.js's resolveTightPillStack
  // (Pill Rule A) for each pill's required vertical correction, keyed on each
  // pill's own segment `midAngle` (0 = 12 o'clock, clockwise — matching
  // pillSafeAnchorRadius's convention) so the 12/6 o'clock extremum is
  // immune to any text-height skew in the rendered box (see that function's
  // header for why keying on angle, not rendered position, matters here).
  //
  // Priority: the UNCHANGED law ("titles never overlap the wheel/segments")
  // outranks Pill Rule A, which only governs pill-vs-PILL collisions. A
  // vertical pill-vs-pill correction can coincidentally push a pill back
  // toward the wheel centre (near 12/6 o'clock the "away from pole" direction
  // IS "toward the ring") — measured live by this unit's own regression run
  // against createPie.ringSafetyCorrectionSidePane.dom.test.js. So every pill
  // this method actually moves gets ring-safety re-verified (and corrected,
  // same bounded loop loadRectSize already uses) immediately afterward.
  applyTightPillStack(entries, self) {
    const wedgeRingGeometryPx =
      self.arcG && self.arcG.node()
        ? unionBBoxCenterRadius(self.arcG.node().querySelectorAll(".arc"))
        : null;

    const items = [];
    for (const { id, d } of entries) {
      const node = self.titleG.select(`#b${id}`).node();
      const rectNode = node && node.querySelector("rect.rect-label");
      if (!node || !rectNode) continue;
      items.push({ id, d, node, rectNode });
    }
    if (!items.length) return;

    // The resolver works in page px; the pill translate is in the wheel
    // SVG's own user units. Median page-px-per-unit (a hovered/selected
    // pill's grow scale skews only its own ratio).
    const pxPerUnit = [];
    for (const { rectNode } of items) {
      const h = rectNode.getBoundingClientRect().height;
      const localH = parseFloat(rectNode.getAttribute("height"));
      if (h > 0 && localH > 0) pxPerUnit.push(h / localH);
    }
    pxPerUnit.sort((x, y) => x - y);
    const scale = pxPerUnit.length ? pxPerUnit[Math.floor(pxPerUnit.length / 2)] : 1;

    const win = self.parent && self.parent.ownerDocument && self.parent.ownerDocument.defaultView;
    const place = (id, d, node, tx, ty) => {
      node.dataset.pillTx = tx;
      node.dataset.pillTy = ty;
      d3.select(node).attr("transform", `translate(${tx},${ty})`);
      self.applyPillGrowTransform(id, !!(d && d.data && d.data.selected));
    };
    const at = (node) => [parseFloat(node.dataset.pillTx) || 0, parseFloat(node.dataset.pillTy) || 0];

    // stgfix-10120830-rimpills-kelly: ONE shared pass with the View Wheel
    // engine (rimPillStack.js) — room, stack, ring re-check, residual shift.
    layoutRimPillStack(
      items.map(({ id, d, node, rectNode }) => {
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return {
          id,
          midAngle,
          measureEl: rectNode,
          transitionEl: node,
          moveY: (dy) => {
            const [tx, ty] = at(node);
            place(id, d, node, tx, ty + dy / (scale > 0 ? scale : 1));
          },
          moveRadial: (c) => {
            const [tx, ty] = at(node);
            place(id, d, node, tx + c * Math.sin(midAngle), ty - c * Math.cos(midAngle));
          },
          moveX: (dx) => {
            const [tx, ty] = at(node);
            place(id, d, node, tx + dx, ty);
          },
        };
      }),
      {
        room: rimPillStackRoom(items[0].node.ownerSVGElement, self.parent, win),
        ring: wedgeRingGeometryPx,
        requiredGapPx: WHEEL_PILL_RIM_GAP,
      }
    );
  }

  // gordon-0915-law: "stack order clockwise from 12 so the LATER pill draws
  // on top" — pills may overlap, and when they do, the LATER segment in
  // clockwise order (starting at 12 o'clock) paints on top of an earlier one.
  // `entries` is already in clockwise/DOM order (see resolveLabelOverlaps),
  // and SVG paints later DOM siblings on top of earlier ones, so appending
  // each entry to its parent IN clockwise order moves the last (latest
  // clockwise) entry to the very end of its parent's children — i.e. on top.

  // gordon-0915-law: "stack order clockwise from 12 so the LATER pill draws
  // on top" — pills may overlap, and when they do, the LATER segment in
  // clockwise order (starting at 12 o'clock) paints on top of an earlier one.
  // `entries` is already in clockwise/DOM order (see resolveLabelOverlaps),
  // and SVG paints later DOM siblings on top of earlier ones, so appending
  // each entry to its parent IN clockwise order moves the last (latest
  // clockwise) entry to the very end of its parent's children — i.e. on top.
  reorderLabelPaintOrder(entries, self) {
    for (let i = 0; i < entries.length; i++) {
      const labelNode = self.titleG.select(`#b${entries[i].d.data.id}`).node();
      if (labelNode && labelNode.parentNode) {
        labelNode.parentNode.appendChild(labelNode);
      }
      const lineNode = self.lineG.select(`#l${entries[i].d.data.id}`).node();
      if (lineNode && lineNode.parentNode) {
        lineNode.parentNode.appendChild(lineNode);
      }
    }
  }

  // ISP-9999: the outward-nudge rescue (nudgeLabelOutward) and its translate
  // reader (_readLabelTranslate) were REMOVED, and the full->dot degradation
  // ladder (demoteLabelToDot) that replaced it was later removed too
  // (gordon-0915-law) — a pill now always stays at its segment anchor, full,
  // regardless of overlap. See resolveLabelOverlaps above.

  // ISP-9999 (grow-motion): toggle the segment pill's hover grow scale, keeping
  // the SAME base translate updateLable() stashed on the node (`pillTx`/`pillTy`)
  // -- setting `style.transform` at all replaces the SVG `transform` attribute,
  // so it must carry the position forward, not just the scale. `grow` is the
  // OR of "currently hovered" and "currently selected", so leaving a selected
  // segment stays grown after the pointer moves off it.
  applyPillGrowTransform(id, grow) {
    const node = this.titleG.select(`#b${id}`).node();
    if (!node) return;
    const tx = node.dataset.pillTx;
    const ty = node.dataset.pillTy;
    if (tx === undefined || ty === undefined) return;
    node.style.transform = grow
      ? `translate(${tx}px, ${ty}px) scale(${SEGMENT_PILL_GROW_SCALE})`
      : `translate(${tx}px, ${ty}px)`;
  }

  // ISP-9999 (rework-fe-pr-4566-devpr, label-blink-on-select): updateArcs()'s
  // selectionOnly fast path calls this instead of tearing down every pill's
  // <g>/<rect>/<text> and redrawing from scratch. Recomputes each EXISTING
  // pill's base translate (same formula updateLable()'s attr("transform")
  // callback uses) — the selected wedge's pill legitimately moves outward, so
  // a plain grow-scale toggle isn't enough — then rebinds the fresh datum (so
  // hover/click handlers see the new `selected`) and reuses
  // applyPillGrowTransform() for the scale. No DOM nodes are removed.
  syncLabelSelectionTransforms() {
    const arc = d3
      .arc()
      .innerRadius(this.pieOutterRadius)
      .outerRadius(this.pieOutterRadius);
    const self = this;
    // gordon-0920-EDIT-WHEEL-builder-labels-ROUND-2: this fast path never
    // calls loadRectSize() (see its own header comment), so its ring-safety
    // correction pass never ran here either — a segment SELECT/deselect
    // click is exactly the path a live QA session drives, so this must carry
    // the SAME final correction loadRectSize() applies. See labelLayout.js's
    // ringSafetyCorrection header for the full why.
    const wedgeRingGeometryPx =
      WHEEL_PILL_LABELS_ENABLED && self.arcG && self.arcG.node()
        ? unionBBoxCenterRadius(self.arcG.node().querySelectorAll(".arc"))
        : null;
    this.pie(this.chartData).forEach((d) => {
      const node = self.titleG.select(`#b${d.data.id}`).node();
      if (!node) return;
      d3.select(node).datum(d);
      d3.select(node).selectAll("text,rect").datum(d);

      let tx, ty;
      const rectNode = node.querySelector("rect.rect-label");
      if (WHEEL_PILL_LABELS_ENABLED) {
        const cen = arc.centroid(d);
        const rOut = self.pillOuterRadiusFor(d);
        const pillR = rOut * 1.05 + WHEEL_PILL_RIM_GAP;
        const f = self.pieOutterRadius ? pillR / self.pieOutterRadius : 1.05;
        tx = cen[0] * f;
        ty = cen[1] * f;
        // gordon-0920 never-overlap-the-wheel: a selection-only re-layout
        // (segment popped out / tucked back in) still has the SAME risk
        // loadRectSize() fixes on a full draw -- the pill's box can sit
        // closer to the wheel centre than its anchor once its own height
        // is accounted for. This path never remeasures text (see the
        // hw-0918b comment below), so reuse the EXISTING rect's already-
        // measured width/height rather than a fresh getBBox().
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        // fix-daniyal-should-come-center-when-segment-only-one-web (round 2):
        // a full-circle wedge's box-centering push-out is meaningless on a
        // midAngle ray that can drift off Math.PI by float error (same root
        // cause as updateLable()'s isFullCircleWedge guard) -- the anchor is
        // already (0, pillR) by construction, so skip the push-out instead of
        // re-deriving a new x off that axis.
        if (rectNode && !isFullCircleWedge(d.startAngle, d.endAngle)) {
          const rectX = parseFloat(rectNode.getAttribute("x")) || 0;
          const rectY = parseFloat(rectNode.getAttribute("y")) || 0;
          const rectWidth = parseFloat(rectNode.getAttribute("width")) || 0;
          const rectHeight = parseFloat(rectNode.getAttribute("height")) || 0;
          const boxCenterLocal = {
            x: rectX + rectWidth / 2,
            y: rectY + rectHeight / 2,
          };
          const safeRadius = pillSafeAnchorRadius(
            rOut + WHEEL_PILL_RIM_GAP,
            midAngle,
            rectWidth / 2,
            rectHeight / 2
          );
          const boxCenterWorld = {
            x: safeRadius * Math.sin(midAngle),
            y: -safeRadius * Math.cos(midAngle),
          };
          tx = boxCenterWorld.x - boxCenterLocal.x;
          ty = boxCenterWorld.y - boxCenterLocal.y;
        }
      } else {
        tx = arc.centroid(d)[0] * 1.2;
        ty = arc.centroid(d)[1] * 1.2;
      }
      // stgqa-0921-builder-label-measure: stash the RAW (pre-pane-clamp)
      // anchor BEFORE the text-budget below reads it, mirroring updateLable()'s
      // own `pillRawTx` stash — widthClampedLimitForNode() prefers this ring-
      // safe anchor over the pane-clamped one (see its own comment).
      node.dataset.pillRawTx = tx;
      // stgqa-0921-builder-label-measure (audit1005-fix-editwheel-batch-open-
      // p1p2p3): re-run the SAME pane-aware text-truncation budget a full
      // `updateLable()` draw applies, which this selection-only fast path
      // never did before — every pill's TEXT/BOX used to stay at whatever
      // width the last full draw measured under the pane's OLD size, while
      // only the ANCHOR above was recomputed for the pane's CURRENT size.
      // Opening a Segment Settings pane (a real segment click, which is
      // exactly what routes through this fast path) narrows the Builder's
      // own container (BUILDER_SEGMENT_THIRDS) in the same tick — anchors
      // pulled closer together, stale wide boxes unchanged, so two pills
      // that never collided at the wide spacing could collide at the narrow
      // one. MEASURED LIVE (web-dev.iyuga.net, main.a0b1030e.js, builder/7500,
      // 10 segments): "Step 1  👉 Oil Blend (4 Ways)" genuinely overlapped
      // "Step 2 👉 MetaPWR ASSIST" after opening a segment (DOM rect
      // intersection, not just visually adjacent). Only re-renders the text
      // when the computed truncation actually differs, so an ordinary
      // selection click on an already-wide-enough pane does no extra DOM work.
      if (WHEEL_PILL_LABELS_ENABLED && rectNode && !isFullCircleWedge(d.startAngle, d.endAngle)) {
        const textNode = node.querySelector("text");
        if (textNode) {
          const limit = widthClampedLimitForNode(textNode, d, self);
          const text = segmentTitleText(d.data.title);
          const lines =
            self.segmentLabelTruncateStyle === "middle"
              ? [truncateUnicodeSafeMiddle(text, limit)]
              : self.segmentLabelTruncateStyle === "end"
                ? [truncateLabelEnd(text, limit)]
                : wrapLabelIntoLines(text, limit);
          // T22.131 (MEASURED web-stg main.796c11a9.js, /wheel/507106 builder,
          // 1100 -> 1440 resize): compare the rendered LINES, not their joined
          // text. A hard mid-word wrap ["T103-afterproof-", "71fdzo"] joins to
          // the same string as the one-line title, so the old textContent check
          // kept the stale two-line pill and it overlapped its 12 o'clock
          // neighbour (dx=100 dy=5) — createPie.resizeRewrapHardBreak.T22131.dom.test.js.
          const tspans = textNode.querySelectorAll("tspan");
          const rendered = tspans.length
            ? Array.from(tspans, (t) => t.textContent)
            : [textNode.textContent];
          if (
            rendered.length !== lines.length ||
            rendered.some((line, i) => line !== lines[i])
          ) {
            renderLabelLines(textNode, lines);
            const bbox = textNode.getBBox();
            const padding = 12;
            rectNode.setAttribute("x", bbox.x - padding);
            rectNode.setAttribute("y", bbox.y - padding * 0.4);
            rectNode.setAttribute("width", bbox.width + 2 * padding);
            rectNode.setAttribute("height", bbox.height + 2 * (padding * 0.4));
          }
        }
      }
      // hw-0914-dev-label-pill-clipped-offscreen-1280: this selection-only
      // fast path recomputes every segment's anchor from `pieOutterRadius`
      // without ever checking it against the pane's CURRENT width -- unlike
      // updateLable()'s full rebuild, it has no COMPB-02-style bound at all.
      // MEASURED live: selecting one segment on a 6-segment wheel at 1280x720
      // left a SIBLING segment's pill sitting at screen left:-16, painted and
      // persistent (not a transient resize race) via elementFromPoint. Same
      // clamp/boundary as updateLable()'s transform callback and COMPB-02's
      // text-width clamp -- see labelRadialX.js's clampAnchorTxToPane header.
      tx = clampAnchorTxToPane(tx, self.parent && self.parent.clientWidth);
      // hw0918b-selected-segment-rim-pill-clipped-by-canvas-card-1440: the
      // anchor clamp above only bounds the ANCHOR point -- the rendered pill
      // BOX (the <rect> loadRectSize() already sized around it) can still
      // stick out past the pane even when the anchor itself is legal. This is
      // exactly the path a real segment CLICK takes (updateArcs()'s
      // selectionOnly fast path calls this method, never loadRectSize()), so
      // without this the box-vs-container nudge loadRectSize() applies on the
      // initial full draw never re-runs when BUILDER_SEGMENT_THIRDS narrows
      // the pane on selection -- MEASURED live on web-dev.iyuga.net
      // (builder/6491, 1440x900): selecting "QA-Segment-B" narrowed its own
      // container to 344px, but the pill's rect (already sized by
      // loadRectSize() at the prior WIDE-pane draw) kept its stale local x/
      // width, so its BOX still overflowed 344px's +/-172 bound by ~84px and
      // painted under the open Segment-editor card -- reusing that rect's own
      // x/width (never recomputing bbox here) keeps this cheap for every
      // selection tick.
      if (rectNode) {
        const rectX = parseFloat(rectNode.getAttribute("x"));
        const rectWidth = parseFloat(rectNode.getAttribute("width"));
        tx = clampPillBoxToPane(tx, rectX, rectWidth, self.parent && self.parent.clientWidth);
      }
      node.dataset.pillTx = tx;
      node.dataset.pillTy = ty;
      node.setAttribute("transform", `translate(${tx},${ty})`);
      // stgfix-1008-B43 (segment-click/panel-close pill-fling): the SVG
      // `transform` ATTRIBUTE set just above is rendering-INERT whenever this
      // node also carries a CSS `transform` (it does -- updateLable()/
      // applyPillGrowTransform() always set `node.style.transform`, which
      // wins over the attribute). Without this sync, runRingSafetyCorrectionLoop's
      // very first getBoundingClientRect() measures the STALE style.transform
      // left over from the prior cycle instead of this fresh base anchor.
      self.applyPillGrowTransform(d.data.id, !!(d.data && d.data.selected));
      // fix-daniyal-should-come-center-when-segment-only-one-web (round 2):
      // ring-safety re-derives its correction from this wedge's midAngle ray,
      // which is meaningless for a full circle -- skip it so it can't shove
      // the pill off its x=0 axis (same reasoning as updateLable()'s guard).
      if (wedgeRingGeometryPx && rectNode && !isFullCircleWedge(d.startAngle, d.endAngle)) {
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        runRingSafetyCorrectionLoop({
          measureEl: rectNode,
          transitionEl: node,
          wedgeCenter: wedgeRingGeometryPx.center,
          wedgeRadius: wedgeRingGeometryPx.radius,
          requiredGapPx: WHEEL_PILL_RIM_GAP,
          onCorrect: (correction) => {
            const curTx = parseFloat(node.dataset.pillTx) || 0;
            const curTy = parseFloat(node.dataset.pillTy) || 0;
            const correctedTx = curTx + correction * Math.sin(midAngle);
            const correctedTy = curTy - correction * Math.cos(midAngle);
            node.dataset.pillTx = correctedTx;
            node.dataset.pillTy = correctedTy;
            node.setAttribute("transform", `translate(${correctedTx},${correctedTy})`);
            // stgfix-1008-B43: same fix as the base-anchor sync above -- this
            // callback is THE measurement loop's only feedback signal
            // (runRingSafetyCorrectionLoop re-measures measureEl via
            // getBoundingClientRect() every pass). loadRectSize()'s sibling
            // onCorrect (below, the full-draw path) already does this; this
            // selection-only/resize fast path did not, so every pass saw the
            // SAME un-updated rendered box, recomputed the SAME deficit, and
            // blindly stacked up to RING_SAFETY_MAX_CORRECTION_PASSES
            // IDENTICAL corrections instead of converging after 1-2 -- MEASURED
            // live on web-stg.iyuga.net/builder/507029: closing the Segment
            // panel (a sustained resize -- BUILDER_LAYOUT_FIT_ENABLED's
            // ResizeObserver fires this fast path repeatedly while the pane
            // CSS-transitions) pushed every rim-pill's `translate()` up to 6x
            // past its correct position, past the wheel's rim and off the
            // visible viewport, for the ~100-200ms gap until a later resize
            // tick's fast path (which finally saw a settled container) pulled
            // them back -- the reported "pills vanish" flash.
            self.applyPillGrowTransform(d.data.id, !!(d.data && d.data.selected));
          },
        });
        // editwheel-0922-P1-rim-pills-clip-when-a-segment-pane-is-open
        // (REMOVED by gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-
        // never-allowed): this call used to re-clamp the box to the pane
        // unconditionally, one final time, straight after the ring-safety
        // loop above -- on the theory that "rim pills never hide" outranks
        // the ring gap. MEASURED LIVE (web-dev.iyuga.net,
        // /builder/4908?segment=37102&pane=2, 1440x900, 8-segment wheel,
        // 2026-09-23): that unconditional re-clamp is exactly what pulled two
        // real labels back ONTO the wheel, -24.8px and -33.1px inside the
        // required gap -- the repeat complaint this fixes. Ring-safety's own
        // loop (above) always converges to (at most a few px above) the
        // MINIMUM safe position, so a floored version of this re-clamp
        // (never move below the position ring-safety just produced) is
        // mathematically a no-op in every realistic case -- there is no
        // slack left for it to use. Gordon's law is now unconditional the
        // OTHER way: a label may never overlap the wheel, full stop, so the
        // call is removed outright rather than kept as dead code. The
        // upstream text-width shrink (widthClampedLimitFor, via pillRawTx —
        // see its own comment) is what now keeps a pill from going fully
        // off-pane in a tight pane, by making the box narrower BEFORE
        // ring-safety ever measures it, instead of yanking it back onto the
        // ring after the fact.
      }
      self.applyPillGrowTransform(d.data.id, !!d.data.selected);
    });
    // stgfix-10101843-T419: this fast path also re-derives every translate
    // from scratch — re-apply the Pill Rule A stack in the same task so the
    // pills never paint un-stacked (see relayoutLabelPills).
    self.reapplyTightPillStack(self.titleG.selectAll("g.label-group"));
    // gordon-0928-P0-rim-labels-wrong-on-first-load-until-hover: this is the
    // resize fast path (a container/pane size change) — schedule the same
    // bounded settle re-verification a full draw gets, so a resize tick's own
    // single pass is never the last word either. See scheduleSettleRelayout's
    // header for the measured failure this closes.
    self.scheduleSettleRelayout();
  }

  highlighLabels(data, labelGroup, self, isMouseHovered) {
    const setOpacity = (targetOpacity) => {
      self.lineG.select(`#l${data.id}`).style("opacity", targetOpacity);
      // QA-1276: hover/leave must ONLY toggle emphasis opacity — never rewrite the
      // label text. The old code re-truncated it to the FULL title on hover and to
      // 25 chars on leave, both differing from the resting truncation
      // updateLable() rendered, so a start/end-anchored label visibly slid sideways
      // on every hover (the segment title "shifted from inside the segment toward
      // its original position"). The sole intended hover effect is the pill
      // grow-scale applied by applyPillGrowTransform() below; the text stays exactly
      // as rendered. Matches renderer-v2/engine.js, whose rim pills never
      // re-truncate on hover.
      self.titleG.select(`#b${data.id}`).style("opacity", targetOpacity);
      // ISP-9999 (label-blink-on-select): loadRectSize() now upserts each
      // pill's existing rect in place — no longer needs (or should do) a
      // blanket selectAll("rect").remove() first, which used to blink every
      // OTHER segment's pill off for a frame on a single segment's hover.
      self.loadRectSize(labelGroup, self);
      // stgfix-10120830-rimpills-kelly (T4.18 repeat / T22.130): loadRectSize()
      // re-derives every pill's translate from its natural anchor, wiping the
      // Pill Rule A stack. MEASURED live web-stg main.e9f6e3b1.js,
      // /builder/508018 (33 segments, 1440x900): stacked and 0 overlaps
      // before a hover, 9 overlapping pairs (12 + 6 o'clock) after one hover
      // and leave. Re-apply the stack in the same task so a hover never moves
      // a pill.
      // stgfix-10121534-T22131: loadRectSize() re-derives every pill's
      // translate from scratch, wiping the Pill Rule A stack — MEASURED live
      // on web-stg.iyuga.net (main.91574a72.js, /builder/507106, 34 segments,
      // 1440x900): one pointer pass over any pill or wedge left 11 pill pairs
      // overlapping at 12/6 o'clock until the next resize. Re-apply it in the
      // same task, exactly as relayoutLabelPills() does.
      self.reapplyTightPillStack(self.titleG.selectAll("g.label-group"));
    };

    if (isMouseHovered) {
      setOpacity(1);
      self.applyPillGrowTransform(data.id, true);
      labelGroup.each(function (d) {
        if (d.data.id !== data.id) {
          self.titleG
            .select(`#b${d.data.id}`)
            .style("opacity", self.inactiveOpacity);
          self.lineG
            .select(`#l${d.data.id}`)
            .style("opacity", self.inactiveOpacity); // Apply opacity to lines
        }
      });
    } else {
      self.applyPillGrowTransform(data.id, !!data.selected);
      labelGroup.each(function (d) {
        self.titleG.select(`#b${d.data.id}`).style("opacity", 1);
        self.lineG.select(`#l${d.data.id}`).style("opacity", 1);
      });
      setOpacity(1);
    }
  }

  loadRectSize(labelGroup, self = null) {
    // gordon-0920-EDIT-WHEEL-builder-labels-ROUND-2: the ring-safety
    // correction pass (below, after every other placement step) needs the
    // wheel's OWN real on-screen geometry, in the SAME page-pixel space the
    // owner's acceptance ruler measures in. Computed ONCE per call (not per
    // label) from the already-rendered wedge paths — the identical selector
    // (`.arc` under the visible-g wrapper) and union-bbox reduction the ruler
    // itself uses, so this never drifts from what the ruler checks.
    const wedgeRingGeometryPx =
      WHEEL_PILL_LABELS_ENABLED && self && self.arcG && self.arcG.node()
        ? unionBBoxCenterRadius(self.arcG.node().querySelectorAll(".arc"))
        : null;
    // gv-0914c-dev-label-pills-clip-off-the-left-edge-of-the-canvas-below-
    // 900px (r1006 re-measure): the real clipping surface a pill can run
    // past is whatever ancestor actually has overflow:hidden/clip — not
    // necessarily self.parent itself (see findClipBoundaryWidth's own
    // header for the measured Builder case). Computed ONCE per call, same
    // pattern as wedgeRingGeometryPx above.
    const clipBoundaryWidthPx =
      self && self.parent ? findClipBoundaryWidth(self.parent) : null;
    // stgfix-1008-NEW-EditWheel-2-right-rimpill-clip-sub900: same ancestor
    // walk as clipBoundaryWidthPx above, but keeping the full rect (not just
    // a width) so the FINAL viewport-safety pass below can intersect it with
    // the real window bounds — see that pass's own comment for why a
    // window-only bound isn't enough below 900px.
    const clipBoundaryRectPx =
      self && self.parent ? findClipBoundaryRect(self.parent) : null;
    // stgfix-1007-P1-editwheel-rimpill-leftclip-sub900px: renderer-v2/engine.js's
    // renderPill already runs runViewportSafetyCorrectionLoop AFTER its own
    // ring-safety pass, to pull a pill back inside the REAL browser window
    // once ring-safety (above, gordon-0923-P0-REPEAT) has deliberately pushed
    // it past the pane. This v1 engine (Builder/Edit Wheel) never got that
    // same final pass — MEASURED live on web-stg.iyuga.net, wheel 507379 at
    // 850px/768px: ring-safety pushes a rim-pill past the pane (as designed),
    // but with nothing to re-pull it back from the ACTUAL window edge (unlike
    // the pane, which overflow:visible lets a pill bleed past harmlessly),
    // the pill renders off-screen (rect.left as low as -42px, clipped by the
    // browser viewport itself, not just the pane). Computed once per call,
    // same pattern as wedgeRingGeometryPx/clipBoundaryWidthPx above; null in
    // non-browser test environments (jsdom without a window) so this is a
    // pure no-op there, same guard engine.js itself uses.
    const win =
      self && self.parent && self.parent.ownerDocument
        ? self.parent.ownerDocument.defaultView
        : null;
    // Calculate the size of the text and then adjust the rectangles accordingly
    labelGroup.each(function (d) {
      const textElement = self.titleG.select(`#b${d.data.id}`).select("text");
      // A label degraded to a dot (or hidden) by resolveLabelOverlaps has no
      // <text> node. Skip it so a hover-driven re-layout never resurrects an
      // overlapping pill — and so getBBox() never throws on a missing node.
      if (textElement.empty() || !textElement.node()) return;
      const bbox = textElement.node().getBBox();
      const padding = 12;

      // ISP-9999 (label-blink-on-select): upsert the existing pill rect rather
      // than always removing + re-inserting one. hover/leave (highlighLabels)
      // and a selection-only updateArcs() both call this on live nodes — a
      // fresh insert-every-time made every pill (not just the one hovered)
      // flash off and on. First draw still inserts (group has no rect yet).
      const group = self.titleG.select(`#b${d.data.id}`);
      let rect = group.select("rect.rect-label");
      if (rect.empty()) {
        rect = group.insert("rect", "text").attr("class", "rect-label").attr("rx", 15);
      }
      const rectX = bbox.x - padding;
      const rectY = bbox.y - padding * 0.4;
      const rectWidth = bbox.width + 2 * padding;
      const rectHeight = bbox.height + 2 * (padding * 0.4);
      rect
        .attr("x", rectX)
        .attr("y", rectY)
        .attr("width", rectWidth)
        .attr("height", rectHeight);

      // gordon-0920-view-wheel-segment-title-labels-must-never-overlap-the-
      // wheel: this <rect> is sized around the text anchor (0,0), which is
      // NOT the rect's own centre for a start/end text-anchor -- so a
      // tall/wide pill's near edge can land closer to the wheel centre than
      // its anchor once the actual measured box is known (only true here,
      // the first point in the render where a REAL getBBox() exists). Push
      // the pill's anchor (dataset.pillTx/pillTy, and the group's own
      // translate) out along the bisector so every point of the box is at
      // least WHEEL_PILL_RIM_GAP past the (possibly popped-out) rim — same
      // guarantee, same shared pillSafeAnchorRadius() math as renderer-v2's
      // renderPill(). Spider-label mode (flag OFF) is untouched.
      const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
      // fix-daniyal-should-come-center-when-segment-only-one-web (round 2):
      // a full-circle (single-segment) wedge's pill is deliberately anchored
      // bottom-center by updateLable() (translate(0, pillR), the same radius
      // every rim pill uses, so it already clears the ring by construction);
      // this push-out re-derives from the wedge's midAngle ray, which is
      // meaningless for a full circle and can knock it off its x=0 axis on
      // float drift -- skip it for that wedge.
      if (WHEEL_PILL_LABELS_ENABLED && !isFullCircleWedge(d.startAngle, d.endAngle)) {
        const groupNode = group.node();
        const rOut = self.pillOuterRadiusFor(d);
        const boxCenterLocal = {
          x: rectX + rectWidth / 2,
          y: rectY + rectHeight / 2,
        };
        const safeRadius = pillSafeAnchorRadius(
          rOut + WHEEL_PILL_RIM_GAP,
          midAngle,
          rectWidth / 2,
          rectHeight / 2
        );
        const boxCenterWorld = {
          x: safeRadius * Math.sin(midAngle),
          y: -safeRadius * Math.cos(midAngle),
        };
        const newTx = boxCenterWorld.x - boxCenterLocal.x;
        const newTy = boxCenterWorld.y - boxCenterLocal.y;
        groupNode.dataset.pillTx = newTx;
        groupNode.dataset.pillTy = newTy;
        group.attr("transform", `translate(${newTx},${newTy})`);
        self.applyPillGrowTransform(d.data.id, !!(d.data && d.data.selected));
      }

      // hw0918b-selected-segment-rim-pill-clipped-by-canvas-card-1440: the
      // anchor clamps above (updateLable()'s transform callback and
      // syncLabelSelectionTransforms()) bound only the ANCHOR POINT to
      // self.parent's own half-width -- the rendered pill BOX drawn around
      // that anchor can still stick out past it. MEASURED live on
      // web-dev.iyuga.net (builder/6491, 1440x900): with a Segment open
      // (BUILDER_SEGMENT_THIRDS narrows the wheel's own container to 344px),
      // "QA-Segment-B"'s anchor (tx 157.4) was correctly inside +/-172, but
      // its rect (x -12, width 111) still ran to local right edge 256.4 --
      // 84.4px past the container -- painting under the open Segment-editor
      // card (data-testid="builder-segment-box") docked just outside that
      // container, which covered the pill's rounded right border and final
      // character. This nudges the whole pill (translate, not the text's own
      // x -- gv-0914c's wrapped <tspan>s each hardcode x=0, so shifting
      // text/tspan x no longer moves them) back inside self.parent's width.
      // Originally fixed by #5616, then reverted as collateral damage by
      // #5650's unrelated "revert the Builder actions rail" cleanup.
      //
      // gv-0914c-dev-label-pills-clip-off-the-left-edge-of-the-canvas-below-
      // 900px (r1006 re-measure): use the real clip boundary
      // (clipBoundaryWidthPx, computed once above) instead of
      // self.parent.clientWidth directly -- see findClipBoundaryWidth's
      // header for why self.parent can be narrower than the surface a pill
      // actually clips against.
      const paneWidth =
        clipBoundaryWidthPx != null
          ? clipBoundaryWidthPx
          : self.parent && self.parent.clientWidth;
      if (typeof paneWidth === "number" && paneWidth > 0) {
        const groupNode = group.node();
        const tx = parseFloat(groupNode.dataset.pillTx) || 0;
        const newTx = clampPillBoxToPane(tx, rectX, rectWidth, paneWidth);
        if (newTx !== tx) {
          groupNode.dataset.pillTx = newTx;
          group.attr("transform", `translate(${newTx},${groupNode.dataset.pillTy})`);
          self.applyPillGrowTransform(d.data.id, !!(d.data && d.data.selected));
        }
      }

      // gordon-0920-EDIT-WHEEL-builder-labels-ROUND-2 (UPDATED by
      // gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-never-allowed):
      // FINAL, authoritative ring-safety check. Everything above (the
      // push-out, the pane clamp just above) reasons from an ESTIMATED box in
      // SVG-local units — the pane clamp in particular can nudge a pill
      // straight back toward the wheel centre, undoing the push-out entirely
      // (measured live: LEFT-half labels landing 20-43px inside the required
      // rim gap, deterministically, every render). Re-measure the pill's REAL
      // on-screen box via getBoundingClientRect() (the exact API the owner's
      // acceptance ruler uses) against the wheel's own real geometry, and if
      // it is short of WHEEL_PILL_RIM_GAP, push it the remaining distance
      // along its own bisector. Ring-safety now wins over pane containment
      // UNCONDITIONALLY — never overlap the wheel, full stop, even if that
      // means the box extends past the pane's own boundary. (A prior
      // revision re-clamped to the pane one final time after this loop,
      // capping ring-safety's win at the pane edge — that re-clamp is what
      // let two real labels land back on the wheel, see gordon-0923-P0-REPEAT;
      // it has been removed. The upstream text-width shrink, widthClampedLimitFor
      // via pillRawTx, is what keeps a pill from going fully off-pane instead.)
      //
      // Looped (bounded): when the pane clamp above pulled the box far enough
      // to straddle the wheel's own centre, a single push along the bisector
      // can change WHICH edge of the box is nearest the centre partway
      // through, so one correction is not always enough (proven by
      // ringSafetyCorrection.test.js's "clamp pulls the box past the
      // centre" case). Each iteration re-measures the REAL rect, so this
      // always converges on the ACTUAL rendered geometry, never a stale
      // estimate.
      if (WHEEL_PILL_LABELS_ENABLED && wedgeRingGeometryPx && !isFullCircleWedge(d.startAngle, d.endAngle)) {
        const groupNode = group.node();
        const rectNode = rect.node();
        runRingSafetyCorrectionLoop({
          measureEl: rectNode,
          transitionEl: groupNode,
          wedgeCenter: wedgeRingGeometryPx.center,
          wedgeRadius: wedgeRingGeometryPx.radius,
          requiredGapPx: WHEEL_PILL_RIM_GAP,
          onCorrect: (correction) => {
            const curTx = parseFloat(groupNode.dataset.pillTx) || 0;
            const curTy = parseFloat(groupNode.dataset.pillTy) || 0;
            const correctedTx = curTx + correction * Math.sin(midAngle);
            const correctedTy = curTy - correction * Math.cos(midAngle);
            groupNode.dataset.pillTx = correctedTx;
            groupNode.dataset.pillTy = correctedTy;
            group.attr("transform", `translate(${correctedTx},${correctedTy})`);
            self.applyPillGrowTransform(d.data.id, !!(d.data && d.data.selected));
          },
        });
        // editwheel-0922-P1-rim-pills-clip-when-a-segment-pane-is-open
        // (REMOVED by gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-
        // never-allowed): see the identical removal in
        // syncLabelSelectionTransforms() (same file) for the full write-up --
        // MEASURED LIVE on /builder/4908?segment=37102&pane=2 (1440x900,
        // 2026-09-23), this unconditional pane re-clamp is exactly what pulled
        // two real labels -24.8px/-33.1px onto the wheel (required gap 4px).
        // Ring-safety's own placement above must never be undone; the
        // upstream text-width shrink (widthClampedLimitFor, via pillRawTx)
        // is what now does the "keep it inside the pane" job instead.
      }

      // stgfix-1007-P1-editwheel-rimpill-leftclip-sub900px: FINAL pass, same
      // order as renderer-v2/engine.js's renderPill — pull the pill back
      // inside the REAL browser window if ring-safety (above) pushed it past
      // the actual viewport edge. Runs even for a full-circle wedge (that
      // branch skips the ring-safety push-out above, but its anchor can still
      // sit off-screen on a narrow container). Uses the SAME fixed radial
      // direction (never changes angle, gordon-0915-law) as the ring-safety
      // correction just above.
      //
      // stgfix-1008-NEW-EditWheel-2-right-rimpill-clip-sub900: window.innerWidth
      // alone isn't the real clip edge here — below 900px, opening the Wheel
      // Settings/Segment pane narrows the Builder's own canvas pane
      // (computePaneWidths(), builderPaneWidths.js) well short of the actual
      // window, so a pill ring-safety pushed past THAT pane edge rendered
      // clipped by the pane's own overflow:hidden while still comfortably
      // inside the window — the window-only bound never engaged. Intersect
      // the window with the real clip-boundary ancestor's rect
      // (clipBoundaryRectPx, the SAME ancestor findClipBoundaryWidth already
      // finds for the EARLIER pre-ring-safety clamp) so this final pass
      // catches whichever edge is actually narrower — strictly tightening
      // the existing window-only bound, never widening it, so a pill that
      // already fit inside the window is untouched when no pane ancestor
      // exists (clipBoundaryRectPx null) or when the pane is the full window
      // width anyway.
      if (win) {
        const groupNode = group.node();
        const rectNode = rect.node();
        const viewportRect = {
          left: clipBoundaryRectPx
            ? Math.max(0, clipBoundaryRectPx.left)
            : 0,
          top: 0,
          right: clipBoundaryRectPx
            ? Math.min(win.innerWidth, clipBoundaryRectPx.right)
            : win.innerWidth,
          bottom: win.innerHeight,
        };
        runViewportSafetyCorrectionLoop({
          measureEl: rectNode,
          transitionEl: groupNode,
          direction: { ux: Math.sin(midAngle), uy: -Math.cos(midAngle) },
          viewportRect,
          onCorrect: (correction) => {
            const curTx = parseFloat(groupNode.dataset.pillTx) || 0;
            const curTy = parseFloat(groupNode.dataset.pillTy) || 0;
            const correctedTx = curTx + correction * Math.sin(midAngle);
            const correctedTy = curTy - correction * Math.cos(midAngle);
            groupNode.dataset.pillTx = correctedTx;
            groupNode.dataset.pillTy = correctedTy;
            group.attr("transform", `translate(${correctedTx},${correctedTy})`);
            self.applyPillGrowTransform(d.data.id, !!(d.data && d.data.selected));
          },
        });
      }

      // Add the rectangles based on the text's bounding box size.
      // RIM-PILL (flag ON): the pill is a FAINT hue-tint fill + wedge-hue keyline
      // stroke (never a solid colour swatch — client-rejected 2026-07-06), via the
      // canonical colorsFor() recipe. Flag OFF keeps the legacy solid-colour pill
      // byte-identical.
      // T22.128: a segment with no saved colour (API/import, color null) paints
      // its wedge DEFAULT_WEDGE_COLOR (wedgeArcFill); the pill uses the same
      // fallback. A raw null here made colorsFor() return tint null, and
      // .style("fill", null) drops the fill -> SVG default BLACK pill.
      const pillHue = d.data.color || DEFAULT_WEDGE_COLOR;
      if (WHEEL_PILL_LABELS_ENABLED) {
        const pc = colorsFor(pillHue);
        rect
          .style("fill", pc.tint)
          .style("stroke", pc.edge)
          .style("stroke-width", "1.5px");
      } else {
        rect.style("fill", pillHue);
      }
    });
  }

  // stgfix1002-P0-clamplabelstoparent-not-a-function: the missing glue for
  // labelPaneClamp.js's computeClampDx/parseTranslate, referenced by
  // arcSelected() (below) since stgport0926-sweep03-rimpill-pane-boundary
  // but never actually defined on this class -- every segment select/
  // deselect in the Builder threw "this.clampLabelsToParent is not a
  // function" (MEASURED live on web-dev.iyuga.net, bundle main.33dd615d.js).
  // Re-clamps each label-group's horizontal translate so its real on-page
  // box stays within this.parent's CURRENT on-page bounds -- both read via
  // getBoundingClientRect() (page-pixel space), matching computeClampDx's
  // contract and labelPaneClamp.test.js's existing coverage.
  clampLabelsToParent(selection) {
    if (!this.parent || typeof this.parent.getBoundingClientRect !== "function") {
      return;
    }
    const containerRect = this.parent.getBoundingClientRect();
    selection.each(function () {
      const groupNode = this;
      if (!groupNode || typeof groupNode.getBoundingClientRect !== "function") {
        return;
      }
      const groupRect = groupNode.getBoundingClientRect();
      const dx = computeClampDx({
        groupLeft: groupRect.left,
        groupRight: groupRect.right,
        containerLeft: containerRect.left,
        containerRight: containerRect.right,
      });
      if (!dx) return;
      const parsed = parseTranslate(groupNode.getAttribute("transform"));
      if (!parsed) return;
      const [tx, ty] = parsed;
      groupNode.setAttribute("transform", `translate(${tx + dx},${ty})`);
    });
  }

  spliceString(rawString, isContinue) {
    const self = this;
    // ISP-9999: every other read in this method already optional-chains
    // (`string?.length`) — the `trimLength` line below did NOT, so a null title
    // threw here too ("Cannot read properties of null (reading 'length')").
    // Normalize once at the entry so the whole body is null-safe and the method
    // always returns a string for a nullish input, which its callers then index
    // into (`spliceString(d.data.title).length * 3`).
    const string = segmentTitleText(rawString);
    if (string?.length <= 25 && isContinue) {
      return;
    }
    let trimLength =
      string.length > 25 && string.length <= 50
        ? Math.floor(string.length / 2)
        : 25;
    if (!isContinue && string?.length <= 25) {
      return string;
    } else {
      if (!self.isDoubleCol && string?.length >= trimLength) {
        const spaceIndex = self.findNextSpaceIndex(string, trimLength);
        if (spaceIndex && spaceIndex !== -1) {
          trimLength = spaceIndex + 1;
        } else {
          trimLength = trimLength;
        }
      }
      if (isContinue) {
        if (string?.length <= 50) {
          return string?.substring(trimLength, 50);
        } else {
          return string?.substring(trimLength - 1, 50 - 3) + "...";
        }
      } else {
        if (!self.isDoubleCol && string?.length >= trimLength) {
          return string?.substring(0, trimLength);
        } else {
          return string?.substring(0, 25 - 3) + "...";
        }
      }
    }
  }

  findNextSpaceIndex(inputString, startIndex) {
    let i1, i2;
    for (let i = startIndex; i < inputString.length; i++) {
      if (inputString.charAt(i) === " ") {
        i1 = i;
        break;
      }
    }
    for (let i = startIndex; i >= 0; i--) {
      if (inputString.charAt(i) === " ") {
        i2 = i;
        break;
      }
    }
    return i1 - startIndex > startIndex - i2 ? i2 : i1;
  }

  mouseover(d, i, text) {
    const self = this;
    d3.select(text).style("opacity", 0.2);

    self.Tooltip.style("opacity", 1)
      .style("stroke", "black")
      .style("opacity", 1);
  }

  mousemove(d, i, node) {
    const self = this;
    const tooltipWidth = self.Tooltip.node().offsetWidth;
    // const tooltipHeight = self.Tooltip.node().offsetHeight;
    const x1 = d.layerX - tooltipWidth / 2; //pageX
    const y1 = d.layerY - 10; //pageY
    const nodeRef = d3.select(node).node().getBoundingClientRect();
    // ISP-9999: compare the NORMALIZED title on both sides, so a null title
    // takes the same branch an empty-string title always has.
    const titleText = segmentTitleText(i.data.title);
    const isTruncated = self.spliceString(titleText) != titleText;
    const xPos =
      !self.isDoubleCol && isTruncated
        ? x1 - (x1 + nodeRef.width / 2 - x1)
        : x1;
    const yPos = !self.isDoubleCol && isTruncated ? y1 : y1 - 5;
    const x = xPos;
    const y = yPos;
    // ISP-9999 gallery-pill-gif: on-brand rim-pill recipe (tooltipPillPaint,
    // wrapping the SAME colorsFor() the rim-pill labels use — never a solid
    // raw-color fill) instead of the retired solid-color tooltip. i.data.color
    // already carries the palette-primary for image/gif-filled segments
    // (withPaletteResolvedColors at the card call sites), so no extra
    // fill-type branching is needed here.
    const paint = tooltipPillPaint(i?.data?.inner_color || i?.data?.color);
    self.Tooltip.style("background-color", paint.background)
      .style("border-color", paint.border)
      .style("color", paint.ink);
    self.Tooltip.text(i.data.title)
      .style("left", x + "px")
      .style("top", y + "px");
  }

  mouseleave(d, i, text) {
    const self = this;
    d3.select(text).style("opacity", 1);

    self.Tooltip.style("opacity", 0).style("stroke", "none");
  }

  updatedTitle() {
    if (this.chartData.length && this.chartData[0].isBlank) return;
    const self = this;
    self.isDoubleCol = self.chartData.length > 20;
    const extraSpacing = self.isDoubleCol ? 30 : 0;
    const arcLabel = d3
      .arc()
      .innerRadius(this.pieOutterRadius * 1)
      .outerRadius(this.pieOutterRadius * 1.2);

    const midAngle = (d) => d.startAngle + (d.endAngle - d.startAngle) / 2;

    const label_position = (d, i) => {
      let pos = arcLabel.centroid(d);
      const textPosition = self.isDoubleCol
        ? 0
        : self.spliceString(segmentTitleText(d.data.title)).length * 3;
      pos[0] =
        self.pieOutterRadius * (midAngle(d) < Math.PI ? 1 : -1) +
        (midAngle(d) < Math.PI ? self.margin / 2 : -(self.margin / 2));
      if (self.isDoubleCol && (i || i == 0)) {
        if (i % 2 == 0) {
          pos[0] =
            pos[0] -
            extraSpacing +
            (midAngle(d) < Math.PI ? 0 : -35) +
            textPosition;
        } else {
          pos[0] =
            pos[0] +
            extraSpacing +
            (midAngle(d) < Math.PI ? 35 : 0) +
            textPosition;
        }
      }
      return pos;
    };

    const label_anchor = (d) => {
      return midAngle(d) < Math.PI ? "start" : "end";
    };

    const arcLine = d3
      .arc()
      .innerRadius(this.pieOutterRadius)
      .outerRadius(this.pieOutterRadius * 0.8);

    const line_position = (d, i) => {
      let arcLabels = arcLabel.centroid(d);
      let pos = [];
      pos[0] =
        self.pieOutterRadius * 1 * (midAngle(d) < Math.PI ? 1 : -1) +
        (midAngle(d) < Math.PI ? self.margin / 2 : -(self.margin / 2));
      pos[1] = arcLabels[1];
      if (self.isDoubleCol && (i || i == 0)) {
        if (i % 2 == 0) {
          pos[0] = pos[0] - extraSpacing + (midAngle(d) < Math.PI ? 0 : -35);
        } else {
          pos[0] = pos[0] + extraSpacing + (midAngle(d) < Math.PI ? 35 : 0);
        }
      }
      return [arcLine.centroid(d), arcLabels, pos];
    };
    this.titleG.exit().remove();
    this.polyLinesG.exit().remove();

    this.titleG
      .selectAll("text")
      .data(this.pie(this.chartData), (d) => d.data.id)
      .join((enter) =>
        enter.append("text").call((selection) =>
          selection
            .attr("dy", ".35em")
            .style("cursor", "pointer")
            .style("font-size", "12px")
            .attr("transform", (d, i) => `translate(${label_position(d, i)})`)
            .style("text-anchor", (d) => label_anchor(d))
            .call((text) =>
              text
                .append("tspan")
                .text((d) =>
                  self.spliceString(segmentTitleText(d.data.title), false)
                )
                .attr("dy", (d) => {
                  const text = segmentTitleText(d.data.title);
                  return !self.isDoubleCol && self.spliceString(text) != text
                    ? "-0.3em"
                    : "";
                })
                .attr("x", (d) => (label_anchor(d) === "start" ? 10 : -10))
            )
            .call((text) =>
              text
                .append("tspan")
                .text((d) =>
                  !self.isDoubleCol
                    ? self.spliceString(segmentTitleText(d.data.title), true)
                    : ""
                )
                .attr("dy", "1em")
                .attr("x", (d) => (label_anchor(d) === "start" ? 10 : -10))
            )
            .on("mouseover", function (event, d) {
              self.mouseover(event, d, this);
            })
            .on("mousemove", function (d, i) {
              self.mousemove(d, i, this);
            })
            .on("mouseleave", function (event, d) {
              d3.select(this).style("fill", null);
              self.mouseleave(event, d, this);
            })
            .on("click", self.arcSelected.bind(this))
        )
      );

    this.polyLinesG
      .selectAll("polyline")
      .data(this.pie(this.chartData), (d) => d.data.id)
      .join((enter) =>
        enter
          .append("polyline")
          .attr("class", "polyline")
          .style("stroke", (d) => d.data.color)
          .call((selection) =>
            selection.attr("points", (d, i) => {
              return line_position(d, i);
            })
          )
      );
  }

  mapDndChartData() {
    // Create a copy of chartData to avoid mutation issues
    const chartData = [...this.chartData].sort(
      (a, b) => a.position - b.position
    );
    const mappedData = [];
    chartData.forEach((d, i) => {
      const rightSideArc = { ...d };
      const leftSideArc = { ...d };
      rightSideArc.key = `right_${d.id}`;
      rightSideArc.custom_position = i * 10;
      mappedData.push({ ...rightSideArc });
      leftSideArc.key = `left_${d.id}`;
      leftSideArc.custom_position = rightSideArc.custom_position + 10;
      mappedData.push({ ...leftSideArc });
    });
    mappedData.sort((a, b) => a.custom_position - b.custom_position);
    this.setHiddenArc(mappedData);
  }

  dragstart(d, i) {
    if (i?.data?.isBlank) return;
    this.isDragging = true;
    if (this.isViewMode || this.isNotDraggable) {
      return;
    }
    this.svgG.selectAll("path").style("opacity", (dd, ii) => {
      // return i.data?.id == dd.data?.id ? 1 : 0.3;
    });
    this.prevSwappedArcId = "";
  }

  drag(d, index) {
    setTimeout(() => {
      if (this.isViewMode || this.isNotDraggable || index?.data?.isBlank) {
        return;
      }
      if (
        this.currentSegment !== null &&
        index.data.key != this.currentSegment.key
      ) {
        if (this.prevSwappedArcId != this.currentSegment?.key) {
          let newOrder;
          const selectedSegIndex = this.chartData.findIndex(
            (d) => d.id == index.data.id
          );
          const mapData = this.chartData.map((dd, i) => {
            if (dd.id == this.currentSegment.id) {
              const isAfter =
                this.currentSegment.key.includes("right") &&
                selectedSegIndex + 1 == i;
              const isBefore =
                this.currentSegment.key.includes("left") &&
                selectedSegIndex - 1 == i;

              if (
                (this.currentSegment.key.includes("left") && !isBefore) ||
                isAfter
              ) {
                newOrder = dd.position + 1;
              } else if (
                selectedSegIndex != this.chartData.length - 1 ||
                isBefore
              ) {
                newOrder = dd.position - 1;
              }

              if (
                (selectedSegIndex == this.chartData.length - 1 &&
                  i == 0 &&
                  !this.currentSegment.key.includes("left")) ||
                (selectedSegIndex == 0 &&
                  i == this.chartData.length - 1 &&
                  !this.currentSegment.key.includes("right"))
              ) {
                newOrder = dd.position;
                dd.position = this.chartData[selectedSegIndex].position;
              }
            }
            return dd;
          });
          mapData[selectedSegIndex].position = newOrder;
          mapData.sort((a, b) => a.position - b.position);
          const updatedData = mapData?.map((d, i) => {
            d.position = i * 10;
            return d;
          });
          updatedData.sort((a, b) => a.position - b.position);
          this.updateDataCB(updatedData);
          this.prevSwappedArcId = this.currentSegment?.key ?? "";
        }
      }
    });
  }

  dragEnd(d, i) {
    if (i?.data?.isBlank) return;
    if (this.isShowLabelOn) {
      this.titleG.selectAll("text").remove();
      this.titleG.selectAll("g").remove();
      this.polyLinesG.selectAll("polyline").remove();
      this.lineG.selectAll("line").remove();
      // this.updatedTitle();
      this.updateLable();
    }
    this.svgG.selectAll("path").style("opacity", (d) => {
      if (this.segmentHighlighting && this.selectedArcs.length) {
        if (!this.selectedArcs.find((item) => item === d.data.id)) {
          return this.segmentOpacity;
        }
      }
    });
    this.prevSwappedArcId = "";
    this.isDragging = false;
  }

  arcSelected(event, d) {
    if (d?.data?.isBlank) return;
    if (
      this.chartData.filter(
        (arcData) =>
          arcData.selected && arcData.isPinned && arcData.id !== d.data.id
      )?.length == 2
    ) {
      return;
    }
    this.selectedArcs = this.selectedArcs.filter((d) =>
      this.chartData.find((u) => u.id === d)
    );
    this.chartData.forEach((d) =>
      !this.selectedArcs.includes(d.id) && d.selected
        ? this.selectedArcs.push(d.id)
        : this.selectedArcs
    );

    // gordon-0920-editwheel-interaction-glitches: this used to `.splice(i, 1)`
    // on `this.selectedArcs` from INSIDE its own `.forEach`, which mutates
    // the array being iterated. Array#forEach does not re-index after a
    // splice, so removing index i shifts every later element left by one and
    // the callback's next call still uses i+1 — the element that just slid
    // into slot i is silently skipped. A stale id (one whose chartData row
    // is no longer `.selected`) could therefore survive in `selectedArcs`,
    // and the `maxSelectedArcs` trim below (which reads `selectedArcs`)
    // could then deselect/trim the WRONG wedge on a LATER click — a
    // concrete mechanism for "sometimes needs multiple clicks to select"
    // and for the selected-wedge bulge (LAW 2, selectedBulgeDelta) applying
    // to a stale wedge instead of (or in addition to) the one just clicked.
    // `.filter()` builds a fresh array in one pass instead of mutating
    // mid-iteration, so every element is evaluated exactly once.
    this.selectedArcs = this.selectedArcs.filter((arcId) => {
      const index = this.chartData.findIndex((arc) => arc.id === arcId);
      return !(index > -1 && !this.chartData[index].selected);
    });

    if (this.isViewMode) {
      const arcsData = this.setSelectedArcSides(true);
      const isExist = arcsData.find((arc) => arc.id == d.data.id);

      if (arcsData?.length >= this.maxArcPerSide && !isExist) {
        const rightCount = arcsData.filter((d) => d.side == "right")?.length;
        const leftCount = arcsData.filter((d) => d.side == "left")?.length;

        const midpointAngle = (d.startAngle + d.endAngle) / 2;
        let side;
        if (midpointAngle == Math.PI) {
          side = "left";
        } else if (midpointAngle > Math.PI) {
          side = "left";
        } else {
          side = "right";
        }

        // ISP-9999 (selected-segment-bulge-does-not-follow-second-selection):
        // this used to mutate `arc.selected` IN PLACE and reassign
        // `this.chartData` to the SAME (now-mutated) object references. Those
        // objects are the EXACT SAME references WheelComponent currently holds
        // as its `chartData` prop (WheelComponent passes chartData straight
        // through to CreatePieChart with no cloning when the SEGMENT_COMPUTED_
        // LABELS/SEGMENT_ARC_FILLS flags are off) — mutating them here silently
        // pre-corrupts the "previous props" snapshot `wheelPropsEqual`
        // (WheelComponent's React.memo comparator) will diff against once the
        // click's `updateDataCB` round-trips back through React/Redux. By the
        // time that comparator runs, prevProps.chartData[i] already reads the
        // POST-click `.selected` value (same mutated object), so it looks
        // identical to nextProps and React.memo skips the re-render entirely —
        // `updateArcs()` never fires again, so the wedge geometry (and its
        // selected-bulge) never updates, even though `this.chartData`/
        // `__data__` and the Redux-driven side panel both look correct.
        // Returning a NEW object for the changed row instead of mutating the
        // existing one keeps every OTHER reference (including any stale props
        // snapshot elsewhere) byte-identical, so the comparator can still tell
        // old and new apart.
        if (rightCount >= this.maxArcPerSide && side == "right") {
          const firstIndex = this.selectedArcs.findIndex((sArc) => {
            return arcsData.find(
              (arc) => arc.id == sArc && arc.side == "right"
            );
          });
          this.chartData = this.chartData.map((arc) =>
            arc.id == this.selectedArcs[firstIndex]
              ? { ...arc, selected: false }
              : arc
          );
          this.selectedArcs.splice(firstIndex, 1);
        } else if (leftCount >= this.maxArcPerSide && side == "left") {
          const firstIndex = this.selectedArcs.findIndex((sArc) => {
            return arcsData.find((arc) => arc.id == sArc && arc.side == "left");
          });
          this.chartData = this.chartData.map((arc) =>
            arc.id == this.selectedArcs[firstIndex]
              ? { ...arc, selected: false }
              : arc
          );
          this.selectedArcs.splice(firstIndex, 1);
        }
      }
    }
    let isHavingPinned = 0;
    let isArcSelectedChanged = false;
    // See the ISP-9999 note above the isViewMode trim block: build a NEW
    // object for the row that actually changed rather than mutating
    // `arcData` (which aliases WheelComponent's current `chartData` prop) in
    // place, so a later React.memo comparison against that prop can still
    // detect the change.
    const updatedData = this.chartData.map((arcData) => {
      let nextArcData = arcData;
      if (arcData.id === d.data.id) {
        const nextIsPinned =
          arcData.selected && arcData.isPinned
            ? !arcData.isPinned
            : arcData.isPinned;
        const nextSelected = !arcData.selected;
        nextArcData = { ...arcData, isPinned: nextIsPinned, selected: nextSelected };
        if (nextArcData.selected) {
          this.selectedArcs.push(nextArcData.id);
          isArcSelectedChanged = nextArcData.selected;
        }
      }
      if (nextArcData.isPinned) {
        isHavingPinned += 1;
      }
      return nextArcData;
    });
    if (isArcSelectedChanged) {
      if (
        this.selectedArcs.length > this.maxSelectedArcs &&
        !(isHavingPinned == 1 && this.selectedArcs.length == 2) &&
        !this.isViewMode
      ) {
        for (let i = 0; i < this.selectedArcs?.length; i++) {
          const index = updatedData.findIndex(
            (d) => d.id == this.selectedArcs[i]
          );
          const data = updatedData[index];
          if (data && !data?.isPinned) {
            updatedData[index] = { ...data, selected: false };
            this.selectedArcs.splice(i, 1);
            break;
          }
        }
      }
    } else {
      const index = this.selectedArcs.findIndex((ar) => ar == d.data.id);
      this.selectedArcs.splice(index, 1);
    }
    // Keep this instance's own bookkeeping (setSelectedArcSides below, and
    // any subsequent click) reading the FRESH selection state, now that the
    // rows above are no longer mutated in place.
    this.chartData = updatedData;
    this.setSelectedArcSides();
    this.updateDataCB(updatedData);

    // stgport0926-sweep03-rimpill-pane-boundary: selecting/deselecting a
    // segment can mount (or unmount) the Builder's Segment Settings side
    // pane as a flex sibling of the wheel canvas (SegmentBuilder/index.jsx),
    // which shifts this.parent's on-page position WITHOUT changing its
    // width/height -- so the ResizeObserver-driven relayout in
    // WheelComponent (gated on the `resize` prop, which the Builder passes
    // as false -- see clampLabelsToParent's own comment) never fires, and
    // never would even if it did fire (ResizeObserver only reacts to size
    // changes, not position-only shifts). loadRectSize()'s clampLabelsToParent
    // call only runs on the initial draw and on hover, not on this selection
    // path, so a pill anchored near the container's edge stays clipped past
    // it (or past the newly-opened pane) indefinitely -- MEASURED live on
    // web-stg.iyuga.net (see PR description): still clipped after a 10s poll,
    // not a transient race. Re-clamping here, after the pane's own mount has
    // had a chance to paint, fixes it without recomputing wedge geometry
    // (which `resize={false}` deliberately skips for this consumer).
    //
    // ISP-9999: `clampLabelsToParent` itself was never ported to this branch
    // (only this call site was, by a prior partial port) -- calling it
    // unconditionally throws `this.clampLabelsToParent is not a function` on
    // every single segment click. Guard on it actually existing so the click
    // path this bug's own fix depends on stays intact; no-op until the real
    // rimpill-pane-boundary fix lands here.
    if (typeof this.clampLabelsToParent === "function") {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => {
          this.clampLabelsToParent(d3.selectAll(".label-group"));
        });
      } else {
        this.clampLabelsToParent(d3.selectAll(".label-group"));
      }
    }
  }

  setSelectedArcSides(getData) {
    let arcsData = [];
    const arcsAngles = this.pie(this.chartData);
    this.chartData.forEach((element) => {
      if (element.selected) {
        const arc = { id: element.id, side: "" };
        const arcAngle = arcsAngles.find((d) => d.data.id == element.id);
        if (arcAngle) {
          const midpointAngle = (arcAngle.startAngle + arcAngle.endAngle) / 2;
          if (midpointAngle == Math.PI) {
            arc.side = "left"; //was middle before
          } else if (midpointAngle > Math.PI) {
            arc.side = "left";
          } else {
            arc.side = "right";
          }
        }
        arcsData.push({ ...arc, ...element, arcAngle });
      }
    });
    if (getData) {
      return arcsData;
    } else {
      this.getSelectedArcs(arcsData);
    }
  }

  pie_in(d, i) {
    if (i?.data?.isBlank) return;
    this.currentSegment = i?.data;
    if (!this.isDragging) {
      const hoveredData = this.chartData.find(
        (data) => data.id == this.currentSegment.id
      );
      this.arcHoverEvents(hoveredData);
    }
  }

  pie_out(d, i) {
    this.currentSegment = null;
    this.setGifHoverWedge(null);
    if (!this.isDragging) {
      this.arcHoverEvents(null);
    }
  }

  setHiddenArc(mappedData) {
    this.hiddenArcs.exit().remove();

    const self = this;
    const mouseover = (d, i) => {
      if (i?.data?.isBlank) return;
      self.pie_in(d, i);
      self.setGifHoverWedge(i?.data?.id);

      if (!self.isDragging && self.showTooltips) {
        if (this.isShowLabelOn) {
          // gordon-0920-view-wheel-segment-hover-other-labels-hide-with-a-
          // delay-must-be-instant (OWNER REPORT 2026-09-20 09:04 PT,
          // web-dev.iyuga.net/wheel/4908): this used to defer
          // highlighLabels(..., true) behind a 500ms `hoverTimer` (a
          // hover-intent debounce), so the OTHER labels stayed visible for
          // half a second after the pointer actually entered the wedge.
          // mouseleave already called highlighLabels synchronously below —
          // hovering must match that, hiding every other label in the SAME
          // tick as the pointer event, no timer/transition/debounce.
          self.highlighLabels(i.data, d3.selectAll(".label-group"), self, true);
        } else {
          self.Tooltip.style("opacity", () => {
            if (this.segmentHighlighting && this.selectedArcs.length) {
              if (!this.selectedArcs.find((item) => item === i.data.id))
                return 0;
            }
            return 1;
          }).style("stroke", "black");
        }
      }
    };

    const mousemove = (d, i) => {
      if (i?.data?.isBlank || this.isShowLabelOn || !self.showTooltips) return;
      const tooltipWidth = self.Tooltip.node().offsetWidth;
      const tooltipHeight = self.Tooltip.node().offsetHeight;
      const x = d.clientX - tooltipWidth / 2; //pageX
      const y = d.clientY - tooltipHeight - 10; //pageY
      // ISP-9999 gallery-pill-gif: on-brand rim-pill recipe (tooltipPillPaint)
      // instead of the retired lightenHexColor solid fill — converges
      // Gallery/Explore/every card-hover tooltip onto the SAME tint+stroke+ink
      // law the rim-pill Segment labels use on Edit/View (design law: NEVER
      // solid).
      const paint = tooltipPillPaint(i?.data?.color);
      self.Tooltip.style("background-color", paint.background)
        .style("border-color", paint.border)
        .style("color", paint.ink);

      // T22.258: viewport x/y -> containing-block left/top (the hovered
      // Gallery card is transformed, which re-anchors position:fixed) and
      // clamped inside the scroll column, so it never grows a scroll bar.
      self.Tooltip.text(i?.data?.title);
      placeFixedTooltip(self.Tooltip.node(), x, y);
    };

    const mouseleave = (d, i) => {
      if (i?.data?.isBlank) return;

      if (self.showTooltips) {
        if (this.isShowLabelOn) {
          self.highlighLabels(
            i.data,
            d3.selectAll(".label-group"),
            self,
            false
          );
        } else {
          self.Tooltip.style("opacity", 0).style("stroke", "none");
        }
      }
    };

    // gordon-0920-editwheel-interaction-glitches: mapDndChartData() builds
    // TWO entries per Segment here (a `right_<id>`/`left_<id>` pair sharing
    // the SAME `.id`, distinguished only by `.key`), but this join was keyed
    // on `d.data.id` — a duplicate key across the two halves of every
    // Segment. d3's keyed `.data()` join treats a repeated key as "already
    // consumed" the second time it appears, so one half of every Segment's
    // invisible click/drag/hover hit-area was torn down (exit) and rebuilt
    // from scratch (enter — a fresh `.append("path")` with all its
    // mouseover/mousemove/mouseleave/drag/click listeners re-attached) on
    // EVERY updateArcs() call, i.e. on every single wedge click, resize, and
    // label update — real DOM/listener churn on the exact layer that
    // receives every click. `.key` (already unique per half, per Segment) is
    // the correct join key.
    this.hiddenArcs
      .selectAll("path")
      .data(this.pie([...mappedData]), (d) => d.data.key)
      .join(
        (enter) =>
          enter.append("path").call((selection) => {
            // VW-3 (Kelly QA-131 #1 / QA-136, 2026-08-12): d3.drag()'s touch
            // listeners were attached to this hit-area UNCONDITIONALLY, even
            // when isNotDraggable is true (dragstart/drag/dragEnd all bail out
            // early on isNotDraggable — no arc ever moves — but the drag
            // gesture recognizer's touchstart/touchmove/touchend still consume
            // the first tap of a touch sequence on a real mobile browser before
            // the native "click" below can fire). The public View Wheel always
            // renders with isNotDraggable: true, so every mobile viewer needed a
            // SECOND tap to open a segment. Only attach drag where dragging is
            // actually possible (the Builder canvas) — a view-only wheel's arcs
            // get click/hover only, so a single tap opens on the first try.
            if (!this.isNotDraggable) {
              selection.call(
                d3
                  .drag()
                  .on("start", this.dragstart.bind(this))
                  .on("drag", this.drag.bind(this))
                  .on("end", this.dragEnd.bind(this))
              );
            }
            selection
              .style("cursor", "pointer")
              .style("fill", "transparent")
              // ISP-9999 (#601a, iPhone-responsive View Wheel): iOS Safari
              // defaults touch-action to `auto`, so a tap on this hit-area ran
              // the double-tap-to-zoom gesture disambiguation and swallowed
              // the first tap — the SAME root cause QA-148 already fixed on
              // the (currently flag-OFF) renderer-v2 engine's `.rv2-wedge-path`
              // (see engine.js, which sets this the same direct way for the
              // SAME jsdom/cssstyle reason: `style.setProperty("touch-action",
              // ...)` is silently dropped by this repo's jsdom, so the
              // property setter is what real browsers AND our tests agree on).
              // This is the ported fix for the LEGACY engine, which is the one
              // actually live on the public View Wheel today.
              .each(function () {
                this.style.touchAction = "manipulation";
              })
              .attr("id", (d) => d.data.key)
              .attr("d", this.arcGenerator)
              .on("mouseover", mouseover)
              .on("mouseout", self.pie_out.bind(this))
              .on("click", self.arcSelected.bind(this))
              .on("mousemove", mousemove)
              .on("mouseleave", mouseleave);
          }),
        (update) =>
          update.call((selection) => selection.attr("d", this.arcGenerator))
      );
  }
}

export default CreatePieChart;
